import { parseCustomSkinReference, parseStoredSkinSnapshots, skinSnapshotsFirestoreData, type OverlayCustomSkinReference, type OverlaySkinSnapshots } from "./overlaySkin";
import { serverTimestamp, type DocumentData } from "firebase/firestore";
import { compactData, dateValue, stringValue } from "./firestore";
import { normalizedHex } from "./appearance";
import type { LeagueModel } from "./catalog";
import type { OrganizationModel } from "./organization";
import { parseGPSFocusOption, type GPSFocusOption } from "./courseMatcher";
import { isCourseKMLRole, type CourseKMLRole, type GPSTrackerCourseKML } from "./gps";
import { browserTimeZone, isValidTimeZone } from "./zonedTime";

// Broadcast overlay configuration — the control-plane document for the
// OBS/TriCaster overlay page. Doc fields are camelCase like every other
// Firestore doc; only computed leaderboard keys (models/leaderboard.ts) are
// dash-case. Contract mirrors firestore.rules validOverlayConfigShape.
//
// Since 2026-07 every element type is INSTANCEABLE: the doc stores dynamic
// per-type instance arrays under the top-level `elements` key (≤ 8 per type).
// Legacy fixed-slot docs (layout.groups MPRO/FPRO, lowerThirds
// primary/secondary/custom, pylons/timers a/b) are auto-migrated at parse
// time into instances with deterministic "legacy-*" ids — see
// legacyOverlayElements. The first full save writes the new shape only.
// functions/src/overlayRuntime.ts normalizedElements() is the raw-data twin
// of this migration and must stay in sync (ids, names, regions).

export type OverlayColorSource =
  | "league-primary"
  | "league-secondary"
  | "league-primary-secondary-gradient"
  | "org-primary"
  | "org-secondary"
  | "custom"
  | "custom-gradient";

export type OverlayGradientDirection = "horizontal" | "vertical";

export const DEFAULT_OVERLAY_GRADIENT_ANGLE = 90;

export interface OverlayColorRef {
  source: OverlayColorSource;
  organizationID?: string;
  /** Resolved 6-hex (no '#') — primary color, or gradient start. */
  hex: string;
  /** Resolved 6-hex (no '#') — gradient end. */
  secondaryHex?: string;
  /** CSS linear-gradient angle in degrees. Legacy docs may still carry gradientDirection. */
  gradientAngle?: number;
  gradientDirection?: OverlayGradientDirection;
}

export interface OverlayFont {
  googleFamily?: string;
  customFontURL?: string;
  customFontName?: string;
}

/**
 * Global skin: the shared geometric silhouette (edges, joins, corner
 * treatments, accent placement) applied to every skinnable element at once.
 * Colors and fonts stay under their own design controls; a skin never changes
 * bar heights or base font sizes (JS fit constants mirror those). "default"
 * is today's flat rectangular look and is omitted from Firestore, so legacy
 * documents parse unchanged. Any skinnable instance may carry its own `skin`
 * override (unset = inherit; "default" is a real value there, forcing the flat
 * look under a skinned stage). The stage stamps the global value as
 * `data-skin`; every skinnable element root stamps its effective skin, and each
 * non-default skin is a stylesheet scoped to `.ovl-<root>[data-skin="…"]`.
 */
export type OverlaySkin = "default" | "velocity" | "circuit" | "championship" | "pennant" | "studio";
/** Roster mirrored by firestore.rules (`validOverlayDesign`) — keep both lists in sync. */
export const OVERLAY_SKINS: OverlaySkin[] = ["default", "velocity", "circuit", "championship", "pennant", "studio"];
export const DEFAULT_OVERLAY_SKIN: OverlaySkin = "default";
export const OVERLAY_SKIN_LABELS: Record<OverlaySkin, string> = {
  default: "Default",
  velocity: "Velocity",
  circuit: "Circuit",
  championship: "Championship",
  pennant: "Pennant",
  studio: "Studio",
};

export function isOverlaySkin(value: unknown): value is OverlaySkin {
  return typeof value === "string" && (OVERLAY_SKINS as string[]).includes(value);
}

/** Unknown or absent values fall back to the default skin (forward-compatible with skins this build doesn't know). */
export function parseOverlaySkin(value: unknown): OverlaySkin {
  return isOverlaySkin(value) ? value : DEFAULT_OVERLAY_SKIN;
}

/** Per-instance override: unknown or absent → undefined (inherit the global skin). Unlike parseOverlaySkin, "default" is preserved — it forces the flat look. */
export function parseOverlaySkinOverride(value: unknown): OverlaySkin | undefined {
  return isOverlaySkin(value) ? value : undefined;
}

/** Skin an element renders with: its own override when set, else the global design skin. */
export function effectiveOverlaySkin(instanceSkin: OverlaySkin | undefined, globalSkin: OverlaySkin): OverlaySkin {
  return instanceSkin ?? globalSkin;
}

/**
 * Global material: how skinnable surfaces render as a physical finish
 * (translucency, texture, sheen) stacked on top of the skin's silhouette.
 * "default" is today's flat paint and is omitted from Firestore, so legacy
 * documents parse unchanged. Any skinnable instance may carry its own
 * `material` override (unset = inherit; "default" is a real value there).
 * The stage stamps the global value as `data-material`; every skinnable
 * element root stamps its effective material, and each non-default material
 * is a stylesheet (overlay/materials/<name>.css) scoped to
 * `.ovl-<root>[data-material="…"]` that only sets the surface / paint
 * contract custom properties declared in overlay.css.
 */
export type OverlayMaterial = "default" | "frosted" | "metal" | "carbon";
/** Roster mirrored by firestore.rules (`validOverlayDesign`) — keep both lists in sync. */
export const OVERLAY_MATERIALS: OverlayMaterial[] = ["default", "frosted", "metal", "carbon"];
export const DEFAULT_OVERLAY_MATERIAL: OverlayMaterial = "default";
export const OVERLAY_MATERIAL_LABELS: Record<OverlayMaterial, string> = {
  default: "Default",
  frosted: "Frosted Glass",
  metal: "Metal",
  carbon: "Carbon Fiber",
};

export function isOverlayMaterial(value: unknown): value is OverlayMaterial {
  return typeof value === "string" && (OVERLAY_MATERIALS as string[]).includes(value);
}

/** Unknown or absent values fall back to the default material (forward-compatible with materials this build doesn't know). */
export function parseOverlayMaterial(value: unknown): OverlayMaterial {
  return isOverlayMaterial(value) ? value : DEFAULT_OVERLAY_MATERIAL;
}

/** Per-instance override: unknown or absent → undefined (inherit the global material). "default" is preserved — it forces flat paint under a textured stage. */
export function parseOverlayMaterialOverride(value: unknown): OverlayMaterial | undefined {
  return isOverlayMaterial(value) ? value : undefined;
}

/** Material an element renders with: its own override when set, else the global design material. */
export function effectiveOverlayMaterial(instanceMaterial: OverlayMaterial | undefined, globalMaterial: OverlayMaterial): OverlayMaterial {
  return instanceMaterial ?? globalMaterial;
}

/** Optional overrides retained separately for each textured material. */
export type OverlayMaterialTextureOpacities = Partial<Record<Exclude<OverlayMaterial, "default">, number>>;

export const OVERLAY_MATERIAL_TEXTURE_DEFAULTS: Record<Exclude<OverlayMaterial, "default">, number> = {
  metal: 0.3,
  carbon: 0.3,
  frosted: 1,
};

/** Drop invalid entries, clamp finite values, and keep explicit zero/default values. */
export function parseOverlayMaterialTextureOpacities(value: unknown): OverlayMaterialTextureOpacities | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const record = value as Record<string, unknown>;
  const result: OverlayMaterialTextureOpacities = {};
  for (const material of ["metal", "carbon", "frosted"] as const) {
    const opacity = record[material];
    if (typeof opacity === "number" && Number.isFinite(opacity)) result[material] = Math.max(0, Math.min(1, opacity));
  }
  return Object.keys(result).length ? result : undefined;
}

export function effectiveOverlayMaterialTextureOpacity(
  material: OverlayMaterial,
  instance?: OverlayMaterialTextureOpacities,
  global?: OverlayMaterialTextureOpacities,
): number {
  if (material === "default") return 1;
  return parseOverlayMaterialTextureOpacities(instance)?.[material]
    ?? parseOverlayMaterialTextureOpacities(global)?.[material]
    ?? OVERLAY_MATERIAL_TEXTURE_DEFAULTS[material];
}

/** Legacy instance darkMode values are intentionally ignored: existing inputs inherit. */
export function parseOverlayDarkModeOverride(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

export function effectiveOverlayDarkMode(override: boolean | undefined, globalDarkMode: boolean): boolean {
  return override ?? globalDarkMode;
}

export interface OverlayXYOffset {
  x: number;
  y: number;
}

export interface OverlayGroupOffsets {
  root: OverlayXYOffset;
  lead?: OverlayXYOffset;
  chase?: OverlayXYOffset;
}

export interface OverlayLowerThirdOffsets {
  root: OverlayXYOffset;
  nameBar?: OverlayXYOffset;
  subBar?: OverlayXYOffset;
  flag?: OverlayXYOffset;
  bib?: OverlayXYOffset;
  name?: OverlayXYOffset;
}

export interface OverlayCourseKMLRef {
  role: CourseKMLRole;
  name: string;
  url: string;
}

export type OverlayLowerThirdWidthMode = "fixed" | "fit";

/** Image framing shared by the player card portrait and the image canvas. */
export type OverlayPhotoFit = "fit" | "fill" | "stretch";

export interface OverlayPhotoPosition {
  /** Horizontal focal point in percent: 0 = left, 100 = right. */
  x: number;
  /** Vertical focal point in percent: 0 = top, 100 = bottom. */
  y: number;
}

export type OverlayPlayerCardPhotoFit = OverlayPhotoFit;
export type OverlayPlayerCardPhotoPosition = OverlayPhotoPosition;

export const DEFAULT_PLAYER_CARD_PHOTO_FIT: OverlayPlayerCardPhotoFit = "fill";
export const DEFAULT_PLAYER_CARD_PHOTO_POSITION: OverlayPlayerCardPhotoPosition = { x: 50, y: 50 };
export const DEFAULT_PLAYER_CARD_PHOTO_BACKGROUND: OverlayColorRef = { source: "custom", hex: "101418" };
export const DEFAULT_PLAYER_CARD_PHOTO_BACKGROUND_OPACITY = 1;

/** Web source viewport dimensions are bounded to the 1080p stage. */
export const WEB_SOURCE_URL_MAX_LENGTH = 8192;
export const WEB_SOURCE_SIZE_MIN = 16;
export const WEB_SOURCE_WIDTH_MAX = 1920;
export const WEB_SOURCE_HEIGHT_MAX = 1080;
export const DEFAULT_WEB_SOURCE_WIDTH = 1920;
export const DEFAULT_WEB_SOURCE_HEIGHT = 1080;

/**
 * Ticker bounds. Speed is stage px per second; bar height derives from the
 * text size (round(fontSize × 1.5)). The default bar spans title-safe
 * (x 192–1728) with ~24 px text, like a news crawl.
 */
export const TICKER_MESSAGE_LIMIT = 50;
export const TICKER_MESSAGE_MAX_LENGTH = 200;
export const TICKER_SPEED_MIN = 20;
export const TICKER_SPEED_MAX = 600;
export const DEFAULT_TICKER_SPEED = 120;
export const TICKER_WIDTH_MIN = 320;
export const TICKER_WIDTH_MAX = 1920;
export const DEFAULT_TICKER_WIDTH = 1536;
export const TICKER_FONT_SIZE_MIN = 12;
export const TICKER_FONT_SIZE_MAX = 96;
export const DEFAULT_TICKER_FONT_SIZE = 24;

/** Image canvas bounds (stage px). The default canvas is the whole 1920×1080 stage. */
export const IMAGE_CANVAS_SIZE_MIN = 16;
export const IMAGE_CANVAS_WIDTH_MAX = 3840;
export const IMAGE_CANVAS_HEIGHT_MAX = 2160;
export const DEFAULT_IMAGE_CANVAS_WIDTH = 1920;
export const DEFAULT_IMAGE_CANVAS_HEIGHT = 1080;
export const IMAGE_CANVAS_RADIUS_MAX = 200;
export const DEFAULT_IMAGE_CANVAS_PHOTO_FIT: OverlayPhotoFit = "fill";
export const DEFAULT_IMAGE_CANVAS_PHOTO_POSITION: OverlayPhotoPosition = { x: 50, y: 50 };
export const DEFAULT_IMAGE_CANVAS_BACKGROUND_OPACITY = 1;

/**
 * Self-contained broadcast snapshot the OBS page renders directly — the
 * unauthenticated overlay page cannot read athlete collections, so the
 * designer resolves the chosen record into these fixed dash-case keys
 * (mirroring the universal athlete role field IDs).
 */
export interface OverlayLowerThirdPerson {
  "full-name": string;
  /** Compact broadcast name ("A. Kolk"); render falls back to full-name when absent. */
  "short-name"?: string;
  "flag-url"?: string;
  /** Portrait photo for the player card — resolved at pick time like flag-url. */
  "headshot-url"?: string;
  bib?: string;
  "extra-label"?: string;
  "extra-value"?: string;
}

export interface OverlayCustomLowerThirdRow {
  id: string;
  name: string;
  accentText: string;
}

export const PYLON_ROW_LIMIT = 10;
export const SORT_FILTER_PYLON_ROW_LIMIT = 15;
export const SORT_FILTER_PYLON_DEFAULT_ROW_COUNT = 10;
/** Divider rows are exempt from PYLON_ROW_LIMIT and capped separately. */
export const PYLON_DIVIDER_LIMIT = 5;
export const PYLON_DIVIDER_TEXT_LIMIT = 40;
/** Sort-key value tag text cap (sort & filter pylon rows). */
export const PYLON_SORT_VALUE_LIMIT = 24;
export const CUSTOM_LOWER_THIRD_ROW_LIMIT = 100;
export const BUG_ROW_LIMIT = 20;
export const BILLBOARD_ROW_LIMIT = 10;
export const BILLBOARD_MESSAGE_LIMIT = 200;
export const DEFAULT_BILLBOARD_BACKGROUND_HEX = "ffffff";
export const DEFAULT_BILLBOARD_ACCENT_HEX = "c8102e";

/** Start list grid: 2 or 3 columns of up to 15 athletes each. */
export const START_LIST_ROWS_PER_COLUMN_MIN = 1;
export const START_LIST_ROWS_PER_COLUMN_MAX = 15;
export const START_LIST_DEFAULT_ROWS_PER_COLUMN = 7;
/** 3 columns × START_LIST_ROWS_PER_COLUMN_MAX — the persisted page snapshot cap. */
export const START_LIST_ROW_LIMIT = 45;
export const START_LIST_LOGO_HEIGHT_MIN = 40;
export const START_LIST_LOGO_HEIGHT_MAX = 240;
export const DEFAULT_START_LIST_LOGO_HEIGHT = 120;

export const LOWER_THIRD_LOGO_RADIUS_MAX = 60;
export const LOWER_THIRD_LOGO_PADDING_MAX = 60;

/** Optional logo box rendered left of the lower-third bars. */
export interface OverlayLowerThirdLogo {
  /** Logo/photo URL — any https URL (flag-url precedent, not bucket-restricted). */
  url: string;
  /** Background paint behind the logo; absent = transparent. */
  background?: OverlayColorRef;
  /** Corner radius of the background plate in px (0–LOWER_THIRD_LOGO_RADIUS_MAX); absent = 0. */
  cornerRadius?: number;
  /**
   * Horizontal padding each side of the logo in px (0–LOWER_THIRD_LOGO_PADDING_MAX).
   * Absent = the CSS default (14 on a colored plate, 0 when transparent), so 0 is
   * a meaningful override and is stored.
   */
  padding?: number;
}

/** One logo in a bug's rotation set. */
export interface OverlayBugRow {
  id: string;
  /** Logo/photo URL — any https URL (flag-url precedent, not bucket-restricted). */
  url: string;
  /** Optional 6-hex (no '#') background plate color; absent = bare transparent logo. */
  accentHex?: string;
}

/** One slate in a billboard's set. */
export interface OverlayBillboardRow {
  id: string;
  /** Logo/photo URL — any https URL (flag-url precedent, not bucket-restricted). */
  url: string;
  /** Optional caption under the logo (≤ BILLBOARD_MESSAGE_LIMIT chars, ~3 rendered lines). */
  message: string;
  /** 6-hex (no '#') card background; absent = DEFAULT_BILLBOARD_BACKGROUND_HEX (white). */
  backgroundHex?: string;
  /** Card-background opacity 0..1 (logo/text stay opaque); default 1. */
  backgroundOpacity: number;
  /** 6-hex (no '#') bottom accent stripe; absent = DEFAULT_BILLBOARD_ACCENT_HEX. */
  accentHex?: string;
}

export interface OverlayPylonAthleteRow {
  id: string;
  /** Absent on athlete rows; the discriminant lives on divider rows. */
  kind?: undefined;
  /** Source record key — lets the designer dedupe drag-ins. */
  recordKey?: string;
  /** Which dataset the row came from ("league-athletes" for the pool). */
  sourceID?: string;
  /** Sort-key display value captured by the sort-and-filter derivation; undefined elsewhere. */
  sortValue?: string;
  person: OverlayLowerThirdPerson;
}

/** Accent-colored group separator ("LEAD" / "CHASE") — never ranked, exempt from the athlete cap. */
export interface OverlayPylonDividerRow {
  id: string;
  kind: "divider";
  text: string;
  /** Never set — declared so athlete dedupe checks can read the union unguarded. */
  recordKey?: undefined;
}

export type OverlayPylonRow = OverlayPylonAthleteRow | OverlayPylonDividerRow;

export function isPylonDividerRow(row: OverlayPylonRow): row is OverlayPylonDividerRow {
  return row.kind === "divider";
}

export function pylonAthleteRows(rows: OverlayPylonRow[]): OverlayPylonAthleteRow[] {
  return rows.filter((row): row is OverlayPylonAthleteRow => row.kind !== "divider");
}

/** Rank per row: running athlete count for athlete rows, null for dividers. */
export function pylonRowRanks(rows: OverlayPylonRow[], rankOffset = 0): (number | null)[] {
  let rank = rankOffset;
  return rows.map((row) => (isPylonDividerRow(row) ? null : ++rank));
}

/** Order-preserving athlete cap with a separate PYLON_DIVIDER_LIMIT for dividers. */
export function capPylonRows(rows: OverlayPylonRow[], athleteLimit = PYLON_ROW_LIMIT): OverlayPylonRow[] {
  let athletes = 0;
  let dividers = 0;
  return rows.filter((row) => (isPylonDividerRow(row) ? ++dividers <= PYLON_DIVIDER_LIMIT : ++athletes <= athleteLimit));
}

export const PODIUM_SLOT_LIMIT = 5;
export type OverlayPodiumPlace = 1 | 2 | 3 | 4 | 5;
export type OverlayPodiumDepth = 3 | 5;

/** One filled podium place. `place` is the identity — unique per instance. */
export interface OverlayPodiumSlot {
  place: OverlayPodiumPlace;
  /** Source record key — lets the designer dedupe drag-ins. */
  recordKey?: string;
  /** Which dataset the slot came from ("league-athletes" for the pool). */
  sourceID?: string;
  person: OverlayLowerThirdPerson;
}

/** On-screen bar order, winner center: white (4/5) flank the medal bars. */
export const PODIUM_DISPLAY_ORDER: Record<OverlayPodiumDepth, OverlayPodiumPlace[]> = {
  3: [2, 1, 3],
  5: [4, 2, 1, 3, 5],
};

/** Slots the current depth actually shows (and the content gate counts). */
export function podiumVisibleSlots(instance: Pick<OverlayPodiumInstance, "slots" | "depth">): OverlayPodiumSlot[] {
  return instance.slots.filter((slot) => slot.place <= instance.depth);
}

/** Place-unique (first wins), sorted, capped slot list. */
export function normalizePodiumSlots(slots: OverlayPodiumSlot[]): OverlayPodiumSlot[] {
  const byPlace = new Map<OverlayPodiumPlace, OverlayPodiumSlot>();
  for (const slot of slots) {
    if (!byPlace.has(slot.place)) byPlace.set(slot.place, slot);
  }
  return [...byPlace.values()].sort((a, b) => a.place - b.place).slice(0, PODIUM_SLOT_LIMIT);
}

// --- Instance model ---------------------------------------------------------

// Every rail write persists one railTypeGroups entry per type here
// (parseRailTypeGroups back-fills missing ones), so this roster's length must
// stay within firestore.rules validOverlayRailTypeGroups' list-size cap —
// bump the cap there before growing the roster past it. 24 types today = the
// cap (24) exactly, so the next type must raise it first.
export type OverlayElementType = "gps" | "timer" | "pylon" | "gpsPylon" | "sortFilterPylon" | "lowerThird" | "customLowerThird" | "playerCard" | "scoreboard" | "miniMap" | "fullMap" | "courseProfile" | "speedometer" | "gradeScope" | "customWidget" | "bug" | "compass" | "billboard" | "podium" | "imageCanvas" | "webSource" | "startList" | "ticker" | "advancedTimer";
export const OVERLAY_ELEMENT_TYPES: OverlayElementType[] = ["gps", "timer", "pylon", "gpsPylon", "sortFilterPylon", "lowerThird", "customLowerThird", "playerCard", "scoreboard", "miniMap", "fullMap", "courseProfile", "speedometer", "gradeScope", "customWidget", "bug", "compass", "billboard", "podium", "imageCanvas", "webSource", "startList", "ticker", "advancedTimer"];

/** Default top-to-bottom order of the designer rail's type groups (display order, distinct from OVERLAY_ELEMENT_TYPES). */
export const OVERLAY_DEFAULT_RAIL_TYPE_ORDER: OverlayElementType[] = ["gps", "timer", "advancedTimer", "scoreboard", "ticker", "bug", "billboard", "fullMap", "imageCanvas", "webSource", "miniMap", "courseProfile", "speedometer", "gradeScope", "compass", "lowerThird", "customLowerThird", "playerCard", "pylon", "gpsPylon", "sortFilterPylon", "startList", "podium", "customWidget"];

export const OVERLAY_ELEMENT_TYPE_LABELS: Record<OverlayElementType, string> = {
  gps: "GPS group",
  timer: "Timer",
  pylon: "Pylon",
  gpsPylon: "GPS Pylon",
  sortFilterPylon: "Sort & Filter Pylon",
  lowerThird: "Lower third",
  customLowerThird: "Custom lower third",
  playerCard: "Player card",
  scoreboard: "Scoreboard",
  miniMap: "Mini-map",
  fullMap: "Full-screen map",
  courseProfile: "Course profile",
  speedometer: "Speedometer",
  gradeScope: "Grade Scope",
  customWidget: "Custom widget",
  bug: "Bug",
  compass: "Compass",
  billboard: "Billboard",
  podium: "Podium",
  imageCanvas: "Image Canvas",
  webSource: "Web Source",
  startList: "Start List",
  ticker: "Ticker",
  advancedTimer: "Advanced Timer",
};

/** Firestore section key under `elements` for each element type. */
export const OVERLAY_ELEMENT_TYPE_KEYS: Record<OverlayElementType, keyof OverlayElements> = {
  gps: "gpsGroups",
  timer: "timers",
  pylon: "pylons",
  gpsPylon: "gpsPylons",
  sortFilterPylon: "sortFilterPylons",
  lowerThird: "lowerThirds",
  customLowerThird: "customLowerThirds",
  playerCard: "playerCards",
  scoreboard: "scoreboards",
  miniMap: "miniMaps",
  fullMap: "fullMaps",
  courseProfile: "courseProfiles",
  speedometer: "speedometers",
  gradeScope: "gradeScopes",
  customWidget: "customWidgets",
  bug: "bugs",
  compass: "compasses",
  billboard: "billboards",
  podium: "podiums",
  imageCanvas: "imageCanvases",
  webSource: "webSources",
  startList: "startLists",
  ticker: "tickers",
  advancedTimer: "advancedTimers",
};

export const OVERLAY_INSTANCE_CAP = 8;
/** Mini-maps are full MapKit canvases — keep the on-air count small. */
export const OVERLAY_MINIMAP_INSTANCE_CAP = 4;
export const OVERLAY_COURSE_PROFILE_INSTANCE_CAP = 4;
/** Kept small for control-key headroom (caps must total ≤ MAX_CONTROL_KEYS 100). */
export const OVERLAY_GRADE_SCOPE_INSTANCE_CAP = 4;
export const OVERLAY_CUSTOM_WIDGET_INSTANCE_CAP = 3;
export const OVERLAY_BUG_INSTANCE_CAP = 4;
export const OVERLAY_COMPASS_INSTANCE_CAP = 4;
export const OVERLAY_BILLBOARD_INSTANCE_CAP = 2;
export const OVERLAY_GPS_PYLON_INSTANCE_CAP = 2;
export const OVERLAY_SORT_FILTER_PYLON_INSTANCE_CAP = 2;
export const OVERLAY_PLAYER_CARD_INSTANCE_CAP = 4;
export const OVERLAY_IMAGE_CANVAS_INSTANCE_CAP = 4;
export const OVERLAY_WEB_SOURCE_INSTANCE_CAP = 4;
export const OVERLAY_START_LIST_INSTANCE_CAP = 2;
export const OVERLAY_TICKER_INSTANCE_CAP = 2;
/**
 * Caps now total exactly MAX_CONTROL_KEYS 100 — any future type (or cap bump)
 * must raise MAX_CONTROL_KEYS in functions/src/overlayRuntime.ts and re-audit
 * heartbeat parsing.
 */
export const OVERLAY_ADVANCED_TIMER_INSTANCE_CAP = 4;

/** Per-type instance limit — the scoreboard, full-screen map, and podium are single-instance. */
export function overlayInstanceCap(type: OverlayElementType): number {
  if (type === "scoreboard") return 1;
  if (type === "fullMap") return 1;
  if (type === "podium") return 1;
  if (type === "miniMap") return OVERLAY_MINIMAP_INSTANCE_CAP;
  if (type === "courseProfile") return OVERLAY_COURSE_PROFILE_INSTANCE_CAP;
  if (type === "gradeScope") return OVERLAY_GRADE_SCOPE_INSTANCE_CAP;
  if (type === "customWidget") return OVERLAY_CUSTOM_WIDGET_INSTANCE_CAP;
  if (type === "bug") return OVERLAY_BUG_INSTANCE_CAP;
  if (type === "compass") return OVERLAY_COMPASS_INSTANCE_CAP;
  if (type === "billboard") return OVERLAY_BILLBOARD_INSTANCE_CAP;
  if (type === "gpsPylon") return OVERLAY_GPS_PYLON_INSTANCE_CAP;
  if (type === "sortFilterPylon") return OVERLAY_SORT_FILTER_PYLON_INSTANCE_CAP;
  if (type === "playerCard") return OVERLAY_PLAYER_CARD_INSTANCE_CAP;
  if (type === "imageCanvas") return OVERLAY_IMAGE_CANVAS_INSTANCE_CAP;
  if (type === "webSource") return OVERLAY_WEB_SOURCE_INSTANCE_CAP;
  if (type === "startList") return OVERLAY_START_LIST_INSTANCE_CAP;
  if (type === "ticker") return OVERLAY_TICKER_INSTANCE_CAP;
  if (type === "advancedTimer") return OVERLAY_ADVANCED_TIMER_INSTANCE_CAP;
  return OVERLAY_INSTANCE_CAP;
}
export const OVERLAY_INSTANCE_ID_PATTERN = /^[A-Za-z0-9_-]{1,40}$/;

export type OverlayHorizontalRegion = "left" | "right";
export type OverlayTimerRegion = "top" | "bottom";
export type OverlayBugCorner = "top-left" | "top-right" | "bottom-left" | "bottom-right";
export type OverlayTimerDirection = "up" | "down";
export type OverlaySpeedUnit = "mph" | "kmh";
export type OverlaySpeedometerDisplayMode = "analog" | "digital";

export const DEFAULT_OVERLAY_TRANSITION_DURATION_MS = 420;
export const MAX_OVERLAY_TRANSITION_DURATION_MS = 10_000;

/** Unset/invalid values retain the renderer's default; zero is an instant cut. */
export function normalizeOverlayTransitionDurationMs(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.round(Math.min(MAX_OVERLAY_TRANSITION_DURATION_MS, Math.max(0, value)))
    : undefined;
}

export interface OverlayRootScale { x: number; y: number }

interface OverlayInstanceBase {
  /** Whole-instance visual scale, applied after the graphic's existing layout and sizing. */
  rootScale?: OverlayRootScale;
  /** Stable generated id (crypto.randomUUID or deterministic "legacy-*"). */
  id: string;
  /** Operator-facing title shown in the designer rail. */
  name: string;
  visible: boolean;
  /** IN/OUT animation duration. Unset uses 420ms. Draft styling: applies on Save. */
  transitionDurationMs?: number;
  /**
   * When true (default), taking this instance IN takes OUT every other live
   * same-type instance in the same region — and vice versa. When false the
   * instance neither takes others out nor is taken out (manual stacking).
   */
  avoidCollisions: boolean;
}

export interface OverlayGpsGroupInstance extends OverlayInstanceBase {
  region: OverlayHorizontalRegion;
  /** Color mode override for distance/chase bodies: unset inherits design.darkMode. Draft-tier — applies on save. */
  darkModeOverride?: boolean;
  /** Known GPSAthleteCategory value or a free-form custom tracking identifier. */
  category: string;
  /** Keep the chase chip on stage. It stays in whenever on, even before a chase group exists. Draft-tier. */
  showChase: boolean;
  /**
   * Always show a split value in the chase chip: the live split when one is
   * computed, else a "+00:00" placeholder so the chip's full size is visible
   * while designing. Draft-tier — applies on save. Unset = false.
   */
  showChaseValue?: boolean;
  /** Count down from the course distance instead of counting up. Applies live, not on save. */
  showDistanceRemaining: boolean;
  /** Show the current leader speed in the title bar. Applies live, not on save. */
  showSpeed: boolean;
  /** Show the course grade at the leader's position in the title bar. Mutually exclusive with showSpeed. Applies live, not on save. */
  showGrade: boolean;
  /** Show the leader's pace (min/mi or min/km per speedUnit) in the title bar. Mutually exclusive with showSpeed/showGrade. Applies live, not on save. */
  showPace: boolean;
  /** Unit used for the optional leader speed and pace displays. Applies live, not on save. */
  speedUnit: OverlaySpeedUnit;
  leadLabel: string;
  chaseLabel: string;
  /** Lead card user scale (fraction of natural size; unset = 1). */
  leadScale?: number;
  /** Chase chip user scale (fraction of natural size; unset = 1). */
  chaseScale?: number;
  /** Fixed lead card width in stage px at 100% scale (layout width — text size untouched; unset = auto). */
  leadWidth?: number;
  /** Fixed chase chip width in stage px at 100% scale (layout width — text size untouched; unset = auto). */
  chaseWidth?: number;
  /**
   * Keep the chase chip at a stable width regardless of label length: the chip
   * holds its width (chaseWidth, else the 300px default floor), the split
   * reading is laid out first, and a label that would widen the chip is
   * ellipsized instead. Draft-tier — applies on save. Unset = false.
   */
  lockChaseWidth?: boolean;
  /** Per-instance skin override; unset = inherit design.skin. "default" is a real value here (forces the flat look). Draft-tier. */
  skin?: OverlaySkin;
  customSkin?: OverlayCustomSkinReference;
  /** Per-instance material override; unset = inherit design.material. "default" is a real value here (forces flat paint). Draft-tier. */
  material?: OverlayMaterial;
  /** Draft-tier texture opacity overrides, keyed by material. */
  materialTextureOpacities?: OverlayMaterialTextureOpacities;
  offsets: OverlayGroupOffsets;
}

/**
 * Broadcast timer. Nothing ticks in Firestore — the doc stores state and every
 * client derives the reading from a skew-corrected clock, so all sessions and
 * the OBS page show the same time with no drift by construction.
 */
export interface OverlayTimerInstance extends OverlayInstanceBase {
  region: OverlayTimerRegion;
  /** Chip label, e.g. "M" / "F" / "NB". */
  label?: string;
  direction: OverlayTimerDirection;
  running: boolean;
  /** Skew-corrected timestamp of the last start/resume; null while paused. */
  startedAt: Date | null;
  /** Elapsed milliseconds accumulated before startedAt (pauses, adjustments). */
  offsetMs: number;
  /** Countdown start value in milliseconds (direction "down"). */
  durationMs: number;
  /** Color mode override: unset inherits design.darkMode; false forces Light, true forces Dark. Draft-tier — applies on save. */
  darkModeOverride?: boolean;
  /** Per-instance skin override; unset = inherit design.skin. "default" is a real value here (forces the flat look). Draft-tier. */
  skin?: OverlaySkin;
  customSkin?: OverlayCustomSkinReference;
  /** Per-instance material override; unset = inherit design.material. "default" is a real value here (forces flat paint). Draft-tier. */
  material?: OverlayMaterial;
  /** Draft-tier texture opacity overrides, keyed by material. */
  materialTextureOpacities?: OverlayMaterialTextureOpacities;
  offsets: OverlayLowerThirdOffsets;
}

export interface OverlayPylonInstance extends OverlayInstanceBase {
  region: OverlayHorizontalRegion;
  /** Optional title bar rendered above the stack. */
  label?: string;
  /** Show the ordinal rank beside each athlete name. Applies live, not on save. */
  showRank: boolean;
  /** Show athlete flags and bibs. Draft-tier — applies on save; defaults enabled. */
  useFlag: boolean;
  useBib: boolean;
  /** Render short names ("A. Kolk") when a row's snapshot carries one. Draft-tier — applies on save. */
  useShortName: boolean;
  /** Anchor the bottom row instead of the top: new rows push the list upward. Draft-tier — applies on save. */
  anchorBottom: boolean;
  widthMode: OverlayLowerThirdWidthMode;
  /** Shrink the name (and sub) text font to fit the graphic width instead of ellipsizing. Draft-tier — applies on save. */
  shrinkTextToFit: boolean;
  /** Color mode override: unset inherits design.darkMode; false forces Light, true forces Dark. Draft-tier — applies on save. */
  darkModeOverride?: boolean;
  /** Per-instance skin override; unset = inherit design.skin. "default" is a real value here (forces the flat look). Draft-tier. */
  skin?: OverlaySkin;
  customSkin?: OverlayCustomSkinReference;
  /** Per-instance material override; unset = inherit design.material. "default" is a real value here (forces flat paint). Draft-tier. */
  material?: OverlayMaterial;
  /** Draft-tier texture opacity overrides, keyed by material. */
  materialTextureOpacities?: OverlayMaterialTextureOpacities;
  /** Optional uniform downscale cap in stage px; unset = natural size. */
  maxWidth?: number;
  maxHeight?: number;
  /** Optional content-independent uniform scale (1 = 100%); the caps above only ever shrink below it. Unset = 1. */
  scale?: number;
  offsets: OverlayLowerThirdOffsets;
  /** Designer-state restore only: which dataset is open in the picker. */
  sourceID?: string;
  rows: OverlayPylonRow[];
}

export type OverlaySortFilterPylonDirection = "asc" | "desc";

/**
 * Pylon whose rows are DERIVED, not hand-picked: the designer recomputes the
 * selected page of the bound external source (filter → sort → page) on every
 * records reload and persists them as ordinary row snapshots — the runtime
 * renders the snapshot only. `sourceID` is therefore live data here, unlike the
 * plain pylon where it just restores the picker.
 */
export interface OverlaySortFilterPylonInstance extends OverlayPylonInstance {
  /** Source mapping targetFieldID to sort by; unset keeps source order. Applies live, not on save. */
  sortFieldKey?: string;
  /** Applies live, not on save. */
  sortDirection: OverlaySortFilterPylonDirection;
  /** Source mapping targetFieldID to filter on. Applies live, not on save. */
  filterFieldKey?: string;
  /** Case-insensitive trimmed exact match; empty/absent = no filtering. Applies live, not on save. */
  filterValue?: string;
  /** Rows kept after filter+sort (1..SORT_FILTER_PYLON_ROW_LIMIT). Applies live, not on save. */
  rowCount: number;
  /** Zero-based page of filtered/sorted records. Applies live, not on save. */
  pageIndex: number;
  /** Show the sort-key value as a tag on the right of each row. Applies live, not on save. */
  showSortValue: boolean;
}

/** The GPS pylon's single optional header readout — all options are mutually exclusive by construction. */
export type OverlayGpsPylonHeaderReadout = "distance" | "distanceToGo" | "speed" | "grade" | "pace";
export const OVERLAY_GPS_PYLON_HEADER_READOUTS: OverlayGpsPylonHeaderReadout[] = ["distance", "distanceToGo", "speed", "grade", "pace"];

/**
 * Pylon/GPS hybrid: a pylon leaderboard bound to one GPS category. Divider rows
 * double as chase-group markers — the k-th divider shows the live gap of the
 * (k+1)-th ranked device — and the title bar carries one optional live readout.
 */
export interface OverlayGpsPylonInstance extends OverlayInstanceBase {
  region: OverlayHorizontalRegion;
  /** Optional title bar rendered above the stack. */
  label?: string;
  /** Known GPSAthleteCategory value or a free-form custom tracking identifier. */
  category: string;
  /** Optional live readout on the right of the title bar; unset = none. Applies live, not on save. */
  headerReadout?: OverlayGpsPylonHeaderReadout;
  /** Unit used for the speed and pace header readouts. Applies live, not on save. */
  speedUnit: OverlaySpeedUnit;
  /** Show the ordinal rank beside each athlete name. Applies live, not on save. */
  showRank: boolean;
  /** Show athlete flags and bibs. Draft-tier — applies on save; defaults enabled. */
  useFlag: boolean;
  useBib: boolean;
  /** Render short names ("A. Kolk") when a row's snapshot carries one. Draft-tier — applies on save. */
  useShortName: boolean;
  /** Anchor the bottom row instead of the top: new rows push the list upward. Draft-tier — applies on save. */
  anchorBottom: boolean;
  widthMode: OverlayLowerThirdWidthMode;
  /** Shrink the name (and sub) text font to fit the graphic width instead of ellipsizing. Draft-tier — applies on save. */
  shrinkTextToFit: boolean;
  /** Color mode override: unset inherits design.darkMode; false forces Light, true forces Dark. Draft-tier — applies on save. */
  darkModeOverride?: boolean;
  /** Per-instance skin override; unset = inherit design.skin. "default" is a real value here (forces the flat look). Draft-tier. */
  skin?: OverlaySkin;
  customSkin?: OverlayCustomSkinReference;
  /** Per-instance material override; unset = inherit design.material. "default" is a real value here (forces flat paint). Draft-tier. */
  material?: OverlayMaterial;
  /** Draft-tier texture opacity overrides, keyed by material. */
  materialTextureOpacities?: OverlayMaterialTextureOpacities;
  /** Optional uniform downscale cap in stage px; unset = natural size. */
  maxWidth?: number;
  maxHeight?: number;
  /** Optional content-independent uniform scale (1 = 100%); the caps above only ever shrink below it. Unset = 1. */
  scale?: number;
  offsets: OverlayLowerThirdOffsets;
  /** Designer-state restore only: which dataset is open in the picker. */
  sourceID?: string;
  rows: OverlayPylonRow[];
}

/**
 * Winner-center row of medal bars near the bottom of the stage: gold (1st)
 * center, silver/bronze beside it, white 4th/5th flanking in Top-5 mode.
 * Slots hold self-contained person snapshots keyed by podium place.
 */
export interface OverlayPodiumInstance extends OverlayInstanceBase {
  /** Fixed nominal region — keeps shared instance plumbing region-safe. */
  region: "bottom";
  /** Optional accent title bar rendered above the row. Draft-tier — applies on save. */
  label?: string;
  /** Podium depth: Top 3 or Top 5. Applies live; dropping to 3 hides but keeps slots 4–5. */
  depth: OverlayPodiumDepth;
  /** Show ordinal badges on the bars. Draft-tier — applies on save. */
  showPlace: boolean;
  /** Show athlete flags and bibs. Draft-tier — applies on save; defaults enabled. */
  useFlag: boolean;
  useBib: boolean;
  /** Render short names ("A. Kolk") when a slot's snapshot carries one. Draft-tier — applies on save. */
  useShortName: boolean;
  /** Shrink each bar's name font to fit `barMaxWidth` instead of growing the bar. Draft-tier — applies on save. */
  shrinkTextToFit: boolean;
  /** Color mode override: unset inherits design.darkMode; false forces Light, true forces Dark. Draft-tier — applies on save. */
  darkModeOverride?: boolean;
  /** Per-instance skin override; unset = inherit design.skin. "default" is a real value here (forces the flat look). Draft-tier. */
  skin?: OverlaySkin;
  customSkin?: OverlayCustomSkinReference;
  /** Per-instance material override; unset = inherit design.material. "default" is a real value here (forces flat paint). Draft-tier. */
  material?: OverlayMaterial;
  /** Draft-tier texture opacity overrides, keyed by material. */
  materialTextureOpacities?: OverlayMaterialTextureOpacities;
  /** Per-bar width cap in stage px, honored only while shrinkTextToFit is on; unset = DEFAULT_PODIUM_BAR_MAX_WIDTH. */
  barMaxWidth?: number;
  /** Optional uniform downscale cap in stage px; unset = natural size. */
  maxWidth?: number;
  maxHeight?: number;
  /** Optional content-independent uniform scale (1 = 100%); the caps above only ever shrink below it. Unset = 1. */
  scale?: number;
  offsets: OverlayLowerThirdOffsets;
  /** Designer-state restore only: which dataset is open in the picker. */
  sourceID?: string;
  /** Sparse — only filled places, sorted, unique by place. Applies live, not on save. */
  slots: OverlayPodiumSlot[];
}

export interface OverlayLowerThirdInstance extends OverlayInstanceBase {
  mode: "data" | "manual";
  rows: OverlayCustomLowerThirdRow[];
  selectedRowID?: string;
  /** Captured stage-edge fit origin; keeps scaling compensation from changing text layout. */
  fitLayoutReferenceLeft?: number;
  region: OverlayHorizontalRegion;
  useFlag: boolean;
  useBib: boolean;
  showExtraLabel: boolean;
  /** Render the short name ("A. Kolk") when the person snapshot carries one. Draft-tier — applies on save. */
  useShortName: boolean;
  widthMode: OverlayLowerThirdWidthMode;
  /** Shrink the name (and sub) text font to fit the graphic width instead of ellipsizing. Draft-tier — applies on save. */
  shrinkTextToFit: boolean;
  /** Color mode override: unset inherits design.darkMode; false forces Light, true forces Dark. Draft-tier — applies on save. */
  darkModeOverride?: boolean;
  /** Per-instance skin override; unset = inherit design.skin. "default" is a real value here (forces the flat look). Draft-tier. */
  skin?: OverlaySkin;
  customSkin?: OverlayCustomSkinReference;
  /** Per-instance material override; unset = inherit design.material. "default" is a real value here (forces flat paint). Draft-tier. */
  material?: OverlayMaterial;
  /** Draft-tier texture opacity overrides, keyed by material. */
  materialTextureOpacities?: OverlayMaterialTextureOpacities;
  /** Optional uniform downscale cap in stage px; unset = natural size. */
  maxWidth?: number;
  maxHeight?: number;
  /** Optional content-independent uniform scale (1 = 100%); the caps above only ever shrink below it. Unset = 1. */
  scale?: number;
  offsets: OverlayLowerThirdOffsets;
  /** Optional logo box left of the bars. Draft-tier — applies on save. */
  logo?: OverlayLowerThirdLogo;
  /** Designer-state restore only: which dataset was picked ("league-athletes" for the pool). */
  sourceID?: string;
  /** Designer-state restore only: which record/athlete was picked. */
  recordKey?: string;
  person: OverlayLowerThirdPerson | null;
}

/** Resolve the selected mode without exposing retained data fields in manual mode. */
export function resolvedLowerThirdPerson(instance: OverlayLowerThirdInstance): OverlayLowerThirdPerson | null {
  if (instance.mode !== "manual") return instance.person;
  const row = instance.rows.find((candidate) => candidate.id === instance.selectedRowID);
  if (!row?.name.trim()) return null;
  return { "full-name": row.name.trim(), "extra-value": row.accentText.trim() || undefined };
}

export function lowerThirdReadinessIssue(instance: OverlayLowerThirdInstance): string | undefined {
  return resolvedLowerThirdPerson(instance)?.["full-name"].trim() ? undefined
    : instance.mode === "manual" ? "Select a manual row with a name before taking this Lower Third in." : "Select a person before taking this Lower Third in.";
}

/**
 * Trading-card style graphic: 3:4 portrait (person["headshot-url"]) on a dark
 * plate, lower-third-style name bar underneath, then the accent bar. Fixed
 * 420 px column — no widthMode; the shared scale/max-size caps still apply.
 */
export interface OverlayPlayerCardInstance extends OverlayInstanceBase {
  region: OverlayHorizontalRegion;
  useFlag: boolean;
  useBib: boolean;
  showExtraLabel: boolean;
  /** Render the short name ("A. Kolk") when the person snapshot carries one. Draft-tier — applies on save. */
  useShortName: boolean;
  /** Color mode override: unset inherits design.darkMode; false forces Light, true forces Dark. Draft-tier — applies on save. */
  darkModeOverride?: boolean;
  /** Per-instance skin override; unset = inherit design.skin. "default" is a real value here (forces the flat look). Draft-tier. */
  skin?: OverlaySkin;
  customSkin?: OverlayCustomSkinReference;
  /** Per-instance material override; unset = inherit design.material. "default" is a real value here (forces flat paint). Draft-tier. */
  material?: OverlayMaterial;
  /** Draft-tier texture opacity overrides, keyed by material. */
  materialTextureOpacities?: OverlayMaterialTextureOpacities;
  /** Portrait scaling inside the fixed 3:4 plate. Draft-tier — applies on save. */
  photoFit: OverlayPlayerCardPhotoFit;
  /** Portrait focal point in percentages. Draft-tier — applies on save. */
  photoPosition: OverlayPlayerCardPhotoPosition;
  /** Paint behind the portrait photo and Fit letterboxing. Draft-tier — applies on save. */
  photoBackground: OverlayColorRef;
  /** Opacity of the portrait background only; the photo stays fully opaque. Draft-tier — applies on save. */
  photoBackgroundOpacity: number;
  /** Optional uniform downscale cap in stage px; unset = natural size. */
  maxWidth?: number;
  maxHeight?: number;
  /** Optional content-independent uniform scale (1 = 100%); the caps above only ever shrink below it. Unset = 1. */
  scale?: number;
  offsets: OverlayLowerThirdOffsets;
  /** Designer-state restore only: which dataset was picked ("league-athletes" for the pool). */
  sourceID?: string;
  /** Designer-state restore only: which record/athlete was picked. */
  recordKey?: string;
  person: OverlayLowerThirdPerson | null;
}

export interface OverlayCustomLowerThirdInstance extends OverlayInstanceBase {
  /** Captured stage-edge fit origin; keeps scaling compensation from changing text layout. */
  fitLayoutReferenceLeft?: number;
  region: OverlayHorizontalRegion;
  widthMode: OverlayLowerThirdWidthMode;
  /** Shrink the name (and sub) text font to fit the graphic width instead of ellipsizing. Draft-tier — applies on save. */
  shrinkTextToFit: boolean;
  /** Color mode override: unset inherits design.darkMode; false forces Light, true forces Dark. Draft-tier — applies on save. */
  darkModeOverride?: boolean;
  /** Per-instance skin override; unset = inherit design.skin. "default" is a real value here (forces the flat look). Draft-tier. */
  skin?: OverlaySkin;
  customSkin?: OverlayCustomSkinReference;
  /** Per-instance material override; unset = inherit design.material. "default" is a real value here (forces flat paint). Draft-tier. */
  material?: OverlayMaterial;
  /** Draft-tier texture opacity overrides, keyed by material. */
  materialTextureOpacities?: OverlayMaterialTextureOpacities;
  /** Optional uniform downscale cap in stage px; unset = natural size. */
  maxWidth?: number;
  maxHeight?: number;
  /** Optional content-independent uniform scale (1 = 100%); the caps above only ever shrink below it. Unset = 1. */
  scale?: number;
  offsets: OverlayLowerThirdOffsets;
  /** Optional logo box left of the bars. Draft-tier — applies on save. */
  logo?: OverlayLowerThirdLogo;
  /** Live-tier — persists immediately, no Save step (like pylon rows). */
  selectedRowID?: string;
  /** Live-tier — persists immediately, no Save step (like pylon rows). */
  rows: OverlayCustomLowerThirdRow[];
}

/**
 * Top-center match scoreboard. Single-instance (overlayInstanceCap), and the
 * only element with a cross-type collision rule: taking it IN takes every live
 * GPS group OUT (both regions) and vice versa — see collisionTakeOuts in
 * functions/src/overlayRuntime.ts. Scores are live fields (immediate writes);
 * names/colors/offsets are styling (saved with the draft).
 */
export interface OverlayScoreboardInstance extends OverlayInstanceBase {
  /** Fixed nominal region — keeps shared instance plumbing region-safe. */
  region: "top";
  team1Name: string;
  team2Name: string;
  /** Left accent stripe. */
  team1Color: OverlayColorRef;
  /** Right accent stripe. */
  team2Color: OverlayColorRef;
  /** Integer ≥ 0, stepper-controlled. */
  score1: number;
  score2: number;
  /** Color mode override: unset inherits design.darkMode; false forces Light, true forces Dark. Draft-tier — applies on save. */
  darkModeOverride?: boolean;
  /** Per-instance skin override; unset = inherit design.skin. "default" is a real value here (forces the flat look). Draft-tier. */
  skin?: OverlaySkin;
  customSkin?: OverlayCustomSkinReference;
  /** Per-instance material override; unset = inherit design.material. "default" is a real value here (forces flat paint). Draft-tier. */
  material?: OverlayMaterial;
  /** Draft-tier texture opacity overrides, keyed by material. */
  materialTextureOpacities?: OverlayMaterialTextureOpacities;
  offsets: OverlayGroupOffsets;
}

/**
 * Circular mini-map: a MapKit map masked to a circle, kept centered on one
 * GPS device while the map pans underneath. Device lock and the speed badge
 * are live fields (immediate writes); zoom/diameter/ring/bib/label/offsets
 * are styling (saved with the draft).
 */
export interface OverlayMiniMapInstance extends OverlayInstanceBase {
  region: OverlayHorizontalRegion;
  /** gpsData doc id of the centered device (overlay-feed device id). Empty = unlocked. */
  deviceID: string;
  /** MapKit CoordinateSpan latitude delta — smaller = closer zoom. */
  zoomSpan: number;
  /** Circle diameter in 1920×1080 stage pixels. */
  diameter: number;
  /** Circular ring border color. */
  ringColor: OverlayColorRef;
  /** Custom bib text inside the centered marker. */
  bibText: string;
  /** Label pill below the marker (e.g. "MLEAD"). */
  labelText: string;
  /** Show the device speed badge. Applies live, not on save. */
  showSpeed: boolean;
  /** Unit for the speed badge. Applies live, not on save. */
  speedUnit: OverlaySpeedUnit;
  offsets: OverlayGroupOffsets;
}

export type OverlayFullMapFraming = "follow" | "course";
export type OverlayFullMapType = "standard" | "mutedStandard" | "satellite" | "hybrid";
export type OverlayFullMapColorScheme = "light" | "dark";

/**
 * Full-screen map takeover: a MapKit map filling the whole 1920×1080 stage,
 * rendered under every other element. Single-instance (overlayInstanceCap).
 * Device lock and framing are live fields (immediate writes); map type, color
 * scheme, tint, rotation, zoom, and the course line color are styling (saved
 * with the draft).
 */
export interface OverlayFullMapInstance extends OverlayInstanceBase {
  /** Fixed nominal region — keeps shared instance plumbing region-safe. */
  region: "full";
  /** "follow" pans with the locked device; "course" holds a camera fitted to the course bounds. */
  framing: OverlayFullMapFraming;
  /** gpsData doc id of the followed device (overlay-feed device id). Empty = unlocked. */
  deviceID: string;
  /** MapKit CoordinateSpan latitude delta for follow framing — smaller = closer zoom. */
  zoomSpan: number;
  mapType: OverlayFullMapType;
  colorScheme: OverlayFullMapColorScheme;
  /** MapKit UI tint (annotation/control accents). */
  tintColor: OverlayColorRef;
  /** Manual map rotation, integer degrees clockwise 0–359. */
  rotation: number;
  /** Course polyline stroke color. */
  courseLineColor: OverlayColorRef;
}

export const COURSE_PROFILE_TRACK_LIMIT = 8;
export const COURSE_PROFILE_WIDTH_MIN = 400;
export const COURSE_PROFILE_WIDTH_MAX = 1920;
export const COURSE_PROFILE_HEIGHT_MIN = 60;
export const COURSE_PROFILE_HEIGHT_MAX = 300;

/** One tracked GPS on a course profile: which device, its fill color, and an optional leading-edge chip. */
export interface OverlayCourseProfileTrack {
  id: string;
  /** gpsData doc id of the tracked device (overlay-feed device id). Empty = unassigned. */
  deviceID: string;
  color: OverlayColorRef;
  /** Optional label chip at the fill's leading edge (e.g. "MLEAD"). */
  label: string;
}

/**
 * Course elevation profile: a grey silhouette generated from the course KML's
 * elevation samples, with per-device colored progress fills layered so the
 * farthest-advanced device paints just above the base and the least-advanced
 * paints topmost. The track list is a live field (immediate writes, like
 * pylon rows); geometry and base appearance are styling (saved with draft).
 */
export interface OverlayCourseProfileInstance extends OverlayInstanceBase {
  /** Fixed nominal region — keeps shared instance plumbing region-safe. */
  region: "bottom";
  /** Profile width/height in 1920×1080 stage pixels. */
  width: number;
  height: number;
  /** Silhouette fill. */
  baseColor: OverlayColorRef;
  /** Silhouette opacity, 0..1. */
  baseOpacity: number;
  /** Live tracked devices, ≤ COURSE_PROFILE_TRACK_LIMIT. */
  tracks: OverlayCourseProfileTrack[];
  offsets: OverlayGroupOffsets;
}

/**
 * Circular speedometer gauge for one GPS device: an analog dial (needle over
 * a 270° arc) or a digital readout, in mph or km/h. Device lock, unit, and
 * display style are live fields (immediate writes); diameter/max/ring/label/
 * offsets are styling (saved with the draft).
 */
export interface OverlaySpeedometerInstance extends OverlayInstanceBase {
  region: OverlayHorizontalRegion;
  /** gpsData doc id of the tracked device (overlay-feed device id). Empty = unlocked. */
  deviceID: string;
  /** Analog needle dial or digital digits. Applies live, not on save. */
  displayMode: OverlaySpeedometerDisplayMode;
  /** Circle diameter in 1920×1080 stage pixels. */
  diameter: number;
  /** Dial full-scale value, interpreted in the selected unit (analog mode). */
  maxSpeed: number;
  /** Circular ring border color. */
  ringColor: OverlayColorRef;
  /** Label pill below the gauge (e.g. "MLEAD"). */
  labelText: string;
  /** Unit for the reading. Applies live, not on save. */
  speedUnit: OverlaySpeedUnit;
  offsets: OverlayGroupOffsets;
}

/**
 * Digital compass for one GPS device: a dark dial whose degree/cardinal rose
 * rotates so the current heading sits under a fixed top index marker. Device
 * lock and the digital readout toggle are live fields (immediate writes);
 * diameter/ring/label/offsets are styling (saved with the draft).
 */
export interface OverlayCompassInstance extends OverlayInstanceBase {
  region: OverlayHorizontalRegion;
  /** gpsData doc id of the tracked device (overlay-feed device id). Empty = unlocked. */
  deviceID: string;
  /** Circle diameter in 1920×1080 stage pixels. */
  diameter: number;
  /** Ring border color; also paints the north tick and fixed index marker. */
  ringColor: OverlayColorRef;
  /** Centered digital heading readout vs plain crosshair. Applies live, not on save. */
  showReadout: boolean;
  /** Label pill below the dial (e.g. "MLEAD"). */
  labelText: string;
  offsets: OverlayGroupOffsets;
}

/**
 * Circular "grade scope" bubble level for one GPS device: the bottom half of
 * the circle is filled with the ring color and its horizon edge tilts to show
 * the course grade under the rider (positive grade ascends to the right).
 * Device lock is a live field (immediate write); diameter/tiltGain/ring/label/
 * offsets are styling (saved with the draft).
 */
export interface OverlayGradeScopeInstance extends OverlayInstanceBase {
  region: OverlayHorizontalRegion;
  /** gpsData doc id of the tracked device (overlay-feed device id). Empty = unlocked. */
  deviceID: string;
  /** Circle diameter in 1920×1080 stage pixels. */
  diameter: number;
  /** Visual tilt exaggeration multiplier — true slope angles look nearly flat. */
  tiltGain: number;
  /** Ring border color; also paints the tilting fill. */
  ringColor: OverlayColorRef;
  /** Label pill below the scope (e.g. "JOHN LEADER"). */
  labelText: string;
  offsets: OverlayGroupOffsets;
}

// --- Custom widget (AI-authored ad-hoc overlay) -------------------------------
//
// One generic element type whose per-instance data embeds a declarative spec
// authored by the AI assistant (layers + control definitions). The spec is
// DATA, never code: the public overlay page renders it through a typed,
// clamped, allowlisted interpreter — no arbitrary CSS strings, no external
// resources beyond the app's own Storage bucket, text via React text nodes.
// `spec` and `values` are LIVE fields (immediate writes, like scoreboard
// scores) so a dirty designer draft's Save can never clobber AI work.

export const CUSTOM_WIDGET_LAYER_LIMIT = 12;
export const CUSTOM_WIDGET_CONTROL_LIMIT = 10;
export const CUSTOM_WIDGET_ENUM_OPTION_LIMIT = 12;
export const CUSTOM_WIDGET_TEXT_MAX_LENGTH = 200;
export const CUSTOM_WIDGET_LABEL_MAX_LENGTH = 40;
export const CUSTOM_WIDGET_SPEC_MAX_JSON_BYTES = 16_384;
export const CUSTOM_WIDGET_CONTROL_KEY_PATTERN = /^[a-z][a-zA-Z0-9_]{0,23}$/;
/** Image layers may only reference the app's own Storage bucket (download URLs). */
export const CUSTOM_WIDGET_IMAGE_URL_PREFIX = "https://firebasestorage.googleapis.com/v0/b/sbot-labs-app.firebasestorage.app/o/";

/** Widget color: shared design token ("text"/"accent") or a literal 6-hex (no '#'). */
export type OverlayCustomWidgetColor =
  | { source: "text" }
  | { source: "accent" }
  | { source: "hex"; hex: string };

/** Position/size in 1920×1080 stage pixels, relative to the widget root. */
export interface OverlayCustomWidgetFrame {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type OverlayCustomWidgetTextTransform = "none" | "uppercase";
export type OverlayCustomWidgetTextAlign = "left" | "center" | "right";
export type OverlayCustomWidgetFontWeight = 400 | 500 | 600 | 700 | 800;
export type OverlayCustomWidgetImageFit = "contain" | "cover";

export interface OverlayCustomWidgetTextStyle {
  fontSize: number;
  fontWeight: OverlayCustomWidgetFontWeight;
  letterSpacing?: number;
  textTransform?: OverlayCustomWidgetTextTransform;
  align?: OverlayCustomWidgetTextAlign;
  color: OverlayCustomWidgetColor;
}

/** Conditional layer visibility bound to one declared control value. */
export interface OverlayCustomWidgetVisibleWhen {
  key: string;
  equals: string | number | boolean;
}

export interface OverlayCustomWidgetRectLayer {
  kind: "rect";
  id: string;
  frame: OverlayCustomWidgetFrame;
  fill: OverlayCustomWidgetColor;
  opacity?: number;
  cornerRadius?: number;
  borderWidth?: number;
  borderColor?: OverlayCustomWidgetColor;
  visibleWhen?: OverlayCustomWidgetVisibleWhen;
}

export interface OverlayCustomWidgetTextLayer {
  kind: "text";
  id: string;
  frame: OverlayCustomWidgetFrame;
  /** Literal text with "{controlKey}" interpolation — rendered as React text nodes only. */
  template: string;
  style: OverlayCustomWidgetTextStyle;
  visibleWhen?: OverlayCustomWidgetVisibleWhen;
}

export interface OverlayCustomWidgetImageLayer {
  kind: "image";
  id: string;
  frame: OverlayCustomWidgetFrame;
  /** Must start with CUSTOM_WIDGET_IMAGE_URL_PREFIX — layers with other URLs are dropped at parse. */
  url: string;
  fit?: OverlayCustomWidgetImageFit;
  opacity?: number;
  cornerRadius?: number;
  visibleWhen?: OverlayCustomWidgetVisibleWhen;
}

export type OverlayCustomWidgetLayer =
  | OverlayCustomWidgetRectLayer
  | OverlayCustomWidgetTextLayer
  | OverlayCustomWidgetImageLayer;

export type OverlayCustomWidgetNumberFormat = "integer" | "decimal1";

export interface OverlayCustomWidgetTextControl {
  kind: "text";
  key: string;
  label: string;
  default: string;
  maxLength?: number;
}

export interface OverlayCustomWidgetNumberControl {
  kind: "number";
  key: string;
  label: string;
  default: number;
  min?: number;
  max?: number;
  step?: number;
  format?: OverlayCustomWidgetNumberFormat;
}

export interface OverlayCustomWidgetEnumControl {
  kind: "enum";
  key: string;
  label: string;
  default: string;
  options: Array<{ value: string; label: string }>;
}

export interface OverlayCustomWidgetBoolControl {
  kind: "bool";
  key: string;
  label: string;
  default: boolean;
}

export type OverlayCustomWidgetControlDef =
  | OverlayCustomWidgetTextControl
  | OverlayCustomWidgetNumberControl
  | OverlayCustomWidgetEnumControl
  | OverlayCustomWidgetBoolControl;

export interface OverlayCustomWidgetSpec {
  version: 1;
  /** Root anchor (top-left) on the 1920×1080 canvas; per-instance offsets.root still applies. */
  base: { x: number; y: number };
  /** Designer hit-target / nominal bounds. */
  size: { width: number; height: number };
  layers: OverlayCustomWidgetLayer[];
  controls: OverlayCustomWidgetControlDef[];
}

export type OverlayCustomWidgetValues = Record<string, string | number | boolean>;

/**
 * AI-authored ad-hoc widget. The spec describes what to draw; `values` holds
 * the operator-entered control values the text layers interpolate. Empty
 * `spec.layers` = no content (content-gated OUT, like a lower third with no
 * person) — so a corrupt or unparseable spec can never break the public page.
 */
export interface OverlayCustomWidgetInstance extends OverlayInstanceBase {
  /** Nominal region — keeps shared collision/region plumbing type-safe. */
  region: OverlayHorizontalRegion;
  /** LIVE field: written by the AI assistant via immediate-write transactions. */
  spec: OverlayCustomWidgetSpec;
  /** LIVE field: operator-edited control values (like scoreboard scores). */
  values: OverlayCustomWidgetValues;
  offsets: OverlayGroupOffsets;
}

export const BUG_ROTATION_SECONDS_MIN = 2;
export const BUG_ROTATION_SECONDS_MAX = 300;
export const DEFAULT_BUG_ROTATION_SECONDS = 8;

/**
 * Broadcast "bug": a corner sponsor/network logo, optionally cycling through a
 * set of logos perpetually. Rotation is derived from the skew-corrected clock
 * (floor(now / interval) % rows) so every browser source swaps at the same
 * instant with no stored anchor — same philosophy as the timer. Rows and the
 * rotation interval are styling (saved with the draft); corner/visible are
 * immediate-write like every region.
 */
export interface OverlayBugInstance extends OverlayInstanceBase {
  mode: "manual" | "data";
  sourceID?: string;
  logoField?: string;
  accentField?: string;
  selectedRowIndices: number[];
  dataRows: OverlayBugRow[];
  region: OverlayBugCorner;
  /** Seconds each logo stays up when rows.length > 1. Clamped 2–300. */
  rotationIntervalSeconds: number;
  /** Rotation set; a single row displays statically. */
  rows: OverlayBugRow[];
  offsets: OverlayGroupOffsets;
}

/** Validate the complete URL, rather than just its scheme prefix. */
export function isValidBugLogoURL(value: string): boolean {
  value = value.trim();
  if (value.slice(8).split(/[/?#]/, 1)[0].includes("@")) return false;
  if (value.length > 8192 || value.includes("\\") || !/^https:\/\/[^/\s][^\s]*$/i.test(value)) return false;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && Boolean(url.hostname) && !url.username && !url.password;
  } catch { return false; }
}

export function resolvedBugRows(instance: OverlayBugInstance): OverlayBugRow[] {
  return (instance.mode === "data" ? instance.dataRows : instance.rows).filter((row) => isValidBugLogoURL(row.url));
}

export function bugReadinessIssue(instance: OverlayBugInstance): string | undefined {
  if (instance.mode !== "data") return resolvedBugRows(instance).length ? undefined : "Add a logo before publishing or taking this Bug in.";
  if (!instance.sourceID || !instance.logoField) return "Choose a data source and Logo URL column.";
  const indices = instance.selectedRowIndices;
  if (!indices.length) return "Select at least one data row.";
  if (indices.length > BUG_ROW_LIMIT) return `Select at most ${BUG_ROW_LIMIT} data rows.`;
  if (indices.some((index) => !Number.isInteger(index) || index < 0) || new Set(indices).size !== indices.length) return "Review the selected data rows and reload the source.";
  const sorted = [...indices].sort((a, b) => a - b);
  if (instance.dataRows.length !== sorted.length || instance.dataRows.some((row, index) => row.id !== `source-${sorted[index]}` || !isValidBugLogoURL(row.url))) return "Reload the source and review missing or invalid selected logo rows before publishing.";
  return undefined;
}

export type OverlayBillboardMode = "rotate" | "manual";

/**
 * Billboard: a 16:9 framed sponsor/event slate centered on the stage. Slates
 * either cycle bug-style (clock-derived, no stored anchor) or hold a manually
 * picked row. Rows, mode, selection, cadence and scale are all draft styling.
 */
export interface OverlayBillboardInstance extends OverlayInstanceBase {
  /** Fixed nominal region — billboards anchor at stage center (offsets nudge from there). */
  region: "center";
  mode: OverlayBillboardMode;
  /** Seconds per slate in rotate mode with >1 contentful rows. Clamped 2–300. */
  rotationIntervalSeconds: number;
  /** Manual-mode held row id; absent or dangling = first contentful row. */
  activeRowId?: string;
  rows: OverlayBillboardRow[];
  /** Optional content-independent uniform scale (1 = 100%); unset = 1. */
  scale?: number;
  /** Per-instance skin override; unset = inherit design.skin. "default" is a real value here (forces the flat look). Draft-tier. */
  skin?: OverlaySkin;
  customSkin?: OverlayCustomSkinReference;
  /** Per-instance material override; unset = inherit design.material. "default" is a real value here (forces flat paint). Draft-tier. */
  material?: OverlayMaterial;
  /** Draft-tier texture opacity overrides, keyed by material. */
  materialTextureOpacities?: OverlayMaterialTextureOpacities;
  offsets: OverlayGroupOffsets;
}

export type OverlayStartListColumns = 2 | 3;
export type OverlayStartListLogoMode = "competition" | "custom" | "none";

/**
 * Start list: a centered card with the competition (or a custom) logo above a
 * 2- or 3-column grid of athletes derived from an external source — filter →
 * sort (bib order by default) → page, persisted as pylon-style row snapshots
 * exactly like the sort & filter pylon. Live tier = source/sort/filter/grid
 * size/page/rows; everything else is draft styling.
 */
export interface OverlayStartListInstance extends OverlayInstanceBase {
  /** Fixed nominal region — start lists anchor at stage center (offsets nudge from there). */
  region: "center";
  /** External source the rows derive from. Applies live, not on save. */
  sourceID?: string;
  /** Source mapping targetFieldID to sort by; unset = ascending bib-number order. Applies live. */
  sortFieldKey?: string;
  /** Applies live, not on save. */
  sortDirection: OverlaySortFilterPylonDirection;
  /** Source mapping targetFieldID to filter on. Applies live. */
  filterFieldKey?: string;
  /** Case-insensitive trimmed exact match; empty/absent = no filtering. Applies live. */
  filterValue?: string;
  /** Grid shape — page size is columns × rowsPerColumn. Applies live. */
  columns: OverlayStartListColumns;
  rowsPerColumn: number;
  /** Zero-based page of filtered/sorted records. Applies live. */
  pageIndex: number;
  /** Derived page snapshot (athlete rows only; ≤ START_LIST_ROW_LIMIT). Applies live. */
  rows: OverlayPylonAthleteRow[];
  /** Optional title rendered under the logo. Draft-tier. */
  label?: string;
  /** Which logo the header shows; "competition" follows the overlay doc's eventLogo pair. Draft-tier. */
  logoMode: OverlayStartListLogoMode;
  /** Custom logo pair (https). Light variant shows in light mode, dark in dark mode, each falling back to the other. */
  customLogoURL?: string;
  customLogoDarkURL?: string;
  /** Logo box height in stage px; unset = DEFAULT_START_LIST_LOGO_HEIGHT. Draft-tier. */
  logoHeight?: number;
  /** Show athlete flags and bibs. Draft-tier; default enabled. */
  useFlag: boolean;
  useBib: boolean;
  /** Render short names when a row's snapshot carries one. Draft-tier. */
  useShortName: boolean;
  /** Shrink each name to fit its cell instead of wrapping. Draft-tier. */
  shrinkTextToFit: boolean;
  darkModeOverride?: boolean;
  skin?: OverlaySkin;
  customSkin?: OverlayCustomSkinReference;
  material?: OverlayMaterial;
  materialTextureOpacities?: OverlayMaterialTextureOpacities;
  /** Optional content-independent uniform scale (1 = 100%); unset = 1. */
  scale?: number;
  /** Optional safety caps (stage px); the tighter cap wins, never upscales. */
  maxWidth?: number;
  maxHeight?: number;
  offsets: OverlayLowerThirdOffsets;
}

/**
 * Image canvas: a sized backdrop (default = the whole stage) that frames one
 * https image with fit/fill/stretch + a focal point over an optional tinted
 * background. Always rendered under every other element (above full-screen
 * maps). Everything is draft styling; take-in autosaves (billboard precedent).
 */
export interface OverlayImageCanvasInstance extends OverlayInstanceBase {
  /** Fixed nominal region — canvases anchor at the stage's top-left (offsets nudge from there). */
  region: "canvas";
  /** https-only image URL; "" = no content (content gate). */
  url: string;
  /** Canvas box in stage px (16–3840 × 16–2160). Default 1920×1080 = the stage. */
  canvasWidth: number;
  canvasHeight: number;
  /** Absent = transparent canvas (image only). */
  background?: OverlayColorRef;
  /** 0–1, applies to the background layer only — the image stays opaque. */
  backgroundOpacity: number;
  /** Optional corner rounding in px (0–200); absent = square. */
  cornerRadius?: number;
  photoFit: OverlayPhotoFit;
  photoPosition: OverlayPhotoPosition;
  /** Optional content-independent uniform scale (1 = 100%, origin top-left); unset = 1. */
  scale?: number;
  offsets: OverlayGroupOffsets;
}

/** An embedded HTTPS page with its own viewport. URL and layout are draft styling. */
export interface OverlayWebSourceInstance extends OverlayInstanceBase {
  region: "canvas";
  url: string;
  canvasWidth: number;
  canvasHeight: number;
  /** Unmount after the OUT transition; disable to keep the page running while hidden. */
  unloadWhenHidden: boolean;
  /** Live command token: changing it reloads each renderer's page. */
  reloadToken: string;
  scale?: number;
  offsets: OverlayGroupOffsets;
}

export type OverlayTickerDivider = "bullet" | "bar" | "image";
/** "rtl" = text travels right → left (enters from the right edge, the usual crawl). */
export type OverlayTickerDirection = "rtl" | "ltr";
export const OVERLAY_TICKER_DIVIDERS: OverlayTickerDivider[] = ["bullet", "bar", "image"];
export const OVERLAY_TICKER_DIRECTIONS: OverlayTickerDirection[] = ["rtl", "ltr"];

export interface OverlayTickerMessage {
  id: string;
  text: string;
}

/**
 * Ticker: a thin top/bottom bar crawling operator-entered messages separated
 * by a divider. `messages` is live-tier (persists immediately, editable on
 * air); everything else is draft styling with take-in autosave. Not skinnable —
 * dark mode + material only.
 */
export interface OverlayTickerInstance extends OverlayInstanceBase {
  region: OverlayTimerRegion;
  /** Live-tier — persists immediately, no Save step. */
  messages: OverlayTickerMessage[];
  divider: OverlayTickerDivider;
  /** https-only divider image; used when divider === "image". */
  dividerImageURL?: string;
  direction: OverlayTickerDirection;
  /** Stage px per second (20–600). */
  speed: number;
  /** Bar width in stage px (320–1920). */
  barWidth: number;
  /** Message text size in stage px (12–96); the bar is 1.5× as tall. */
  fontSize: number;
  darkModeOverride?: boolean;
  material?: OverlayMaterial;
  materialTextureOpacities?: OverlayMaterialTextureOpacities;
  offsets: OverlayGroupOffsets;
}

/** True when at least one message has visible text (the content gate). */
export function tickerHasContent(instance: Pick<OverlayTickerInstance, "messages">): boolean {
  return instance.messages.some((message) => message.text.trim().length > 0);
}

/**
 * Crawl translateX (px) for the skew-corrected wall clock, so every surface
 * (designer Preview/Output, OBS page) shows the same phase and re-mounts never
 * restart the crawl. `cycleWidth` is one pass of every message + divider.
 */
export function tickerOffsetPx(skewedNowMs: number, speed: number, cycleWidth: number, direction: OverlayTickerDirection): number {
  if (!(cycleWidth > 0) || !(speed > 0) || !Number.isFinite(skewedNowMs)) return 0;
  // Reduce the clock first so the float product keeps sub-pixel precision.
  const periodMs = (cycleWidth / speed) * 1000;
  const shift = ((skewedNowMs % periodMs) / 1000) * speed;
  return direction === "ltr" ? shift - cycleWidth : -shift;
}

/** "countdown" counts down to targetAt; "countup" counts elapsed time from it. */
export type OverlayAdvancedTimerMode = "countdown" | "countup";
export const OVERLAY_ADVANCED_TIMER_MODES: OverlayAdvancedTimerMode[] = ["countdown", "countup"];

/**
 * Advanced timer: counts down to (or up from) an absolute date and time. The
 * reading is derived purely from the skew-corrected clock — no start/pause
 * state. `timeZone` only drives how the designer enters and shows the target;
 * the renderer needs `targetAt` alone. Skins, materials and dark mode reuse
 * the timer chip.
 */
export interface OverlayAdvancedTimerInstance extends OverlayInstanceBase {
  region: OverlayTimerRegion;
  label?: string;
  mode: OverlayAdvancedTimerMode;
  /** The instant the countdown ends / the count-up starts; null until set (content gate). */
  targetAt: Date | null;
  /** IANA zone the target was entered in, e.g. "Pacific/Honolulu". */
  timeZone: string;
  /** Color mode override: unset inherits design.darkMode; false forces Light, true forces Dark. Draft-tier — applies on save. */
  darkModeOverride?: boolean;
  /** Per-instance skin override; unset = inherit design.skin. Draft-tier. */
  skin?: OverlaySkin;
  customSkin?: OverlayCustomSkinReference;
  /** Per-instance material override; unset = inherit design.material. Draft-tier. */
  material?: OverlayMaterial;
  materialTextureOpacities?: OverlayMaterialTextureOpacities;
  offsets: OverlayLowerThirdOffsets;
}

/**
 * Milliseconds the advanced timer shows as of `nowMs` (skew-corrected). A
 * countdown holds at zero once the target passes; a count-up holds at zero
 * until the target arrives.
 */
export function advancedTimerDisplayMs(timer: Pick<OverlayAdvancedTimerInstance, "mode" | "targetAt">, nowMs: number): number {
  if (!timer.targetAt || !Number.isFinite(nowMs)) return 0;
  const target = timer.targetAt.getTime();
  if (!Number.isFinite(target)) return 0;
  return Math.max(0, timer.mode === "countup" ? nowMs - target : target - nowMs);
}

/**
 * Compact "2d 04:13:09" reading with leading zero units hidden ("04:13:09"
 * under a day, "13:09" under an hour). Countdowns round seconds up so the
 * last second reads 00:01 and 00:00 lands exactly on the target.
 */
export function formatAdvancedTimerClock(milliseconds: number, mode: OverlayAdvancedTimerMode): string {
  const clamped = Math.max(0, Number.isFinite(milliseconds) ? milliseconds : 0);
  const totalSeconds = mode === "countdown" ? Math.ceil(clamped / 1000) : Math.floor(clamped / 1000);
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value: number) => String(value).padStart(2, "0");
  if (days > 0) return `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  if (hours > 0) return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  return `${pad(minutes)}:${pad(seconds)}`;
}

/** True once a target is set (the content gate). */
export function advancedTimerHasContent(instance: Pick<OverlayAdvancedTimerInstance, "targetAt">): boolean {
  return instance.targetAt instanceof Date && Number.isFinite(instance.targetAt.getTime());
}

export interface OverlayElements {
  gpsGroups: OverlayGpsGroupInstance[];
  timers: OverlayTimerInstance[];
  pylons: OverlayPylonInstance[];
  gpsPylons: OverlayGpsPylonInstance[];
  sortFilterPylons: OverlaySortFilterPylonInstance[];
  lowerThirds: OverlayLowerThirdInstance[];
  customLowerThirds: OverlayCustomLowerThirdInstance[];
  playerCards: OverlayPlayerCardInstance[];
  scoreboards: OverlayScoreboardInstance[];
  miniMaps: OverlayMiniMapInstance[];
  fullMaps: OverlayFullMapInstance[];
  courseProfiles: OverlayCourseProfileInstance[];
  speedometers: OverlaySpeedometerInstance[];
  gradeScopes: OverlayGradeScopeInstance[];
  customWidgets: OverlayCustomWidgetInstance[];
  bugs: OverlayBugInstance[];
  compasses: OverlayCompassInstance[];
  billboards: OverlayBillboardInstance[];
  podiums: OverlayPodiumInstance[];
  imageCanvases: OverlayImageCanvasInstance[];
  webSources: OverlayWebSourceInstance[];
  startLists: OverlayStartListInstance[];
  tickers: OverlayTickerInstance[];
  advancedTimers: OverlayAdvancedTimerInstance[];
}

export type OverlayElementInstance =
  | OverlayGpsGroupInstance
  | OverlayTimerInstance
  | OverlayPylonInstance
  | OverlayGpsPylonInstance
  | OverlaySortFilterPylonInstance
  | OverlayLowerThirdInstance
  | OverlayCustomLowerThirdInstance
  | OverlayPlayerCardInstance
  | OverlayScoreboardInstance
  | OverlayMiniMapInstance
  | OverlayFullMapInstance
  | OverlayCourseProfileInstance
  | OverlaySpeedometerInstance
  | OverlayGradeScopeInstance
  | OverlayCustomWidgetInstance
  | OverlayBugInstance
  | OverlayCompassInstance
  | OverlayBillboardInstance
  | OverlayPodiumInstance
  | OverlayImageCanvasInstance
  | OverlayWebSourceInstance
  | OverlayStartListInstance
  | OverlayTickerInstance
  | OverlayAdvancedTimerInstance;

/**
 * Named container of In/Out controls the operator toggles as a unit. Pure AND
 * gate: effective on-air visibility = instance.visible && container active —
 * toggling a container never mutates member `visible` flags, so individual
 * control state survives container cycles. One container per control (parse
 * dedupes across containers, first wins). Live state: every mutation persists
 * immediately, and a designer Save re-copies the stored value (like gpsFocus).
 * Gate mirrored in functions/src/overlayRuntime.ts + web/src/services/overlayRuntime.ts.
 */
export interface OverlayControlGroup {
  id: string;
  name: string;
  /** Container In/Out. Inactive hides every member regardless of its own flag. */
  active: boolean;
  /** Dynamic control keys "{type}:{instanceId}". */
  members: string[];
  /** Rail accordion state — shared authoring layout, not per-user and not on-air state. */
  collapsed: boolean;
}

export const MAX_CONTROL_GROUPS = 8;
const CONTROL_GROUP_MEMBER_PATTERN = /^(gps|timer|pylon|gpsPylon|sortFilterPylon|lowerThird|customLowerThird|playerCard|scoreboard|miniMap|fullMap|courseProfile|speedometer|gradeScope|customWidget|bug|compass|billboard|podium|imageCanvas|webSource|startList|ticker|advancedTimer):[A-Za-z0-9_-]{1,40}$/;

export function parseControlGroups(raw: unknown): OverlayControlGroup[] {
  if (!Array.isArray(raw)) return [];
  const seenIds = new Set<string>();
  const claimedMembers = new Set<string>();
  const groups: OverlayControlGroup[] = [];
  for (const entry of raw) {
    if (groups.length >= MAX_CONTROL_GROUPS) break;
    if (!entry || typeof entry !== "object") continue;
    const record = entry as Record<string, unknown>;
    const id = stringValue(record.id);
    if (!id || !OVERLAY_INSTANCE_ID_PATTERN.test(id) || seenIds.has(id)) continue;
    seenIds.add(id);
    const members: string[] = [];
    if (Array.isArray(record.members)) {
      for (const member of record.members) {
        if (typeof member !== "string" || !CONTROL_GROUP_MEMBER_PATTERN.test(member)) continue;
        if (claimedMembers.has(member)) continue;
        claimedMembers.add(member);
        members.push(member);
      }
    }
    groups.push({
      id,
      name: (stringValue(record.name) ?? "Container").slice(0, 40),
      active: record.active !== false,
      members,
      collapsed: record.collapsed === true,
    });
  }
  return groups;
}

export function controlGroupsFirestoreData(groups: OverlayControlGroup[]): Array<Record<string, unknown>> {
  return groups.slice(0, MAX_CONTROL_GROUPS).map((group) => ({
    id: group.id,
    name: group.name,
    // Stored explicitly (not compacted): active is the live operator flag.
    active: group.active,
    members: group.members,
    collapsed: group.collapsed,
  }));
}

/** Rail type-group entry: order and accordion state for one built-in section. */
export interface OverlayRailTypeGroup {
  type: OverlayElementType;
  collapsed: boolean;
}

/**
 * Rail type-group order + accordion state. Forward-compatible: unknown types
 * are dropped and known types missing from the stored list are appended in
 * OVERLAY_DEFAULT_RAIL_TYPE_ORDER — so a newly shipped element type appears at
 * the bottom of an existing overlay's rail instead of disappearing (new
 * overlays get it in default position). Result always covers every type.
 */
export function parseRailTypeGroups(raw: unknown): OverlayRailTypeGroup[] {
  const seen = new Set<OverlayElementType>();
  const groups: OverlayRailTypeGroup[] = [];
  if (Array.isArray(raw)) {
    for (const entry of raw) {
      if (!entry || typeof entry !== "object") continue;
      const record = entry as Record<string, unknown>;
      const type = record.type;
      if (typeof type !== "string" || !(OVERLAY_ELEMENT_TYPES as string[]).includes(type)) continue;
      if (seen.has(type as OverlayElementType)) continue;
      seen.add(type as OverlayElementType);
      groups.push({ type: type as OverlayElementType, collapsed: record.collapsed === true });
    }
  }
  for (const type of OVERLAY_DEFAULT_RAIL_TYPE_ORDER) {
    if (seen.has(type)) continue;
    groups.push({ type, collapsed: false });
  }
  return groups;
}

export function railTypeGroupsFirestoreData(groups: OverlayRailTypeGroup[]): Array<Record<string, unknown>> {
  return groups.map((group) => ({ type: group.type, collapsed: group.collapsed }));
}

export const OVERLAY_PROGRAM_REFRESH_VALUE_MAX_LENGTH = 160;

export interface OverlayProgramRefresh {
  buildID: string;
  requestID: string;
}

export function parseOverlayProgramRefresh(value: unknown): OverlayProgramRefresh | undefined {
  if (!value || typeof value !== "object") return undefined;
  const record = value as Record<string, unknown>;
  const buildID = stringValue(record.buildID)?.trim();
  const requestID = stringValue(record.requestID)?.trim();
  if (!buildID || !requestID) return undefined;
  if (buildID.length > OVERLAY_PROGRAM_REFRESH_VALUE_MAX_LENGTH || requestID.length > OVERLAY_PROGRAM_REFRESH_VALUE_MAX_LENGTH) return undefined;
  return { buildID, requestID };
}

export interface OverlayConfig {
  skinSnapshots?: OverlaySkinSnapshots;
  skinContractVersion?: 1;
  id: string;
  leagueID: string;
  competitionInstanceID: string;
  name: string;
  courseKML: OverlayCourseKMLRef | null;
  /**
   * Competition logo pair denormalized from the competition's eventLogo /
   * eventLogoDark fields (the public overlay page cannot read competition
   * docs). Live tier: the designer syncs it on open, a Save re-copies it.
   */
  eventLogo: OverlayEventLogo | null;
  /** Focus KML window for GPS matching — "full" or a course quarter. */
  gpsFocus: GPSFocusOption;
  /** Bumped by the "Re-lock GPS" control; overlay pages discard match history when it changes. */
  gpsRelockNonce: number;
  design: {
    textColor: OverlayColorRef;
    accentColor: OverlayColorRef;
    font: OverlayFont;
    autoCapitalizePersonText: boolean;
    /** Shared geometric silhouette for skinnable elements; "default" = flat rectangles (omitted on write). */
    skin: OverlaySkin;
    customSkin?: OverlayCustomSkinReference;
    /** Shared material finish for skinnable elements; "default" = flat paint (omitted on write). */
    material: OverlayMaterial;
    materialTextureOpacities?: OverlayMaterialTextureOpacities;
    /** Global light/dark palette; instances inherit unless explicitly overridden. Draft-tier. */
    darkMode: boolean;
  };
  elements: OverlayElements;
  /** Live In/Out containers (AND gate over member visibility). */
  controlGroups: OverlayControlGroup[];
  /** Designer rail type-group order + accordion state (live-write, shared). */
  railTypeGroups: OverlayRailTypeGroup[];
  /** Deployment command observed by connected Program URL browser sources. */
  programRefresh?: OverlayProgramRefresh;
  createdByUserID?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

// --- Timer math --------------------------------------------------------------

/** Elapsed milliseconds a timer has accumulated as of `nowMs` (skew-corrected). */
export function timerElapsedMs(timer: OverlayTimerInstance, nowMs: number): number {
  const runningPortion = timer.running && timer.startedAt ? Math.max(0, nowMs - timer.startedAt.getTime()) : 0;
  return Math.max(0, timer.offsetMs + runningPortion);
}

/** The milliseconds value a timer chip displays as of `nowMs`. */
export function timerDisplayMs(timer: OverlayTimerInstance, nowMs: number): number {
  const elapsed = timerElapsedMs(timer, nowMs);
  return timer.direction === "down" ? Math.max(0, timer.durationMs - elapsed) : elapsed;
}

/** hh:mm:ss, hours unpadded past 99 rather than wrapping. */
export function formatTimerClock(milliseconds: number): string {
  const totalSeconds = Math.floor(Math.max(0, milliseconds) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/** Parses "hh:mm:ss", "mm:ss", or "ss" into milliseconds; null when invalid. */
export function parseTimerClockInput(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parts = trimmed.split(":").map((part) => part.trim());
  if (parts.length > 3 || parts.some((part) => part === "" || !/^\d+$/.test(part))) return null;
  const numbers = parts.map(Number);
  while (numbers.length < 3) numbers.unshift(0);
  const [hours, minutes, seconds] = numbers;
  if (minutes > 59 || seconds > 59) return null;
  return ((hours * 60 + minutes) * 60 + seconds) * 1000;
}

// --- Shared parse/serialize helpers -------------------------------------------

function parseColorRef(value: unknown): OverlayColorRef | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const source = record.source;
  const hex = normalizedHex(stringValue(record.hex));
  const secondaryHex = normalizedHex(stringValue(record.secondaryHex)) ?? hex;
  const gradientDirection =
    record.gradientDirection === "vertical" ? "vertical" : record.gradientDirection === "horizontal" ? "horizontal" : undefined;
  const gradientAngle = normalizeOverlayGradientAngle(record.gradientAngle, overlayGradientAngleForDirection(gradientDirection));
  if (
    !hex ||
    (source !== "league-primary" &&
      source !== "league-secondary" &&
      source !== "league-primary-secondary-gradient" &&
      source !== "org-primary" &&
      source !== "org-secondary" &&
      source !== "custom" &&
      source !== "custom-gradient")
  ) {
    return null;
  }
  if (source === "league-primary-secondary-gradient" || source === "custom-gradient") {
    return { source, organizationID: stringValue(record.organizationID), hex, secondaryHex, gradientAngle, gradientDirection };
  }
  return { source, organizationID: stringValue(record.organizationID), hex };
}

export function normalizeOverlayExtraValue(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed || trimmed.toLocaleLowerCase() === "none") return undefined;
  return trimmed;
}

function zeroOffset(): OverlayXYOffset {
  return { x: 0, y: 0 };
}

export function defaultGroupOffsets(): OverlayGroupOffsets {
  return { root: zeroOffset() };
}

export function defaultLowerThirdOffsets(): OverlayLowerThirdOffsets {
  return { root: zeroOffset() };
}

function cloneXYOffset(offset: OverlayXYOffset): OverlayXYOffset {
  return { x: offset.x, y: offset.y };
}

function cloneGroupOffsets(offsets: OverlayGroupOffsets): OverlayGroupOffsets {
  return {
    root: cloneXYOffset(offsets.root),
    lead: offsets.lead ? cloneXYOffset(offsets.lead) : undefined,
    chase: offsets.chase ? cloneXYOffset(offsets.chase) : undefined,
  };
}

function cloneLowerThirdOffsets(offsets: OverlayLowerThirdOffsets): OverlayLowerThirdOffsets {
  return {
    root: cloneXYOffset(offsets.root),
    nameBar: offsets.nameBar ? cloneXYOffset(offsets.nameBar) : undefined,
    subBar: offsets.subBar ? cloneXYOffset(offsets.subBar) : undefined,
    flag: offsets.flag ? cloneXYOffset(offsets.flag) : undefined,
    bib: offsets.bib ? cloneXYOffset(offsets.bib) : undefined,
    name: offsets.name ? cloneXYOffset(offsets.name) : undefined,
  };
}

function finiteNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

export function normalizeOverlayGradientAngle(value: unknown, fallback = DEFAULT_OVERLAY_GRADIENT_ANGLE): number {
  const number = finiteNumber(value) ?? fallback;
  return ((Math.round(number) % 360) + 360) % 360;
}

export function overlayGradientAngleForDirection(direction: OverlayGradientDirection | undefined): number {
  return direction === "vertical" ? 180 : DEFAULT_OVERLAY_GRADIENT_ANGLE;
}

export function overlayGradientAngle(value: Pick<OverlayColorRef, "gradientAngle" | "gradientDirection">): number {
  return normalizeOverlayGradientAngle(value.gradientAngle, overlayGradientAngleForDirection(value.gradientDirection));
}

function parseXYOffset(value: unknown): OverlayXYOffset {
  if (!value || typeof value !== "object") return zeroOffset();
  const record = value as Record<string, unknown>;
  return {
    x: finiteNumber(record.x) ?? 0,
    y: finiteNumber(record.y) ?? 0,
  };
}

function isZeroOffset(offset: OverlayXYOffset | undefined): boolean {
  return !offset || (offset.x === 0 && offset.y === 0);
}

function xyOffsetFirestoreData(offset: OverlayXYOffset | undefined): Record<string, unknown> | undefined {
  if (!offset || isZeroOffset(offset)) return undefined;
  return { x: offset.x, y: offset.y };
}

function parseGroupOffsets(value: unknown): OverlayGroupOffsets {
  if (!value || typeof value !== "object") return defaultGroupOffsets();
  const record = value as Record<string, unknown>;
  return {
    root: parseXYOffset(record.root),
    lead: record.lead ? parseXYOffset(record.lead) : undefined,
    chase: record.chase ? parseXYOffset(record.chase) : undefined,
  };
}

function parseLowerThirdOffsets(value: unknown): OverlayLowerThirdOffsets {
  if (!value || typeof value !== "object") return defaultLowerThirdOffsets();
  const record = value as Record<string, unknown>;
  return {
    root: parseXYOffset(record.root),
    nameBar: record.nameBar ? parseXYOffset(record.nameBar) : undefined,
    subBar: record.subBar ? parseXYOffset(record.subBar) : undefined,
    flag: record.flag ? parseXYOffset(record.flag) : undefined,
    bib: record.bib ? parseXYOffset(record.bib) : undefined,
    name: record.name ? parseXYOffset(record.name) : undefined,
  };
}

function groupOffsetsFirestoreData(offsets: OverlayGroupOffsets): Record<string, unknown> | undefined {
  const data = compactData({
    root: xyOffsetFirestoreData(offsets.root),
    lead: xyOffsetFirestoreData(offsets.lead),
    chase: xyOffsetFirestoreData(offsets.chase),
  });
  return Object.keys(data).length > 0 ? data : undefined;
}

function lowerThirdOffsetsFirestoreData(offsets: OverlayLowerThirdOffsets): Record<string, unknown> | undefined {
  const data = compactData({
    root: xyOffsetFirestoreData(offsets.root),
    nameBar: xyOffsetFirestoreData(offsets.nameBar),
    subBar: xyOffsetFirestoreData(offsets.subBar),
    flag: xyOffsetFirestoreData(offsets.flag),
    bib: xyOffsetFirestoreData(offsets.bib),
    name: xyOffsetFirestoreData(offsets.name),
  });
  return Object.keys(data).length > 0 ? data : undefined;
}

function colorRefFirestoreData(ref: OverlayColorRef): Record<string, unknown> {
  return compactData({
    source: ref.source,
    organizationID: ref.source.startsWith("org-") ? ref.organizationID : undefined,
    hex: ref.hex,
    secondaryHex: ref.source === "league-primary-secondary-gradient" || ref.source === "custom-gradient" ? ref.secondaryHex : undefined,
    gradientAngle:
      ref.source === "league-primary-secondary-gradient" || ref.source === "custom-gradient" ? overlayGradientAngle(ref) : undefined,
  });
}

function parseLowerThirdPerson(value: unknown): OverlayLowerThirdPerson | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const fullName = stringValue(record["full-name"]);
  if (!fullName) return null;
  return {
    "full-name": fullName,
    "short-name": stringValue(record["short-name"]),
    "flag-url": stringValue(record["flag-url"]),
    "headshot-url": stringValue(record["headshot-url"]),
    bib: stringValue(record.bib),
    "extra-label": stringValue(record["extra-label"]),
    "extra-value": normalizeOverlayExtraValue(stringValue(record["extra-value"])),
  };
}

function parseCustomLowerThirdRow(value: unknown): OverlayCustomLowerThirdRow | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const id = stringValue(record.id);
  if (!id) return null;
  return {
    id,
    name: stringValue(record.name) ?? "",
    accentText: stringValue(record.accentText) ?? "",
  };
}

function parseCustomLowerThirdRows(value: unknown): OverlayCustomLowerThirdRow[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(parseCustomLowerThirdRow)
    .filter((row): row is OverlayCustomLowerThirdRow => row !== null)
    .slice(0, CUSTOM_LOWER_THIRD_ROW_LIMIT);
}

const BUG_ROW_URL_PATTERN = /^https:\/\//i;
const BUG_ACCENT_HEX_PATTERN = /^[0-9a-f]{6}$/i;

function parseLowerThirdLogo(value: unknown): OverlayLowerThirdLogo | undefined {
  if (!value || typeof value !== "object") return undefined;
  const record = value as Record<string, unknown>;
  const url = stringValue(record.url) ?? "";
  if (!BUG_ROW_URL_PATTERN.test(url)) return undefined;
  const background = parseColorRef(record.background) ?? undefined;
  const radius = finiteNumber(record.cornerRadius);
  const cornerRadius = radius === undefined ? undefined : Math.min(LOWER_THIRD_LOGO_RADIUS_MAX, Math.max(0, Math.round(radius)));
  const rawPadding = finiteNumber(record.padding);
  const padding = rawPadding === undefined ? undefined : Math.min(LOWER_THIRD_LOGO_PADDING_MAX, Math.max(0, Math.round(rawPadding)));
  return { url, background, cornerRadius: cornerRadius || undefined, padding };
}

function lowerThirdLogoFirestoreData(logo: OverlayLowerThirdLogo | undefined): Record<string, unknown> | undefined {
  if (!logo) return undefined;
  return compactData({
    url: logo.url,
    background: logo.background ? colorRefFirestoreData(logo.background) : undefined,
    cornerRadius: logo.cornerRadius || undefined,
    // 0 is a meaningful override of the CSS default padding — only absent folds away.
    padding: logo.padding,
  });
}

function parseBugRow(value: unknown): OverlayBugRow | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const id = stringValue(record.id);
  if (!id) return null;
  const url = stringValue(record.url) ?? "";
  const accentHex = stringValue(record.accentHex);
  return {
    id,
    url: isValidBugLogoURL(url) ? url : "",
    accentHex: accentHex && BUG_ACCENT_HEX_PATTERN.test(accentHex) ? accentHex.toLowerCase() : undefined,
  };
}

function parseBugRows(value: unknown): OverlayBugRow[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(parseBugRow)
    .filter((row): row is OverlayBugRow => row !== null)
    .slice(0, BUG_ROW_LIMIT);
}

function parseBillboardRow(value: unknown): OverlayBillboardRow | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const id = stringValue(record.id);
  if (!id) return null;
  const url = stringValue(record.url) ?? "";
  const message = stringValue(record.message) ?? "";
  const backgroundHex = stringValue(record.backgroundHex);
  const accentHex = stringValue(record.accentHex);
  return {
    id,
    url: BUG_ROW_URL_PATTERN.test(url) ? url : "",
    message: message.slice(0, BILLBOARD_MESSAGE_LIMIT),
    backgroundHex: backgroundHex && BUG_ACCENT_HEX_PATTERN.test(backgroundHex) ? backgroundHex.toLowerCase() : undefined,
    backgroundOpacity: clampNumber(finiteNumber(record.backgroundOpacity) ?? 1, 0, 1),
    accentHex: accentHex && BUG_ACCENT_HEX_PATTERN.test(accentHex) ? accentHex.toLowerCase() : undefined,
  };
}

function parseBillboardRows(value: unknown): OverlayBillboardRow[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(parseBillboardRow)
    .filter((row): row is OverlayBillboardRow => row !== null)
    .slice(0, BILLBOARD_ROW_LIMIT);
}

function parsePylonRow(value: unknown): OverlayPylonRow | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const id = stringValue(record.id);
  if (!id) return null;
  if (record.kind === "divider") {
    // stringValue does not trim, so trim explicitly before the empty check.
    const text = stringValue(record.text)?.trim().slice(0, PYLON_DIVIDER_TEXT_LIMIT);
    return text ? { id, kind: "divider", text } : null;
  }
  const person = parseLowerThirdPerson(record.person);
  if (!person) return null;
  return {
    id,
    recordKey: stringValue(record.recordKey),
    sourceID: stringValue(record.sourceID),
    sortValue: stringValue(record.sortValue)?.trim().slice(0, PYLON_SORT_VALUE_LIMIT) || undefined,
    person,
  };
}

function parsePylonRows(value: unknown, athleteLimit = PYLON_ROW_LIMIT): OverlayPylonRow[] {
  if (!Array.isArray(value)) return [];
  return capPylonRows(value.map(parsePylonRow).filter((row): row is OverlayPylonRow => row !== null), athleteLimit);
}

export function pylonRowFirestoreData(row: OverlayPylonRow): Record<string, unknown> {
  if (isPylonDividerRow(row)) {
    return compactData({ id: row.id, kind: row.kind, text: row.text });
  }
  return compactData({
    id: row.id,
    recordKey: row.recordKey,
    sourceID: row.sourceID,
    sortValue: row.sortValue,
    person: compactData({ ...row.person }),
  });
}

function parsePodiumSlot(value: unknown): OverlayPodiumSlot | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const place = finiteNumber(record.place);
  if (place === undefined || !Number.isInteger(place) || place < 1 || place > PODIUM_SLOT_LIMIT) return null;
  const person = parseLowerThirdPerson(record.person);
  if (!person) return null;
  return {
    place: place as OverlayPodiumPlace,
    recordKey: stringValue(record.recordKey),
    sourceID: stringValue(record.sourceID),
    person,
  };
}

function parsePodiumSlots(value: unknown): OverlayPodiumSlot[] {
  if (!Array.isArray(value)) return [];
  return normalizePodiumSlots(value.map(parsePodiumSlot).filter((slot): slot is OverlayPodiumSlot => slot !== null));
}

export function podiumSlotFirestoreData(slot: OverlayPodiumSlot): Record<string, unknown> {
  return compactData({
    place: slot.place,
    recordKey: slot.recordKey,
    sourceID: slot.sourceID,
    person: compactData({ ...slot.person }),
  });
}

// --- Instance parsing ----------------------------------------------------------

interface InstanceIdentity {
  id: string;
  name: string;
}

function parseHorizontalRegion(value: unknown, fallback: OverlayHorizontalRegion = "left"): OverlayHorizontalRegion {
  return value === "left" || value === "right" ? value : fallback;
}

function parseTimerRegion(value: unknown, fallback: OverlayTimerRegion = "top"): OverlayTimerRegion {
  return value === "top" || value === "bottom" ? value : fallback;
}

function parseBugCorner(value: unknown, fallback: OverlayBugCorner = "top-left"): OverlayBugCorner {
  return value === "top-left" || value === "top-right" || value === "bottom-left" || value === "bottom-right"
    ? value
    : fallback;
}

function parseInstanceBase(record: Record<string, unknown>, identity: InstanceIdentity): OverlayInstanceBase {
  return {
    id: identity.id,
    name: stringValue(record.name) ?? identity.name,
    visible: record.visible === true,
    transitionDurationMs: normalizeOverlayTransitionDurationMs(record.transitionDurationMs),
    rootScale: normalizeOverlayRootScale(record.rootScale),
    avoidCollisions: record.avoidCollisions !== false,
  };
}

function parseGpsGroupInstance(
  record: Record<string, unknown>,
  identity: InstanceIdentity,
  defaults: { region: OverlayHorizontalRegion; category: string; leadLabel: string; chaseLabel: string },
): OverlayGpsGroupInstance {
  return {
    ...parseInstanceBase(record, identity),
    region: parseHorizontalRegion(record.region, defaults.region),
    category: stringValue(record.category) ?? defaults.category,
    darkModeOverride: parseOverlayDarkModeOverride(record.darkModeOverride),
    showChase: record.showChase === true,
    showChaseValue: record.showChaseValue === true ? true : undefined,
    showDistanceRemaining: record.showDistanceRemaining === true,
    showSpeed: record.showSpeed === true,
    // Mutually exclusive slot: speed > grade > pace if a doc ever carries several.
    showGrade: record.showGrade === true && record.showSpeed !== true,
    showPace: record.showPace === true && record.showSpeed !== true && record.showGrade !== true,
    speedUnit: record.speedUnit === "kmh" ? "kmh" : "mph",
    leadLabel: stringValue(record.leadLabel) ?? defaults.leadLabel,
    chaseLabel: stringValue(record.chaseLabel) ?? defaults.chaseLabel,
    leadScale: parseElementScale(record.leadScale),
    chaseScale: parseElementScale(record.chaseScale),
    leadWidth: parseMaxSizePx(record.leadWidth, ELEMENT_MAX_WIDTH_MIN, ELEMENT_MAX_WIDTH_MAX),
    chaseWidth: parseMaxSizePx(record.chaseWidth, ELEMENT_MAX_WIDTH_MIN, ELEMENT_MAX_WIDTH_MAX),
    lockChaseWidth: record.lockChaseWidth === true ? true : undefined,
    skin: parseOverlaySkinOverride(record.skin),
    customSkin: parseCustomSkinReference(record.customSkin),
    material: parseOverlayMaterialOverride(record.material),
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(record.materialTextureOpacities),
    offsets: parseGroupOffsets(record.offsets),
  };
}

function parseTimerInstance(
  record: Record<string, unknown>,
  identity: InstanceIdentity,
  defaults: { region: OverlayTimerRegion; label?: string },
): OverlayTimerInstance {
  return {
    ...parseInstanceBase(record, identity),
    region: parseTimerRegion(record.region ?? record.position, defaults.region),
    label: stringValue(record.label) ?? defaults.label,
    direction: record.direction === "down" ? "down" : "up",
    running: record.running === true,
    startedAt: dateValue(record.startedAt) ?? null,
    offsetMs: Math.max(0, finiteNumber(record.offsetMs) ?? 0),
    durationMs: Math.max(0, finiteNumber(record.durationMs) ?? 0),
    darkModeOverride: parseOverlayDarkModeOverride(record.darkModeOverride),
    skin: parseOverlaySkinOverride(record.skin),
    customSkin: parseCustomSkinReference(record.customSkin),
    material: parseOverlayMaterialOverride(record.material),
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(record.materialTextureOpacities),
    offsets: parseLowerThirdOffsets(record.offsets),
  };
}

function parsePylonInstance(
  record: Record<string, unknown>,
  identity: InstanceIdentity,
  defaults: { region: OverlayHorizontalRegion },
  athleteLimit = PYLON_ROW_LIMIT,
): OverlayPylonInstance {
  return {
    ...parseInstanceBase(record, identity),
    region: parseHorizontalRegion(record.region ?? record.side, defaults.region),
    label: stringValue(record.label),
    showRank: record.showRank !== false,
    useFlag: record.useFlag !== false,
    useBib: record.useBib !== false,
    useShortName: record.useShortName === true,
    anchorBottom: record.anchorBottom === true,
    widthMode: record.widthMode === "fit" ? "fit" : "fixed",
    shrinkTextToFit: record.shrinkTextToFit === true,
    darkModeOverride: parseOverlayDarkModeOverride(record.darkModeOverride),
    skin: parseOverlaySkinOverride(record.skin),
    customSkin: parseCustomSkinReference(record.customSkin),
    material: parseOverlayMaterialOverride(record.material),
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(record.materialTextureOpacities),
    maxWidth: parseMaxSizePx(record.maxWidth, ELEMENT_MAX_WIDTH_MIN, ELEMENT_MAX_WIDTH_MAX),
    maxHeight: parseMaxSizePx(record.maxHeight, ELEMENT_MAX_HEIGHT_MIN, ELEMENT_MAX_HEIGHT_MAX),
    scale: parseElementScale(record.scale),
    offsets: parseLowerThirdOffsets(record.offsets),
    sourceID: stringValue(record.sourceID),
    rows: parsePylonRows(record.rows, athleteLimit),
  };
}

function parseSortFilterPylonRowCount(raw: unknown): number {
  const value = typeof raw === "number" && Number.isFinite(raw) ? Math.round(raw) : SORT_FILTER_PYLON_DEFAULT_ROW_COUNT;
  return Math.min(SORT_FILTER_PYLON_ROW_LIMIT, Math.max(1, value));
}

export function normalizeSortFilterPylonPageIndex(raw: unknown): number {
  return typeof raw === "number" && Number.isSafeInteger(raw) && raw >= 0 ? raw : 0;
}

function parseSortFilterPylonInstance(
  record: Record<string, unknown>,
  identity: InstanceIdentity,
  defaults: { region: OverlayHorizontalRegion },
): OverlaySortFilterPylonInstance {
  return {
    ...parsePylonInstance(record, identity, defaults, SORT_FILTER_PYLON_ROW_LIMIT),
    sortFieldKey: stringValue(record.sortFieldKey),
    sortDirection: record.sortDirection === "desc" ? "desc" : "asc",
    filterFieldKey: stringValue(record.filterFieldKey),
    filterValue: stringValue(record.filterValue),
    rowCount: parseSortFilterPylonRowCount(record.rowCount),
    pageIndex: normalizeSortFilterPylonPageIndex(record.pageIndex),
    showSortValue: record.showSortValue === true,
  };
}

function parseGpsPylonHeaderReadout(raw: unknown): OverlayGpsPylonHeaderReadout | undefined {
  return OVERLAY_GPS_PYLON_HEADER_READOUTS.includes(raw as OverlayGpsPylonHeaderReadout)
    ? (raw as OverlayGpsPylonHeaderReadout)
    : undefined;
}

function parseGpsPylonInstance(
  record: Record<string, unknown>,
  identity: InstanceIdentity,
  defaults: { region: OverlayHorizontalRegion; category: string },
): OverlayGpsPylonInstance {
  return {
    ...parseInstanceBase(record, identity),
    region: parseHorizontalRegion(record.region, defaults.region),
    label: stringValue(record.label),
    category: stringValue(record.category) ?? defaults.category,
    headerReadout: parseGpsPylonHeaderReadout(record.headerReadout),
    speedUnit: record.speedUnit === "kmh" ? "kmh" : "mph",
    showRank: record.showRank !== false,
    useFlag: record.useFlag !== false,
    useBib: record.useBib !== false,
    useShortName: record.useShortName === true,
    anchorBottom: record.anchorBottom === true,
    widthMode: record.widthMode === "fit" ? "fit" : "fixed",
    shrinkTextToFit: record.shrinkTextToFit === true,
    darkModeOverride: parseOverlayDarkModeOverride(record.darkModeOverride),
    skin: parseOverlaySkinOverride(record.skin),
    customSkin: parseCustomSkinReference(record.customSkin),
    material: parseOverlayMaterialOverride(record.material),
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(record.materialTextureOpacities),
    maxWidth: parseMaxSizePx(record.maxWidth, ELEMENT_MAX_WIDTH_MIN, ELEMENT_MAX_WIDTH_MAX),
    maxHeight: parseMaxSizePx(record.maxHeight, ELEMENT_MAX_HEIGHT_MIN, ELEMENT_MAX_HEIGHT_MAX),
    scale: parseElementScale(record.scale),
    offsets: parseLowerThirdOffsets(record.offsets),
    sourceID: stringValue(record.sourceID),
    rows: parsePylonRows(record.rows),
  };
}

function parsePodiumInstance(record: Record<string, unknown>, identity: InstanceIdentity): OverlayPodiumInstance {
  return {
    ...parseInstanceBase(record, identity),
    region: "bottom",
    label: stringValue(record.label),
    depth: record.depth === 5 ? 5 : 3,
    showPlace: record.showPlace === true,
    useFlag: record.useFlag !== false,
    useBib: record.useBib !== false,
    useShortName: record.useShortName === true,
    shrinkTextToFit: record.shrinkTextToFit === true,
    darkModeOverride: parseOverlayDarkModeOverride(record.darkModeOverride),
    skin: parseOverlaySkinOverride(record.skin),
    customSkin: parseCustomSkinReference(record.customSkin),
    material: parseOverlayMaterialOverride(record.material),
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(record.materialTextureOpacities),
    barMaxWidth: parseMaxSizePx(record.barMaxWidth, PODIUM_BAR_MAX_WIDTH_MIN, PODIUM_BAR_MAX_WIDTH_MAX),
    maxWidth: parseMaxSizePx(record.maxWidth, ELEMENT_MAX_WIDTH_MIN, ELEMENT_MAX_WIDTH_MAX),
    maxHeight: parseMaxSizePx(record.maxHeight, ELEMENT_MAX_HEIGHT_MIN, ELEMENT_MAX_HEIGHT_MAX),
    scale: parseElementScale(record.scale),
    offsets: parseLowerThirdOffsets(record.offsets),
    sourceID: stringValue(record.sourceID),
    slots: parsePodiumSlots(record.slots),
  };
}

function parseLowerThirdInstance(
  record: Record<string, unknown>,
  identity: InstanceIdentity,
  defaults: { region: OverlayHorizontalRegion },
): OverlayLowerThirdInstance {
  const rows = parseCustomLowerThirdRows(record.rows);
  const selectedRowID = stringValue(record.selectedRowID);
  return {
    ...parseInstanceBase(record, identity),
    mode: record.mode === "manual" ? "manual" : "data",
    rows,
    selectedRowID: rows.some((row) => row.id === selectedRowID) ? selectedRowID : rows[0]?.id,
    fitLayoutReferenceLeft: normalizeFitLayoutReferenceLeft(record.fitLayoutReferenceLeft),
    region: parseHorizontalRegion(record.region, defaults.region),
    useFlag: record.useFlag !== false,
    useBib: record.useBib !== false,
    showExtraLabel: record.showExtraLabel === true,
    useShortName: record.useShortName === true,
    widthMode: record.widthMode === "fit" ? "fit" : "fixed",
    shrinkTextToFit: record.shrinkTextToFit === true,
    darkModeOverride: parseOverlayDarkModeOverride(record.darkModeOverride),
    skin: parseOverlaySkinOverride(record.skin),
    customSkin: parseCustomSkinReference(record.customSkin),
    material: parseOverlayMaterialOverride(record.material),
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(record.materialTextureOpacities),
    maxWidth: parseMaxSizePx(record.maxWidth, ELEMENT_MAX_WIDTH_MIN, ELEMENT_MAX_WIDTH_MAX),
    maxHeight: parseMaxSizePx(record.maxHeight, ELEMENT_MAX_HEIGHT_MIN, ELEMENT_MAX_HEIGHT_MAX),
    scale: parseElementScale(record.scale),
    offsets: parseLowerThirdOffsets(record.offsets),
    logo: parseLowerThirdLogo(record.logo),
    sourceID: stringValue(record.sourceID),
    recordKey: stringValue(record.recordKey),
    person: parseLowerThirdPerson(record.person),
  };
}

/** Fit + focal point shared by the player card portrait and the image canvas. */
function parsePhotoFraming(
  record: Record<string, unknown>,
  defaultFit: OverlayPhotoFit,
): { photoFit: OverlayPhotoFit; photoPosition: OverlayPhotoPosition } {
  const rawPhotoPosition = record.photoPosition && typeof record.photoPosition === "object"
    ? record.photoPosition as Record<string, unknown>
    : {};
  return {
    photoFit: record.photoFit === "fit" || record.photoFit === "stretch" || record.photoFit === "fill" ? record.photoFit : defaultFit,
    photoPosition: {
      x: clampNumber(Math.round(finiteNumber(rawPhotoPosition.x) ?? 50), 0, 100),
      y: clampNumber(Math.round(finiteNumber(rawPhotoPosition.y) ?? 50), 0, 100),
    },
  };
}

function parsePlayerCardInstance(
  record: Record<string, unknown>,
  identity: InstanceIdentity,
  defaults: { region: OverlayHorizontalRegion },
): OverlayPlayerCardInstance {
  return {
    ...parseInstanceBase(record, identity),
    region: parseHorizontalRegion(record.region, defaults.region),
    useFlag: record.useFlag !== false,
    useBib: record.useBib !== false,
    showExtraLabel: record.showExtraLabel === true,
    useShortName: record.useShortName === true,
    darkModeOverride: parseOverlayDarkModeOverride(record.darkModeOverride),
    skin: parseOverlaySkinOverride(record.skin),
    customSkin: parseCustomSkinReference(record.customSkin),
    material: parseOverlayMaterialOverride(record.material),
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(record.materialTextureOpacities),
    ...parsePhotoFraming(record, DEFAULT_PLAYER_CARD_PHOTO_FIT),
    photoBackground: parseColorRef(record.photoBackground) ?? { ...DEFAULT_PLAYER_CARD_PHOTO_BACKGROUND },
    photoBackgroundOpacity: clampNumber(
      finiteNumber(record.photoBackgroundOpacity) ?? DEFAULT_PLAYER_CARD_PHOTO_BACKGROUND_OPACITY,
      0,
      1,
    ),
    maxWidth: parseMaxSizePx(record.maxWidth, ELEMENT_MAX_WIDTH_MIN, ELEMENT_MAX_WIDTH_MAX),
    maxHeight: parseMaxSizePx(record.maxHeight, ELEMENT_MAX_HEIGHT_MIN, ELEMENT_MAX_HEIGHT_MAX),
    scale: parseElementScale(record.scale),
    offsets: parseLowerThirdOffsets(record.offsets),
    sourceID: stringValue(record.sourceID),
    recordKey: stringValue(record.recordKey),
    person: parseLowerThirdPerson(record.person),
  };
}

function parseCustomLowerThirdInstance(
  record: Record<string, unknown>,
  identity: InstanceIdentity,
  defaults: { region: OverlayHorizontalRegion },
): OverlayCustomLowerThirdInstance {
  const rows = parseCustomLowerThirdRows(record.rows);
  const selectedRowID = stringValue(record.selectedRowID);
  const selectedRowExists = rows.some((row) => row.id === selectedRowID);
  return {
    ...parseInstanceBase(record, identity),
    fitLayoutReferenceLeft: normalizeFitLayoutReferenceLeft(record.fitLayoutReferenceLeft),
    region: parseHorizontalRegion(record.region, defaults.region),
    widthMode: record.widthMode === "fit" ? "fit" : "fixed",
    shrinkTextToFit: record.shrinkTextToFit === true,
    darkModeOverride: parseOverlayDarkModeOverride(record.darkModeOverride),
    skin: parseOverlaySkinOverride(record.skin),
    customSkin: parseCustomSkinReference(record.customSkin),
    material: parseOverlayMaterialOverride(record.material),
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(record.materialTextureOpacities),
    maxWidth: parseMaxSizePx(record.maxWidth, ELEMENT_MAX_WIDTH_MIN, ELEMENT_MAX_WIDTH_MAX),
    maxHeight: parseMaxSizePx(record.maxHeight, ELEMENT_MAX_HEIGHT_MIN, ELEMENT_MAX_HEIGHT_MAX),
    scale: parseElementScale(record.scale),
    offsets: parseLowerThirdOffsets(record.offsets),
    logo: parseLowerThirdLogo(record.logo),
    selectedRowID: selectedRowExists ? selectedRowID : rows[0]?.id,
    rows,
  };
}

const DEFAULT_SCOREBOARD_TEAM1_HEX = "D0021B";
const DEFAULT_SCOREBOARD_TEAM2_HEX = "0057B8";

function parseScoreboardInstance(record: Record<string, unknown>, identity: InstanceIdentity): OverlayScoreboardInstance {
  const score = (value: unknown): number => Math.max(0, Math.round(finiteNumber(value) ?? 0));
  return {
    ...parseInstanceBase(record, identity),
    region: "top",
    team1Name: stringValue(record.team1Name) ?? "",
    team2Name: stringValue(record.team2Name) ?? "",
    team1Color: parseColorRef(record.team1Color) ?? { source: "custom", hex: DEFAULT_SCOREBOARD_TEAM1_HEX },
    team2Color: parseColorRef(record.team2Color) ?? { source: "custom", hex: DEFAULT_SCOREBOARD_TEAM2_HEX },
    score1: score(record.score1),
    score2: score(record.score2),
    darkModeOverride: parseOverlayDarkModeOverride(record.darkModeOverride),
    skin: parseOverlaySkinOverride(record.skin),
    customSkin: parseCustomSkinReference(record.customSkin),
    material: parseOverlayMaterialOverride(record.material),
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(record.materialTextureOpacities),
    offsets: parseGroupOffsets(record.offsets),
  };
}

/** Cycled defaults for newly added course-profile tracks. */
export const COURSE_PROFILE_TRACK_PALETTE = ["FF2D78", "D0021B", "2D9BFF", "28C76F", "FFB020", "9B51E0", "00C2CB", "F2545B"];
const DEFAULT_COURSE_PROFILE_BASE_HEX = "9AA0A6";
const DEFAULT_COURSE_PROFILE_BASE_OPACITY = 0.8;
const DEFAULT_COURSE_PROFILE_WIDTH = 1200;
const DEFAULT_COURSE_PROFILE_HEIGHT = 130;

const DEFAULT_MINIMAP_RING_HEX = "FF2D78";
export const MINIMAP_ZOOM_SPAN_MIN = 0.0005;
export const MINIMAP_ZOOM_SPAN_MAX = 1;
export const MINIMAP_DIAMETER_MIN = 120;
export const MINIMAP_DIAMETER_MAX = 900;
const DEFAULT_MINIMAP_ZOOM_SPAN = 0.01;
const DEFAULT_MINIMAP_DIAMETER = 320;

const DEFAULT_FULLMAP_TINT_HEX = "FF2D78";
const DEFAULT_FULLMAP_COURSE_LINE_HEX = "FF2D78";
export const FULLMAP_ROTATION_MIN = 0;
export const FULLMAP_ROTATION_MAX = 359;
const DEFAULT_FULLMAP_ZOOM_SPAN = 0.02;

const DEFAULT_SPEEDOMETER_RING_HEX = "FF2D78";
export const SPEEDOMETER_DIAMETER_MIN = 120;
export const SPEEDOMETER_DIAMETER_MAX = 900;
export const SPEEDOMETER_MAX_SPEED_MIN = 10;
export const SPEEDOMETER_MAX_SPEED_MAX = 300;
const DEFAULT_SPEEDOMETER_DIAMETER = 320;
const DEFAULT_SPEEDOMETER_MAX_SPEED = 60;

const DEFAULT_COMPASS_RING_HEX = "FF2D78";
export const COMPASS_DIAMETER_MIN = 120;
export const COMPASS_DIAMETER_MAX = 900;
const DEFAULT_COMPASS_DIAMETER = 320;

const DEFAULT_GRADE_SCOPE_RING_HEX = "FF2D78";
export const GRADE_SCOPE_DIAMETER_MIN = 120;
export const GRADE_SCOPE_DIAMETER_MAX = 900;
export const GRADE_SCOPE_TILT_GAIN_MIN = 1;
export const GRADE_SCOPE_TILT_GAIN_MAX = 6;
const DEFAULT_GRADE_SCOPE_DIAMETER = 320;
export const DEFAULT_GRADE_SCOPE_TILT_GAIN = 3;

/** Shared caps for the optional lower-third/pylon max-size downscale fields. */
export const ELEMENT_MAX_WIDTH_MIN = 120;
export const ELEMENT_MAX_WIDTH_MAX = 1920;
export const ELEMENT_MAX_HEIGHT_MIN = 40;
export const ELEMENT_MAX_HEIGHT_MAX = 1080;

/** Podium per-bar width cap (shrink-text mode only). */
export const PODIUM_BAR_MAX_WIDTH_MIN = 160;
export const PODIUM_BAR_MAX_WIDTH_MAX = 900;
export const DEFAULT_PODIUM_BAR_MAX_WIDTH = 380;

/** Bounds for the lower-third/pylon user scale (25%–300%; 12–144px pylon rows). */
export const ELEMENT_SCALE_MIN = 0.25;
export const ELEMENT_SCALE_MAX = 3;

/** Shared canvas-scale normalization for storage and uncommitted designer values. */
export function normalizeOverlayRootScale(value: unknown): OverlayRootScale | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const record = value as Record<string, unknown>;
  const axis = (value: unknown) => typeof value === "number" && Number.isFinite(value)
    ? Math.round(Math.min(ELEMENT_SCALE_MAX, Math.max(ELEMENT_SCALE_MIN, value)) * 10000) / 10000 : 1;
  const x = axis(record.x);
  const y = axis(record.y);
  return x === 1 && y === 1 ? undefined : { x, y };
}

export function normalizeFitLayoutReferenceLeft(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? value : undefined;
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function parseMaxSizePx(value: unknown, min: number, max: number): number | undefined {
  const parsed = finiteNumber(value);
  return parsed === undefined ? undefined : clampNumber(Math.round(parsed), min, max);
}

// Fractional, unlike parseMaxSizePx (rounding would collapse e.g. 1.33 to 1);
// 1 folds to undefined so 100% stays absent from the Firestore doc.
function parseElementScale(value: unknown): number | undefined {
  const parsed = finiteNumber(value);
  if (parsed === undefined) return undefined;
  const clamped = clampNumber(parsed, ELEMENT_SCALE_MIN, ELEMENT_SCALE_MAX);
  return clamped === 1 ? undefined : Math.round(clamped * 10000) / 10000;
}

function parseMiniMapInstance(
  record: Record<string, unknown>,
  identity: InstanceIdentity,
  defaults: { region: OverlayHorizontalRegion },
): OverlayMiniMapInstance {
  return {
    ...parseInstanceBase(record, identity),
    region: parseHorizontalRegion(record.region, defaults.region),
    deviceID: stringValue(record.deviceID) ?? "",
    zoomSpan: clampNumber(finiteNumber(record.zoomSpan) ?? DEFAULT_MINIMAP_ZOOM_SPAN, MINIMAP_ZOOM_SPAN_MIN, MINIMAP_ZOOM_SPAN_MAX),
    diameter: clampNumber(finiteNumber(record.diameter) ?? DEFAULT_MINIMAP_DIAMETER, MINIMAP_DIAMETER_MIN, MINIMAP_DIAMETER_MAX),
    ringColor: parseColorRef(record.ringColor) ?? { source: "custom", hex: DEFAULT_MINIMAP_RING_HEX },
    bibText: stringValue(record.bibText) ?? "",
    labelText: stringValue(record.labelText) ?? "",
    showSpeed: record.showSpeed === true,
    speedUnit: record.speedUnit === "kmh" ? "kmh" : "mph",
    offsets: parseGroupOffsets(record.offsets),
  };
}

function parseFullMapFraming(value: unknown): OverlayFullMapFraming {
  return value === "course" ? "course" : "follow";
}

function parseFullMapType(value: unknown): OverlayFullMapType {
  return value === "standard" || value === "satellite" || value === "hybrid" ? value : "mutedStandard";
}

function parseFullMapInstance(record: Record<string, unknown>, identity: InstanceIdentity): OverlayFullMapInstance {
  return {
    ...parseInstanceBase(record, identity),
    rootScale: undefined,
    region: "full",
    framing: parseFullMapFraming(record.framing),
    deviceID: stringValue(record.deviceID) ?? "",
    zoomSpan: clampNumber(finiteNumber(record.zoomSpan) ?? DEFAULT_FULLMAP_ZOOM_SPAN, MINIMAP_ZOOM_SPAN_MIN, MINIMAP_ZOOM_SPAN_MAX),
    mapType: parseFullMapType(record.mapType),
    colorScheme: record.colorScheme === "light" ? "light" : "dark",
    tintColor: parseColorRef(record.tintColor) ?? { source: "custom", hex: DEFAULT_FULLMAP_TINT_HEX },
    rotation: clampNumber(Math.round(finiteNumber(record.rotation) ?? 0), FULLMAP_ROTATION_MIN, FULLMAP_ROTATION_MAX),
    courseLineColor: parseColorRef(record.courseLineColor) ?? { source: "custom", hex: DEFAULT_FULLMAP_COURSE_LINE_HEX },
  };
}

function parseSpeedometerInstance(
  record: Record<string, unknown>,
  identity: InstanceIdentity,
  defaults: { region: OverlayHorizontalRegion },
): OverlaySpeedometerInstance {
  return {
    ...parseInstanceBase(record, identity),
    region: parseHorizontalRegion(record.region, defaults.region),
    deviceID: stringValue(record.deviceID) ?? "",
    displayMode: record.displayMode === "digital" ? "digital" : "analog",
    diameter: clampNumber(finiteNumber(record.diameter) ?? DEFAULT_SPEEDOMETER_DIAMETER, SPEEDOMETER_DIAMETER_MIN, SPEEDOMETER_DIAMETER_MAX),
    maxSpeed: clampNumber(finiteNumber(record.maxSpeed) ?? DEFAULT_SPEEDOMETER_MAX_SPEED, SPEEDOMETER_MAX_SPEED_MIN, SPEEDOMETER_MAX_SPEED_MAX),
    ringColor: parseColorRef(record.ringColor) ?? { source: "custom", hex: DEFAULT_SPEEDOMETER_RING_HEX },
    labelText: stringValue(record.labelText) ?? "",
    speedUnit: record.speedUnit === "kmh" ? "kmh" : "mph",
    offsets: parseGroupOffsets(record.offsets),
  };
}

function parseCompassInstance(
  record: Record<string, unknown>,
  identity: InstanceIdentity,
  defaults: { region: OverlayHorizontalRegion },
): OverlayCompassInstance {
  return {
    ...parseInstanceBase(record, identity),
    region: parseHorizontalRegion(record.region, defaults.region),
    deviceID: stringValue(record.deviceID) ?? "",
    diameter: clampNumber(finiteNumber(record.diameter) ?? DEFAULT_COMPASS_DIAMETER, COMPASS_DIAMETER_MIN, COMPASS_DIAMETER_MAX),
    ringColor: parseColorRef(record.ringColor) ?? { source: "custom", hex: DEFAULT_COMPASS_RING_HEX },
    showReadout: record.showReadout !== false,
    labelText: stringValue(record.labelText) ?? "",
    offsets: parseGroupOffsets(record.offsets),
  };
}

function parseGradeScopeInstance(
  record: Record<string, unknown>,
  identity: InstanceIdentity,
  defaults: { region: OverlayHorizontalRegion },
): OverlayGradeScopeInstance {
  return {
    ...parseInstanceBase(record, identity),
    region: parseHorizontalRegion(record.region, defaults.region),
    deviceID: stringValue(record.deviceID) ?? "",
    diameter: clampNumber(finiteNumber(record.diameter) ?? DEFAULT_GRADE_SCOPE_DIAMETER, GRADE_SCOPE_DIAMETER_MIN, GRADE_SCOPE_DIAMETER_MAX),
    tiltGain: clampNumber(finiteNumber(record.tiltGain) ?? DEFAULT_GRADE_SCOPE_TILT_GAIN, GRADE_SCOPE_TILT_GAIN_MIN, GRADE_SCOPE_TILT_GAIN_MAX),
    ringColor: parseColorRef(record.ringColor) ?? { source: "custom", hex: DEFAULT_GRADE_SCOPE_RING_HEX },
    labelText: stringValue(record.labelText) ?? "",
    offsets: parseGroupOffsets(record.offsets),
  };
}

// --- Custom widget spec parsing -----------------------------------------------
//
// Tolerant-but-strict sanitizer shared by the designer, the AI write path, and
// the public overlay page (via parseOverlayConfig): every number is clamped,
// every color validated, every string truncated, and anything unrecognizable
// is dropped rather than propagated. The AI write path additionally compares
// its submitted spec against the sanitized result and rejects on any
// difference (see OverlayInputParameterCodec) — only the read path is lenient.

export const CUSTOM_WIDGET_FRAME_COORD_LIMIT = { x: 960, y: 540 };
export const CUSTOM_WIDGET_FONT_SIZE_MIN = 8;
export const CUSTOM_WIDGET_FONT_SIZE_MAX = 160;

export function emptyCustomWidgetSpec(): OverlayCustomWidgetSpec {
  return { version: 1, base: { x: 760, y: 120 }, size: { width: 400, height: 100 }, layers: [], controls: [] };
}

function parseCustomWidgetColor(value: unknown): OverlayCustomWidgetColor {
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (record.source === "accent") return { source: "accent" };
    if (record.source === "hex") {
      const hex = stringValue(record.hex);
      if (hex && /^[0-9a-f]{6}$/i.test(hex)) return { source: "hex", hex: hex.toLowerCase() };
    }
  }
  return { source: "text" };
}

function parseCustomWidgetFrame(value: unknown): OverlayCustomWidgetFrame | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const x = finiteNumber(record.x);
  const y = finiteNumber(record.y);
  const width = finiteNumber(record.width);
  const height = finiteNumber(record.height);
  if (x === undefined || y === undefined || width === undefined || height === undefined) return null;
  return {
    x: clampNumber(x, -CUSTOM_WIDGET_FRAME_COORD_LIMIT.x, CUSTOM_WIDGET_FRAME_COORD_LIMIT.x),
    y: clampNumber(y, -CUSTOM_WIDGET_FRAME_COORD_LIMIT.y, CUSTOM_WIDGET_FRAME_COORD_LIMIT.y),
    width: clampNumber(width, 1, 1920),
    height: clampNumber(height, 1, 1080),
  };
}

function parseCustomWidgetVisibleWhen(value: unknown, declaredKeys: Set<string>): OverlayCustomWidgetVisibleWhen | undefined {
  if (!value || typeof value !== "object") return undefined;
  const record = value as Record<string, unknown>;
  const key = stringValue(record.key);
  if (!key || !declaredKeys.has(key)) return undefined;
  const equals = record.equals;
  if (typeof equals !== "string" && typeof equals !== "number" && typeof equals !== "boolean") return undefined;
  return { key, equals };
}

function parseCustomWidgetControlDef(value: unknown): OverlayCustomWidgetControlDef | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const key = stringValue(record.key);
  if (!key || !CUSTOM_WIDGET_CONTROL_KEY_PATTERN.test(key)) return null;
  const label = (stringValue(record.label) ?? key).slice(0, CUSTOM_WIDGET_LABEL_MAX_LENGTH);
  switch (record.kind) {
    case "text": {
      const maxLength = finiteNumber(record.maxLength);
      const cappedMax = maxLength !== undefined ? clampNumber(Math.round(maxLength), 1, CUSTOM_WIDGET_TEXT_MAX_LENGTH) : undefined;
      const fallback = (stringValue(record.default) ?? "").slice(0, cappedMax ?? CUSTOM_WIDGET_TEXT_MAX_LENGTH);
      return { kind: "text", key, label, default: fallback, maxLength: cappedMax };
    }
    case "number": {
      const min = finiteNumber(record.min);
      const max = finiteNumber(record.max);
      const step = finiteNumber(record.step);
      const format = record.format === "integer" || record.format === "decimal1" ? record.format : undefined;
      let fallback = finiteNumber(record.default) ?? 0;
      if (min !== undefined && max !== undefined && min > max) return null;
      fallback = clampNumber(fallback, min ?? -Infinity, max ?? Infinity);
      if (format === "integer") fallback = Math.round(fallback);
      return {
        kind: "number",
        key,
        label,
        default: fallback,
        min,
        max,
        step: step !== undefined && step > 0 ? step : undefined,
        format,
      };
    }
    case "enum": {
      if (!Array.isArray(record.options)) return null;
      const seenValues = new Set<string>();
      const options: Array<{ value: string; label: string }> = [];
      for (const entry of record.options) {
        if (options.length >= CUSTOM_WIDGET_ENUM_OPTION_LIMIT) break;
        if (!entry || typeof entry !== "object") continue;
        const optionRecord = entry as Record<string, unknown>;
        const optionValue = stringValue(optionRecord.value);
        if (!optionValue || optionValue.length > CUSTOM_WIDGET_LABEL_MAX_LENGTH || seenValues.has(optionValue)) continue;
        seenValues.add(optionValue);
        options.push({
          value: optionValue,
          label: (stringValue(optionRecord.label) ?? optionValue).slice(0, CUSTOM_WIDGET_LABEL_MAX_LENGTH),
        });
      }
      if (options.length === 0) return null;
      const fallback = stringValue(record.default);
      return {
        kind: "enum",
        key,
        label,
        default: fallback && seenValues.has(fallback) ? fallback : options[0].value,
        options,
      };
    }
    case "bool":
      return { kind: "bool", key, label, default: record.default === true };
    default:
      return null;
  }
}

function parseCustomWidgetTextStyle(value: unknown): OverlayCustomWidgetTextStyle {
  const record = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  const weight = finiteNumber(record.fontWeight);
  const fontWeight: OverlayCustomWidgetFontWeight =
    weight === 400 || weight === 500 || weight === 700 || weight === 800 ? weight : 600;
  const letterSpacing = finiteNumber(record.letterSpacing);
  return {
    fontSize: clampNumber(finiteNumber(record.fontSize) ?? 24, CUSTOM_WIDGET_FONT_SIZE_MIN, CUSTOM_WIDGET_FONT_SIZE_MAX),
    fontWeight,
    letterSpacing: letterSpacing !== undefined ? clampNumber(letterSpacing, -2, 12) : undefined,
    textTransform: record.textTransform === "uppercase" ? "uppercase" : undefined,
    align: record.align === "center" || record.align === "right" ? record.align : undefined,
    color: parseCustomWidgetColor(record.color),
  };
}

function parseCustomWidgetLayer(value: unknown, declaredKeys: Set<string>): OverlayCustomWidgetLayer | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const id = stringValue(record.id);
  if (!id || !OVERLAY_INSTANCE_ID_PATTERN.test(id)) return null;
  const frame = parseCustomWidgetFrame(record.frame);
  if (!frame) return null;
  const visibleWhen = parseCustomWidgetVisibleWhen(record.visibleWhen, declaredKeys);
  const opacity = finiteNumber(record.opacity);
  const cornerRadius = finiteNumber(record.cornerRadius);
  switch (record.kind) {
    case "rect": {
      const borderWidth = finiteNumber(record.borderWidth);
      return {
        kind: "rect",
        id,
        frame,
        fill: parseCustomWidgetColor(record.fill),
        opacity: opacity !== undefined ? clampNumber(opacity, 0, 1) : undefined,
        cornerRadius: cornerRadius !== undefined ? clampNumber(cornerRadius, 0, 200) : undefined,
        borderWidth: borderWidth !== undefined ? clampNumber(borderWidth, 0, 20) : undefined,
        borderColor: record.borderColor !== undefined ? parseCustomWidgetColor(record.borderColor) : undefined,
        visibleWhen,
      };
    }
    case "text": {
      const template = stringValue(record.template);
      if (template === undefined) return null;
      return {
        kind: "text",
        id,
        frame,
        template: template.slice(0, CUSTOM_WIDGET_TEXT_MAX_LENGTH),
        style: parseCustomWidgetTextStyle(record.style),
        visibleWhen,
      };
    }
    case "image": {
      const url = stringValue(record.url);
      if (!url || !url.startsWith(CUSTOM_WIDGET_IMAGE_URL_PREFIX)) return null;
      return {
        kind: "image",
        id,
        frame,
        url,
        fit: record.fit === "cover" ? "cover" : undefined,
        opacity: opacity !== undefined ? clampNumber(opacity, 0, 1) : undefined,
        cornerRadius: cornerRadius !== undefined ? clampNumber(cornerRadius, 0, 200) : undefined,
        visibleWhen,
      };
    }
    default:
      return null;
  }
}

export function parseCustomWidgetSpec(raw: unknown): OverlayCustomWidgetSpec {
  if (!raw || typeof raw !== "object") return emptyCustomWidgetSpec();
  const record = raw as Record<string, unknown>;

  const controls: OverlayCustomWidgetControlDef[] = [];
  const declaredKeys = new Set<string>();
  if (Array.isArray(record.controls)) {
    for (const entry of record.controls) {
      if (controls.length >= CUSTOM_WIDGET_CONTROL_LIMIT) break;
      const control = parseCustomWidgetControlDef(entry);
      if (!control || declaredKeys.has(control.key)) continue;
      declaredKeys.add(control.key);
      controls.push(control);
    }
  }

  const layers: OverlayCustomWidgetLayer[] = [];
  const seenLayerIds = new Set<string>();
  if (Array.isArray(record.layers)) {
    for (const entry of record.layers) {
      if (layers.length >= CUSTOM_WIDGET_LAYER_LIMIT) break;
      const layer = parseCustomWidgetLayer(entry, declaredKeys);
      if (!layer || seenLayerIds.has(layer.id)) continue;
      seenLayerIds.add(layer.id);
      layers.push(layer);
    }
  }

  const baseRecord = record.base && typeof record.base === "object" ? (record.base as Record<string, unknown>) : {};
  const sizeRecord = record.size && typeof record.size === "object" ? (record.size as Record<string, unknown>) : {};
  const spec: OverlayCustomWidgetSpec = {
    version: 1,
    base: {
      x: clampNumber(finiteNumber(baseRecord.x) ?? 760, 0, 1920),
      y: clampNumber(finiteNumber(baseRecord.y) ?? 120, 0, 1080),
    },
    size: {
      width: clampNumber(finiteNumber(sizeRecord.width) ?? 400, 1, 1920),
      height: clampNumber(finiteNumber(sizeRecord.height) ?? 100, 1, 1080),
    },
    layers,
    controls,
  };
  // Byte cap is the doc-growth backstop — an oversize spec degrades to empty
  // (content-gated OUT) rather than partially rendering.
  if (JSON.stringify(spec).length > CUSTOM_WIDGET_SPEC_MAX_JSON_BYTES) return emptyCustomWidgetSpec();
  return spec;
}

/**
 * Strict-write companion to the tolerant parseCustomWidgetSpec: names what the
 * sanitizer would reject or drop, so the AI write path can refuse a spec
 * instead of silently persisting a stripped-down widget. Number clamps and
 * cosmetic normalizations are deliberately NOT issues — only structural loss.
 */
export function customWidgetSpecIssues(raw: unknown): string[] {
  if (!raw || typeof raw !== "object") return ["spec must be a JSON object"];
  const record = raw as Record<string, unknown>;
  const issues: string[] = [];
  let rawBytes = 0;
  try {
    rawBytes = JSON.stringify(raw).length;
  } catch {
    return ["spec must be plain JSON (no cycles or non-JSON values)"];
  }
  if (rawBytes > CUSTOM_WIDGET_SPEC_MAX_JSON_BYTES) {
    issues.push(`spec is ${rawBytes} JSON bytes — the limit is ${CUSTOM_WIDGET_SPEC_MAX_JSON_BYTES}`);
  }
  const parsed = parseCustomWidgetSpec(raw);
  if (!Array.isArray(record.layers)) {
    issues.push("layers must be an array");
  } else if (record.layers.length > parsed.layers.length) {
    issues.push(
      `${record.layers.length - parsed.layers.length} of ${record.layers.length} layers were rejected `
      + `(invalid kind/id/frame, duplicate id, template missing, image URL outside the app's Storage bucket, `
      + `or over the ${CUSTOM_WIDGET_LAYER_LIMIT}-layer limit)`,
    );
  }
  if (!Array.isArray(record.controls)) {
    issues.push("controls must be an array");
  } else if (record.controls.length > parsed.controls.length) {
    issues.push(
      `${record.controls.length - parsed.controls.length} of ${record.controls.length} controls were rejected `
      + `(invalid kind, key not matching ${CUSTOM_WIDGET_CONTROL_KEY_PATTERN}, duplicate key, min > max, empty enum options, `
      + `or over the ${CUSTOM_WIDGET_CONTROL_LIMIT}-control limit)`,
    );
  }
  const declaredKeys = new Set(parsed.controls.map((control) => control.key));
  if (Array.isArray(record.layers)) {
    for (const entry of record.layers) {
      if (!entry || typeof entry !== "object") continue;
      const visibleWhen = (entry as Record<string, unknown>).visibleWhen;
      if (!visibleWhen || typeof visibleWhen !== "object") continue;
      const key = stringValue((visibleWhen as Record<string, unknown>).key);
      if (key && !declaredKeys.has(key)) issues.push(`visibleWhen references undeclared control "${key}"`);
    }
  }
  return issues;
}

/** Drops unknown keys and coerces every kept value into its control's domain. */
export function sanitizeCustomWidgetValues(spec: OverlayCustomWidgetSpec, raw: unknown): OverlayCustomWidgetValues {
  const record = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const values: OverlayCustomWidgetValues = {};
  for (const control of spec.controls) {
    const value = record[control.key];
    if (value === undefined) continue;
    switch (control.kind) {
      case "text": {
        if (typeof value !== "string") break;
        values[control.key] = value.slice(0, control.maxLength ?? CUSTOM_WIDGET_TEXT_MAX_LENGTH);
        break;
      }
      case "number": {
        const number = finiteNumber(value);
        if (number === undefined) break;
        let coerced = clampNumber(number, control.min ?? -Infinity, control.max ?? Infinity);
        if (control.format === "integer") coerced = Math.round(coerced);
        values[control.key] = coerced;
        break;
      }
      case "enum": {
        if (typeof value !== "string") break;
        values[control.key] = control.options.some((option) => option.value === value) ? value : control.default;
        break;
      }
      case "bool": {
        values[control.key] = value === true;
        break;
      }
    }
  }
  return values;
}

/** Control defaults overlaid with the operator's saved values — what text layers interpolate. */
export function resolvedCustomWidgetValues(instance: OverlayCustomWidgetInstance): OverlayCustomWidgetValues {
  const resolved: OverlayCustomWidgetValues = {};
  for (const control of instance.spec.controls) resolved[control.key] = control.default;
  return { ...resolved, ...instance.values };
}

function parseCustomWidgetInstance(
  record: Record<string, unknown>,
  identity: InstanceIdentity,
  defaults: { region: OverlayHorizontalRegion },
): OverlayCustomWidgetInstance {
  const spec = parseCustomWidgetSpec(record.spec);
  return {
    ...parseInstanceBase(record, identity),
    region: parseHorizontalRegion(record.region, defaults.region),
    spec,
    values: sanitizeCustomWidgetValues(spec, record.values),
    offsets: parseGroupOffsets(record.offsets),
  };
}

function parseBugInstance(
  record: Record<string, unknown>,
  identity: InstanceIdentity,
  defaults: { region: OverlayBugCorner },
): OverlayBugInstance {
  return {
    ...parseInstanceBase(record, identity),
    region: parseBugCorner(record.region, defaults.region),
    rotationIntervalSeconds: clampNumber(
      finiteNumber(record.rotationIntervalSeconds) ?? DEFAULT_BUG_ROTATION_SECONDS,
      BUG_ROTATION_SECONDS_MIN,
      BUG_ROTATION_SECONDS_MAX,
    ),
    mode: record.mode === "data" ? "data" : "manual",
    sourceID: stringValue(record.sourceID),
    logoField: stringValue(record.logoField),
    accentField: stringValue(record.accentField),
    selectedRowIndices: Array.isArray(record.selectedRowIndices) ? [...new Set(record.selectedRowIndices.filter((value): value is number => typeof value === "number" && Number.isInteger(value) && value >= 0))].sort((a, b) => a - b).slice(0, BUG_ROW_LIMIT) : [],
    dataRows: parseBugRows(record.dataRows),
    rows: parseBugRows(record.rows),
    offsets: parseGroupOffsets(record.offsets),
  };
}

function parseBillboardInstance(record: Record<string, unknown>, identity: InstanceIdentity): OverlayBillboardInstance {
  return {
    ...parseInstanceBase(record, identity),
    region: "center",
    mode: record.mode === "manual" ? "manual" : "rotate",
    rotationIntervalSeconds: clampNumber(
      finiteNumber(record.rotationIntervalSeconds) ?? DEFAULT_BUG_ROTATION_SECONDS,
      BUG_ROTATION_SECONDS_MIN,
      BUG_ROTATION_SECONDS_MAX,
    ),
    activeRowId: stringValue(record.activeRowId),
    rows: parseBillboardRows(record.rows),
    scale: parseElementScale(record.scale),
    skin: parseOverlaySkinOverride(record.skin),
    customSkin: parseCustomSkinReference(record.customSkin),
    material: parseOverlayMaterialOverride(record.material),
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(record.materialTextureOpacities),
    offsets: parseGroupOffsets(record.offsets),
  };
}

function parseStartListRowsPerColumn(raw: unknown): number {
  const value = typeof raw === "number" && Number.isFinite(raw) ? Math.round(raw) : START_LIST_DEFAULT_ROWS_PER_COLUMN;
  return clampNumber(value, START_LIST_ROWS_PER_COLUMN_MIN, START_LIST_ROWS_PER_COLUMN_MAX);
}

function parseStartListLogoMode(raw: unknown): OverlayStartListLogoMode {
  return raw === "custom" || raw === "none" ? raw : "competition";
}

/** https-only logo URL (lower-third logo precedent — any host); anything else folds away. */
function parseHttpsImageURL(raw: unknown): string | undefined {
  const url = stringValue(raw)?.trim() ?? "";
  return BUG_ROW_URL_PATTERN.test(url) ? url : undefined;
}

function parseStartListInstance(record: Record<string, unknown>, identity: InstanceIdentity): OverlayStartListInstance {
  return {
    ...parseInstanceBase(record, identity),
    region: "center",
    sourceID: stringValue(record.sourceID),
    sortFieldKey: stringValue(record.sortFieldKey),
    sortDirection: record.sortDirection === "desc" ? "desc" : "asc",
    filterFieldKey: stringValue(record.filterFieldKey),
    filterValue: stringValue(record.filterValue),
    columns: record.columns === 2 ? 2 : 3,
    rowsPerColumn: parseStartListRowsPerColumn(record.rowsPerColumn),
    pageIndex: normalizeSortFilterPylonPageIndex(record.pageIndex),
    rows: pylonAthleteRows(parsePylonRows(record.rows, START_LIST_ROW_LIMIT)),
    label: stringValue(record.label),
    logoMode: parseStartListLogoMode(record.logoMode),
    customLogoURL: parseHttpsImageURL(record.customLogoURL),
    customLogoDarkURL: parseHttpsImageURL(record.customLogoDarkURL),
    logoHeight: parseMaxSizePx(record.logoHeight, START_LIST_LOGO_HEIGHT_MIN, START_LIST_LOGO_HEIGHT_MAX),
    useFlag: record.useFlag !== false,
    useBib: record.useBib !== false,
    useShortName: record.useShortName === true,
    shrinkTextToFit: record.shrinkTextToFit === true,
    darkModeOverride: parseOverlayDarkModeOverride(record.darkModeOverride),
    skin: parseOverlaySkinOverride(record.skin),
    customSkin: parseCustomSkinReference(record.customSkin),
    material: parseOverlayMaterialOverride(record.material),
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(record.materialTextureOpacities),
    scale: parseElementScale(record.scale),
    maxWidth: parseMaxSizePx(record.maxWidth, ELEMENT_MAX_WIDTH_MIN, ELEMENT_MAX_WIDTH_MAX),
    maxHeight: parseMaxSizePx(record.maxHeight, ELEMENT_MAX_HEIGHT_MIN, ELEMENT_MAX_HEIGHT_MAX),
    offsets: parseLowerThirdOffsets(record.offsets),
  };
}

/** Accept public HTTPS URL syntax without embedded credentials or browser URL repair. */
export function normalizeWebSourceURL(value: unknown): string {
  const url = typeof value === "string" ? value.trim() : "";
  if (url.length > WEB_SOURCE_URL_MAX_LENGTH || !/^https:\/\/[^/]/i.test(url) || /[\s\\]/.test(url) || url.slice(8).split(/[/?#]/, 1)[0].includes("@")) return "";
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" && parsed.hostname && !parsed.username && !parsed.password ? url : "";
  } catch {
    return "";
  }
}

function parseWebSourceInstance(record: Record<string, unknown>, identity: InstanceIdentity): OverlayWebSourceInstance {
  return {
    ...parseInstanceBase(record, identity),
    region: "canvas",
    url: normalizeWebSourceURL(record.url),
    canvasWidth: parseMaxSizePx(record.canvasWidth, WEB_SOURCE_SIZE_MIN, WEB_SOURCE_WIDTH_MAX) ?? DEFAULT_WEB_SOURCE_WIDTH,
    canvasHeight: parseMaxSizePx(record.canvasHeight, WEB_SOURCE_SIZE_MIN, WEB_SOURCE_HEIGHT_MAX) ?? DEFAULT_WEB_SOURCE_HEIGHT,
    unloadWhenHidden: record.unloadWhenHidden !== false,
    reloadToken: (stringValue(record.reloadToken) ?? "").slice(0, 80),
    scale: parseElementScale(record.scale),
    offsets: parseGroupOffsets(record.offsets),
  };
}

function parseTickerMessages(value: unknown): OverlayTickerMessage[] {
  if (!Array.isArray(value)) return [];
  const messages: OverlayTickerMessage[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== "object") continue;
    const record = entry as Record<string, unknown>;
    const id = stringValue(record.id);
    if (!id) continue;
    messages.push({ id, text: (stringValue(record.text) ?? "").slice(0, TICKER_MESSAGE_MAX_LENGTH) });
    if (messages.length >= TICKER_MESSAGE_LIMIT) break;
  }
  return messages;
}

function parseAdvancedTimerInstance(record: Record<string, unknown>, identity: InstanceIdentity): OverlayAdvancedTimerInstance {
  return {
    ...parseInstanceBase(record, identity),
    region: parseTimerRegion(record.region, "top"),
    label: stringValue(record.label),
    mode: record.mode === "countup" ? "countup" : "countdown",
    targetAt: dateValue(record.targetAt) ?? null,
    timeZone: isValidTimeZone(record.timeZone) ? record.timeZone : "UTC",
    darkModeOverride: parseOverlayDarkModeOverride(record.darkModeOverride),
    skin: parseOverlaySkinOverride(record.skin),
    customSkin: parseCustomSkinReference(record.customSkin),
    material: parseOverlayMaterialOverride(record.material),
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(record.materialTextureOpacities),
    offsets: parseLowerThirdOffsets(record.offsets),
  };
}

function parseTickerInstance(record: Record<string, unknown>, identity: InstanceIdentity): OverlayTickerInstance {
  return {
    ...parseInstanceBase(record, identity),
    region: parseTimerRegion(record.region, "bottom"),
    messages: parseTickerMessages(record.messages),
    divider: OVERLAY_TICKER_DIVIDERS.includes(record.divider as OverlayTickerDivider) ? record.divider as OverlayTickerDivider : "bullet",
    dividerImageURL: parseHttpsImageURL(record.dividerImageURL),
    direction: record.direction === "ltr" ? "ltr" : "rtl",
    speed: parseMaxSizePx(record.speed, TICKER_SPEED_MIN, TICKER_SPEED_MAX) ?? DEFAULT_TICKER_SPEED,
    barWidth: parseMaxSizePx(record.barWidth, TICKER_WIDTH_MIN, TICKER_WIDTH_MAX) ?? DEFAULT_TICKER_WIDTH,
    fontSize: parseMaxSizePx(record.fontSize, TICKER_FONT_SIZE_MIN, TICKER_FONT_SIZE_MAX) ?? DEFAULT_TICKER_FONT_SIZE,
    darkModeOverride: parseOverlayDarkModeOverride(record.darkModeOverride),
    material: parseOverlayMaterialOverride(record.material),
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(record.materialTextureOpacities),
    offsets: parseGroupOffsets(record.offsets),
  };
}

function parseImageCanvasInstance(record: Record<string, unknown>, identity: InstanceIdentity): OverlayImageCanvasInstance {
  const url = stringValue(record.url)?.trim() ?? "";
  return {
    ...parseInstanceBase(record, identity),
    region: "canvas",
    // https-only (lower-third logo / bug precedent — any host, not bucket-restricted).
    url: BUG_ROW_URL_PATTERN.test(url) ? url : "",
    canvasWidth: parseMaxSizePx(record.canvasWidth, IMAGE_CANVAS_SIZE_MIN, IMAGE_CANVAS_WIDTH_MAX) ?? DEFAULT_IMAGE_CANVAS_WIDTH,
    canvasHeight: parseMaxSizePx(record.canvasHeight, IMAGE_CANVAS_SIZE_MIN, IMAGE_CANVAS_HEIGHT_MAX) ?? DEFAULT_IMAGE_CANVAS_HEIGHT,
    background: parseColorRef(record.background) ?? undefined,
    backgroundOpacity: clampNumber(finiteNumber(record.backgroundOpacity) ?? DEFAULT_IMAGE_CANVAS_BACKGROUND_OPACITY, 0, 1),
    cornerRadius: parseMaxSizePx(record.cornerRadius, 0, IMAGE_CANVAS_RADIUS_MAX) || undefined,
    ...parsePhotoFraming(record, DEFAULT_IMAGE_CANVAS_PHOTO_FIT),
    scale: parseElementScale(record.scale),
    offsets: parseGroupOffsets(record.offsets),
  };
}

function parseCourseProfileTrack(value: unknown, index: number): OverlayCourseProfileTrack | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const id = stringValue(record.id);
  if (!id) return null;
  return {
    id,
    deviceID: stringValue(record.deviceID) ?? "",
    color: parseColorRef(record.color)
      ?? { source: "custom", hex: COURSE_PROFILE_TRACK_PALETTE[index % COURSE_PROFILE_TRACK_PALETTE.length] },
    label: stringValue(record.label) ?? "",
  };
}

function parseCourseProfileTracks(value: unknown): OverlayCourseProfileTrack[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry, index) => parseCourseProfileTrack(entry, index))
    .filter((track): track is OverlayCourseProfileTrack => track !== null)
    .slice(0, COURSE_PROFILE_TRACK_LIMIT);
}

function parseCourseProfileInstance(record: Record<string, unknown>, identity: InstanceIdentity): OverlayCourseProfileInstance {
  return {
    ...parseInstanceBase(record, identity),
    region: "bottom",
    width: clampNumber(finiteNumber(record.width) ?? DEFAULT_COURSE_PROFILE_WIDTH, COURSE_PROFILE_WIDTH_MIN, COURSE_PROFILE_WIDTH_MAX),
    height: clampNumber(finiteNumber(record.height) ?? DEFAULT_COURSE_PROFILE_HEIGHT, COURSE_PROFILE_HEIGHT_MIN, COURSE_PROFILE_HEIGHT_MAX),
    baseColor: parseColorRef(record.baseColor) ?? { source: "custom", hex: DEFAULT_COURSE_PROFILE_BASE_HEX },
    baseOpacity: clampNumber(finiteNumber(record.baseOpacity) ?? DEFAULT_COURSE_PROFILE_BASE_OPACITY, 0, 1),
    tracks: parseCourseProfileTracks(record.tracks),
    offsets: parseGroupOffsets(record.offsets),
  };
}

/** Drops invalid/duplicate ids and applies the per-type cap. */
function parseInstanceArray<T extends { id: string }>(
  value: unknown,
  fallbackName: string,
  parseOne: (record: Record<string, unknown>, identity: InstanceIdentity) => T,
  cap = OVERLAY_INSTANCE_CAP,
): T[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const instances: T[] = [];
  for (const entry of value) {
    if (instances.length >= cap) break;
    if (!entry || typeof entry !== "object") continue;
    const record = entry as Record<string, unknown>;
    const id = stringValue(record.id);
    if (!id || !OVERLAY_INSTANCE_ID_PATTERN.test(id) || seen.has(id)) continue;
    seen.add(id);
    instances.push(parseOne(record, { id, name: fallbackName }));
  }
  return instances;
}

// --- Legacy migration ------------------------------------------------------------

export const LEGACY_INSTANCE_IDS = {
  mpro: "legacy-mpro",
  fpro: "legacy-fpro",
  primary: "legacy-primary",
  secondary: "legacy-secondary",
  custom: "legacy-custom",
  pylonA: "legacy-pylon-a",
  pylonB: "legacy-pylon-b",
  timerA: "legacy-timer-a",
  timerB: "legacy-timer-b",
} as const;

/**
 * Synthesizes instances from a legacy fixed-slot doc (or from `{}` for the
 * default starter set). Deterministic ids/names/regions so live-control keys
 * are stable before the first save. Mirrored by normalizedElements() in
 * functions/src/overlayRuntime.ts — keep both in sync.
 */
export function legacyOverlayElements(data: DocumentData): OverlayElements {
  const layoutRecord = (data.layout ?? {}) as Record<string, unknown>;
  const groupsRecord = (layoutRecord.groups ?? {}) as Record<string, unknown>;
  const mproRegion: OverlayHorizontalRegion = layoutRecord.position1Category === "FPRO" ? "right" : "left";
  const fproRegion: OverlayHorizontalRegion = mproRegion === "left" ? "right" : "left";
  const lowerThirdsRecord = (data.lowerThirds ?? {}) as Record<string, unknown>;
  const pylonsRecord = (data.pylons ?? {}) as Record<string, unknown>;
  const timersRecord = (data.timers ?? {}) as Record<string, unknown>;
  const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === "object" ? (value as Record<string, unknown>) : {};

  return {
    gpsGroups: [
      parseGpsGroupInstance(asRecord(groupsRecord.MPRO), { id: LEGACY_INSTANCE_IDS.mpro, name: "Men's Group" }, {
        region: mproRegion,
        category: "MPRO",
        leadLabel: "MEN'S LEAD",
        chaseLabel: "CHASE GROUP 1",
      }),
      parseGpsGroupInstance(asRecord(groupsRecord.FPRO), { id: LEGACY_INSTANCE_IDS.fpro, name: "Women's Group" }, {
        region: fproRegion,
        category: "FPRO",
        leadLabel: "WOMEN'S LEAD",
        chaseLabel: "CHASE GROUP 1",
      }),
    ],
    timers: [
      parseTimerInstance(asRecord(timersRecord.a), { id: LEGACY_INSTANCE_IDS.timerA, name: "Timer A" }, { region: "top", label: "M" }),
      parseTimerInstance(asRecord(timersRecord.b), { id: LEGACY_INSTANCE_IDS.timerB, name: "Timer B" }, { region: "bottom", label: "F" }),
    ],
    pylons: [
      parsePylonInstance(asRecord(pylonsRecord.a), { id: LEGACY_INSTANCE_IDS.pylonA, name: "Pylon A" }, { region: "left" }),
      parsePylonInstance(asRecord(pylonsRecord.b), { id: LEGACY_INSTANCE_IDS.pylonB, name: "Pylon B" }, { region: "right" }),
    ],
    lowerThirds: [
      parseLowerThirdInstance(asRecord(lowerThirdsRecord.primary), { id: LEGACY_INSTANCE_IDS.primary, name: "Primary lower third" }, { region: "left" }),
      parseLowerThirdInstance(asRecord(lowerThirdsRecord.secondary), { id: LEGACY_INSTANCE_IDS.secondary, name: "Secondary lower third" }, { region: "right" }),
    ],
    customLowerThirds: [
      parseCustomLowerThirdInstance(asRecord(lowerThirdsRecord.custom), { id: LEGACY_INSTANCE_IDS.custom, name: "Custom lower third" }, { region: "left" }),
    ],
    // Player cards, scoreboards, mini-maps, full-screen maps, course profiles,
    // speedometers, grade scopes, custom widgets, bugs, compasses, billboards,
    // podiums, image canvases and start lists postdate the legacy shape — nothing to synthesize.
    playerCards: [],
    scoreboards: [],
    miniMaps: [],
    fullMaps: [],
    courseProfiles: [],
    speedometers: [],
    gradeScopes: [],
    customWidgets: [],
    bugs: [],
    compasses: [],
    billboards: [],
    podiums: [],
    imageCanvases: [],
    webSources: [],
    gpsPylons: [],
    sortFilterPylons: [],
    startLists: [],
    tickers: [],
    advancedTimers: [],
  };
}

/** Per-type precedence: `elements.<type>` array when present, else legacy synthesis. */
export function parseOverlayElements(data: DocumentData): OverlayElements {
  const elementsRecord = (data.elements ?? {}) as Record<string, unknown>;
  let legacyCache: OverlayElements | null = null;
  const legacy = (): OverlayElements => (legacyCache ??= legacyOverlayElements(data));

  return {
    gpsGroups: Array.isArray(elementsRecord.gpsGroups)
      ? parseInstanceArray(elementsRecord.gpsGroups, "GPS group", (record, identity) =>
        parseGpsGroupInstance(record, identity, { region: "left", category: "MPRO", leadLabel: "LEAD", chaseLabel: "CHASE" }))
      : legacy().gpsGroups,
    timers: Array.isArray(elementsRecord.timers)
      ? parseInstanceArray(elementsRecord.timers, "Timer", (record, identity) =>
        parseTimerInstance(record, identity, { region: "top" }))
      : legacy().timers,
    pylons: Array.isArray(elementsRecord.pylons)
      ? parseInstanceArray(elementsRecord.pylons, "Pylon", (record, identity) =>
        parsePylonInstance(record, identity, { region: "left" }))
      : legacy().pylons,
    gpsPylons: Array.isArray(elementsRecord.gpsPylons)
      ? parseInstanceArray(elementsRecord.gpsPylons, "GPS Pylon", (record, identity) =>
        parseGpsPylonInstance(record, identity, { region: "left", category: "MPRO" }), overlayInstanceCap("gpsPylon"))
      : [],
    sortFilterPylons: Array.isArray(elementsRecord.sortFilterPylons)
      ? parseInstanceArray(elementsRecord.sortFilterPylons, "Sort & Filter Pylon", (record, identity) =>
        parseSortFilterPylonInstance(record, identity, { region: "left" }), overlayInstanceCap("sortFilterPylon"))
      : [],
    lowerThirds: Array.isArray(elementsRecord.lowerThirds)
      ? parseInstanceArray(elementsRecord.lowerThirds, "Lower third", (record, identity) =>
        parseLowerThirdInstance(record, identity, { region: "left" }))
      : legacy().lowerThirds,
    customLowerThirds: Array.isArray(elementsRecord.customLowerThirds)
      ? parseInstanceArray(elementsRecord.customLowerThirds, "Custom lower third", (record, identity) =>
        parseCustomLowerThirdInstance(record, identity, { region: "left" }))
      : legacy().customLowerThirds,
    playerCards: Array.isArray(elementsRecord.playerCards)
      ? parseInstanceArray(elementsRecord.playerCards, "Player card", (record, identity) =>
        parsePlayerCardInstance(record, identity, { region: "left" }), overlayInstanceCap("playerCard"))
      : [],
    scoreboards: Array.isArray(elementsRecord.scoreboards)
      ? parseInstanceArray(elementsRecord.scoreboards, "Scoreboard", parseScoreboardInstance, overlayInstanceCap("scoreboard"))
      : [],
    miniMaps: Array.isArray(elementsRecord.miniMaps)
      ? parseInstanceArray(elementsRecord.miniMaps, "Mini-map", (record, identity) =>
        parseMiniMapInstance(record, identity, { region: "left" }), overlayInstanceCap("miniMap"))
      : [],
    fullMaps: Array.isArray(elementsRecord.fullMaps)
      ? parseInstanceArray(elementsRecord.fullMaps, "Full-screen map", parseFullMapInstance, overlayInstanceCap("fullMap"))
      : [],
    courseProfiles: Array.isArray(elementsRecord.courseProfiles)
      ? parseInstanceArray(elementsRecord.courseProfiles, "Course profile", parseCourseProfileInstance, overlayInstanceCap("courseProfile"))
      : [],
    speedometers: Array.isArray(elementsRecord.speedometers)
      ? parseInstanceArray(elementsRecord.speedometers, "Speedometer", (record, identity) =>
        parseSpeedometerInstance(record, identity, { region: "left" }), overlayInstanceCap("speedometer"))
      : [],
    gradeScopes: Array.isArray(elementsRecord.gradeScopes)
      ? parseInstanceArray(elementsRecord.gradeScopes, "Grade Scope", (record, identity) =>
        parseGradeScopeInstance(record, identity, { region: "left" }), overlayInstanceCap("gradeScope"))
      : [],
    customWidgets: Array.isArray(elementsRecord.customWidgets)
      ? parseInstanceArray(elementsRecord.customWidgets, "Custom widget", (record, identity) =>
        parseCustomWidgetInstance(record, identity, { region: "left" }), overlayInstanceCap("customWidget"))
      : [],
    bugs: Array.isArray(elementsRecord.bugs)
      ? parseInstanceArray(elementsRecord.bugs, "Bug", (record, identity) =>
        parseBugInstance(record, identity, { region: "top-left" }), overlayInstanceCap("bug"))
      : [],
    compasses: Array.isArray(elementsRecord.compasses)
      ? parseInstanceArray(elementsRecord.compasses, "Compass", (record, identity) =>
        parseCompassInstance(record, identity, { region: "left" }), overlayInstanceCap("compass"))
      : [],
    billboards: Array.isArray(elementsRecord.billboards)
      ? parseInstanceArray(elementsRecord.billboards, "Billboard", parseBillboardInstance, overlayInstanceCap("billboard"))
      : [],
    podiums: Array.isArray(elementsRecord.podiums)
      ? parseInstanceArray(elementsRecord.podiums, "Podium", parsePodiumInstance, overlayInstanceCap("podium"))
      : [],
    imageCanvases: Array.isArray(elementsRecord.imageCanvases)
      ? parseInstanceArray(elementsRecord.imageCanvases, "Image Canvas", parseImageCanvasInstance, overlayInstanceCap("imageCanvas"))
      : [],
    webSources: Array.isArray(elementsRecord.webSources)
      ? parseInstanceArray(elementsRecord.webSources, "Web Source", parseWebSourceInstance, overlayInstanceCap("webSource"))
      : [],
    startLists: Array.isArray(elementsRecord.startLists)
      ? parseInstanceArray(elementsRecord.startLists, "Start List", parseStartListInstance, overlayInstanceCap("startList"))
      : [],
    tickers: Array.isArray(elementsRecord.tickers)
      ? parseInstanceArray(elementsRecord.tickers, "Ticker", parseTickerInstance, overlayInstanceCap("ticker"))
      : [],
    advancedTimers: Array.isArray(elementsRecord.advancedTimers)
      ? parseInstanceArray(elementsRecord.advancedTimers, "Advanced Timer", parseAdvancedTimerInstance, overlayInstanceCap("advancedTimer"))
      : [],
  };
}

// --- Instance serialization -------------------------------------------------------

function instanceBaseFirestoreData(instance: OverlayInstanceBase & { region: string }): Record<string, unknown> {
  return {
    id: instance.id,
    name: instance.name,
    region: instance.region,
    visible: instance.visible,
    transitionDurationMs: normalizeOverlayTransitionDurationMs(instance.transitionDurationMs),
    rootScale: instance.region === "full" ? undefined : normalizeOverlayRootScale(instance.rootScale),
    // Default-true flag stored only when off (compact).
    ...(instance.avoidCollisions ? {} : { avoidCollisions: false }),
  };
}

export function gpsGroupInstanceFirestoreData(instance: OverlayGpsGroupInstance): Record<string, unknown> {
  return compactData({
    ...instanceBaseFirestoreData(instance),
    darkModeOverride: instance.darkModeOverride,
    category: instance.category,
    showChase: instance.showChase,
    showChaseValue: instance.showChaseValue ? true : undefined,
    showDistanceRemaining: instance.showDistanceRemaining,
    showSpeed: instance.showSpeed ? true : undefined,
    showGrade: instance.showGrade ? true : undefined,
    showPace: instance.showPace ? true : undefined,
    speedUnit: instance.speedUnit === "kmh" ? "kmh" : undefined,
    leadLabel: instance.leadLabel,
    chaseLabel: instance.chaseLabel,
    leadScale: instance.leadScale === 1 ? undefined : instance.leadScale,
    chaseScale: instance.chaseScale === 1 ? undefined : instance.chaseScale,
    leadWidth: instance.leadWidth,
    chaseWidth: instance.chaseWidth,
    lockChaseWidth: instance.lockChaseWidth ? true : undefined,
    // Unlike design.skin, a per-instance "default" is a real override and is written as-is; only unset folds away.
    skin: instance.skin,
    customSkin: instance.customSkin,
    material: instance.material,
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(instance.materialTextureOpacities),
    offsets: groupOffsetsFirestoreData(instance.offsets),
  });
}

export function timerInstanceFirestoreData(instance: OverlayTimerInstance): Record<string, unknown> {
  return compactData({
    ...instanceBaseFirestoreData(instance),
    label: instance.label,
    direction: instance.direction === "down" ? "down" : undefined,
    running: instance.running ? true : undefined,
    startedAt: instance.startedAt ?? undefined,
    offsetMs: instance.offsetMs > 0 ? instance.offsetMs : undefined,
    durationMs: instance.durationMs > 0 ? instance.durationMs : undefined,
    darkModeOverride: instance.darkModeOverride,
    skin: instance.skin,
    customSkin: instance.customSkin,
    material: instance.material,
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(instance.materialTextureOpacities),
    offsets: lowerThirdOffsetsFirestoreData(instance.offsets),
  });
}

export function pylonInstanceFirestoreData(instance: OverlayPylonInstance, athleteLimit = PYLON_ROW_LIMIT): Record<string, unknown> {
  return compactData({
    ...instanceBaseFirestoreData(instance),
    label: instance.label,
    // Default-true flag stored only when off (compact).
    showRank: instance.showRank ? undefined : false,
    useFlag: instance.useFlag === false ? false : undefined,
    useBib: instance.useBib === false ? false : undefined,
    useShortName: instance.useShortName ? true : undefined,
    anchorBottom: instance.anchorBottom ? true : undefined,
    widthMode: instance.widthMode === "fit" ? "fit" : undefined,
    shrinkTextToFit: instance.shrinkTextToFit ? true : undefined,
    darkModeOverride: instance.darkModeOverride,
    skin: instance.skin,
    customSkin: instance.customSkin,
    material: instance.material,
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(instance.materialTextureOpacities),
    maxWidth: instance.maxWidth,
    maxHeight: instance.maxHeight,
    scale: instance.scale === 1 ? undefined : instance.scale,
    offsets: lowerThirdOffsetsFirestoreData(instance.offsets),
    sourceID: instance.sourceID,
    rows: capPylonRows(instance.rows, athleteLimit).map(pylonRowFirestoreData),
  });
}

export function sortFilterPylonInstanceFirestoreData(instance: OverlaySortFilterPylonInstance): Record<string, unknown> {
  return compactData({
    ...pylonInstanceFirestoreData(instance, SORT_FILTER_PYLON_ROW_LIMIT),
    sortFieldKey: instance.sortFieldKey,
    // Defaults folded away (compact): "asc" direction and the default row count.
    sortDirection: instance.sortDirection === "desc" ? "desc" : undefined,
    filterFieldKey: instance.filterFieldKey,
    filterValue: instance.filterValue,
    rowCount: instance.rowCount === SORT_FILTER_PYLON_DEFAULT_ROW_COUNT ? undefined : instance.rowCount,
    pageIndex: normalizeSortFilterPylonPageIndex(instance.pageIndex) || undefined,
    showSortValue: instance.showSortValue ? true : undefined,
  });
}

export function gpsPylonInstanceFirestoreData(instance: OverlayGpsPylonInstance): Record<string, unknown> {
  return compactData({
    ...instanceBaseFirestoreData(instance),
    label: instance.label,
    category: instance.category,
    headerReadout: instance.headerReadout,
    speedUnit: instance.speedUnit === "kmh" ? "kmh" : undefined,
    // Default-true flag stored only when off (compact).
    showRank: instance.showRank ? undefined : false,
    useFlag: instance.useFlag === false ? false : undefined,
    useBib: instance.useBib === false ? false : undefined,
    useShortName: instance.useShortName ? true : undefined,
    anchorBottom: instance.anchorBottom ? true : undefined,
    widthMode: instance.widthMode === "fit" ? "fit" : undefined,
    shrinkTextToFit: instance.shrinkTextToFit ? true : undefined,
    darkModeOverride: instance.darkModeOverride,
    skin: instance.skin,
    customSkin: instance.customSkin,
    material: instance.material,
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(instance.materialTextureOpacities),
    maxWidth: instance.maxWidth,
    maxHeight: instance.maxHeight,
    scale: instance.scale === 1 ? undefined : instance.scale,
    offsets: lowerThirdOffsetsFirestoreData(instance.offsets),
    sourceID: instance.sourceID,
    rows: capPylonRows(instance.rows).map(pylonRowFirestoreData),
  });
}

export function lowerThirdInstanceFirestoreData(instance: OverlayLowerThirdInstance): Record<string, unknown> {
  return compactData({
    ...instanceBaseFirestoreData(instance),
    mode: instance.mode ?? "data",
    selectedRowID: instance.selectedRowID,
    rows: (instance.rows ?? []).slice(0, CUSTOM_LOWER_THIRD_ROW_LIMIT).map((row) => ({ id: row.id, name: row.name, accentText: row.accentText })),
    fitLayoutReferenceLeft: normalizeFitLayoutReferenceLeft(instance.fitLayoutReferenceLeft),
    useFlag: instance.useFlag,
    useBib: instance.useBib,
    showExtraLabel: instance.showExtraLabel ? true : undefined,
    useShortName: instance.useShortName ? true : undefined,
    widthMode: instance.widthMode === "fit" ? "fit" : undefined,
    shrinkTextToFit: instance.shrinkTextToFit ? true : undefined,
    darkModeOverride: instance.darkModeOverride,
    skin: instance.skin,
    customSkin: instance.customSkin,
    material: instance.material,
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(instance.materialTextureOpacities),
    maxWidth: instance.maxWidth,
    maxHeight: instance.maxHeight,
    scale: instance.scale === 1 ? undefined : instance.scale,
    offsets: lowerThirdOffsetsFirestoreData(instance.offsets),
    logo: lowerThirdLogoFirestoreData(instance.logo),
    sourceID: instance.sourceID,
    recordKey: instance.recordKey,
    person: instance.person ? compactData({ ...instance.person }) : null,
  });
}

export function playerCardInstanceFirestoreData(instance: OverlayPlayerCardInstance): Record<string, unknown> {
  const photoPosition = instance.photoPosition.x === DEFAULT_PLAYER_CARD_PHOTO_POSITION.x
    && instance.photoPosition.y === DEFAULT_PLAYER_CARD_PHOTO_POSITION.y
    ? undefined
    : { x: instance.photoPosition.x, y: instance.photoPosition.y };
  const photoBackground = instance.photoBackground.source === DEFAULT_PLAYER_CARD_PHOTO_BACKGROUND.source
    && instance.photoBackground.hex === DEFAULT_PLAYER_CARD_PHOTO_BACKGROUND.hex
    && !instance.photoBackground.secondaryHex
    ? undefined
    : colorRefFirestoreData(instance.photoBackground);
  return compactData({
    ...instanceBaseFirestoreData(instance),
    useFlag: instance.useFlag,
    useBib: instance.useBib,
    showExtraLabel: instance.showExtraLabel ? true : undefined,
    useShortName: instance.useShortName ? true : undefined,
    darkModeOverride: instance.darkModeOverride,
    skin: instance.skin,
    customSkin: instance.customSkin,
    material: instance.material,
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(instance.materialTextureOpacities),
    photoFit: instance.photoFit === DEFAULT_PLAYER_CARD_PHOTO_FIT ? undefined : instance.photoFit,
    photoPosition,
    photoBackground,
    photoBackgroundOpacity:
      instance.photoBackgroundOpacity === DEFAULT_PLAYER_CARD_PHOTO_BACKGROUND_OPACITY
        ? undefined
        : instance.photoBackgroundOpacity,
    maxWidth: instance.maxWidth,
    maxHeight: instance.maxHeight,
    scale: instance.scale === 1 ? undefined : instance.scale,
    offsets: lowerThirdOffsetsFirestoreData(instance.offsets),
    sourceID: instance.sourceID,
    recordKey: instance.recordKey,
    person: instance.person ? compactData({ ...instance.person }) : null,
  });
}

export function customLowerThirdInstanceFirestoreData(instance: OverlayCustomLowerThirdInstance): Record<string, unknown> {
  return compactData({
    ...instanceBaseFirestoreData(instance),
    fitLayoutReferenceLeft: normalizeFitLayoutReferenceLeft(instance.fitLayoutReferenceLeft),
    widthMode: instance.widthMode === "fit" ? "fit" : undefined,
    shrinkTextToFit: instance.shrinkTextToFit ? true : undefined,
    darkModeOverride: instance.darkModeOverride,
    skin: instance.skin,
    customSkin: instance.customSkin,
    material: instance.material,
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(instance.materialTextureOpacities),
    maxWidth: instance.maxWidth,
    maxHeight: instance.maxHeight,
    scale: instance.scale === 1 ? undefined : instance.scale,
    offsets: lowerThirdOffsetsFirestoreData(instance.offsets),
    logo: lowerThirdLogoFirestoreData(instance.logo),
    selectedRowID: instance.selectedRowID,
    rows: instance.rows.slice(0, CUSTOM_LOWER_THIRD_ROW_LIMIT).map((row) => ({
      id: row.id,
      name: row.name,
      accentText: row.accentText,
    })),
  });
}

export function scoreboardInstanceFirestoreData(instance: OverlayScoreboardInstance): Record<string, unknown> {
  return compactData({
    ...instanceBaseFirestoreData(instance),
    team1Name: instance.team1Name,
    team2Name: instance.team2Name,
    team1Color: colorRefFirestoreData(instance.team1Color),
    team2Color: colorRefFirestoreData(instance.team2Color),
    // Scores are stored unconditionally (0 included) — they are live state.
    score1: Math.max(0, Math.round(instance.score1)),
    score2: Math.max(0, Math.round(instance.score2)),
    darkModeOverride: instance.darkModeOverride,
    skin: instance.skin,
    customSkin: instance.customSkin,
    material: instance.material,
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(instance.materialTextureOpacities),
    offsets: groupOffsetsFirestoreData(instance.offsets),
  });
}

export function miniMapInstanceFirestoreData(instance: OverlayMiniMapInstance): Record<string, unknown> {
  return compactData({
    ...instanceBaseFirestoreData(instance),
    deviceID: instance.deviceID || undefined,
    zoomSpan: instance.zoomSpan,
    diameter: instance.diameter,
    ringColor: colorRefFirestoreData(instance.ringColor),
    bibText: instance.bibText || undefined,
    labelText: instance.labelText || undefined,
    showSpeed: instance.showSpeed ? true : undefined,
    speedUnit: instance.speedUnit === "kmh" ? "kmh" : undefined,
    offsets: groupOffsetsFirestoreData(instance.offsets),
  });
}

export function fullMapInstanceFirestoreData(instance: OverlayFullMapInstance): Record<string, unknown> {
  return compactData({
    ...instanceBaseFirestoreData(instance),
    framing: instance.framing === "course" ? "course" : undefined,
    deviceID: instance.deviceID || undefined,
    zoomSpan: instance.zoomSpan,
    mapType: instance.mapType === "mutedStandard" ? undefined : instance.mapType,
    colorScheme: instance.colorScheme === "light" ? "light" : undefined,
    tintColor: colorRefFirestoreData(instance.tintColor),
    rotation: instance.rotation > 0 ? Math.round(instance.rotation) : undefined,
    courseLineColor: colorRefFirestoreData(instance.courseLineColor),
  });
}

function courseProfileTrackFirestoreData(track: OverlayCourseProfileTrack): Record<string, unknown> {
  return compactData({
    id: track.id,
    deviceID: track.deviceID || undefined,
    color: colorRefFirestoreData(track.color),
    label: track.label || undefined,
  });
}

export function courseProfileInstanceFirestoreData(instance: OverlayCourseProfileInstance): Record<string, unknown> {
  return compactData({
    ...instanceBaseFirestoreData(instance),
    width: instance.width,
    height: instance.height,
    baseColor: colorRefFirestoreData(instance.baseColor),
    baseOpacity: instance.baseOpacity,
    // Tracks are live state — stored unconditionally.
    tracks: instance.tracks.slice(0, COURSE_PROFILE_TRACK_LIMIT).map(courseProfileTrackFirestoreData),
    offsets: groupOffsetsFirestoreData(instance.offsets),
  });
}

export function speedometerInstanceFirestoreData(instance: OverlaySpeedometerInstance): Record<string, unknown> {
  return compactData({
    ...instanceBaseFirestoreData(instance),
    deviceID: instance.deviceID || undefined,
    displayMode: instance.displayMode === "digital" ? "digital" : undefined,
    diameter: instance.diameter,
    maxSpeed: instance.maxSpeed,
    ringColor: colorRefFirestoreData(instance.ringColor),
    labelText: instance.labelText || undefined,
    speedUnit: instance.speedUnit === "kmh" ? "kmh" : undefined,
    offsets: groupOffsetsFirestoreData(instance.offsets),
  });
}

export function compassInstanceFirestoreData(instance: OverlayCompassInstance): Record<string, unknown> {
  return compactData({
    ...instanceBaseFirestoreData(instance),
    deviceID: instance.deviceID || undefined,
    diameter: instance.diameter,
    ringColor: colorRefFirestoreData(instance.ringColor),
    // Default-true flag stored only when off (compact).
    showReadout: instance.showReadout ? undefined : false,
    labelText: instance.labelText || undefined,
    offsets: groupOffsetsFirestoreData(instance.offsets),
  });
}

export function gradeScopeInstanceFirestoreData(instance: OverlayGradeScopeInstance): Record<string, unknown> {
  return compactData({
    ...instanceBaseFirestoreData(instance),
    deviceID: instance.deviceID || undefined,
    diameter: instance.diameter,
    tiltGain: instance.tiltGain,
    ringColor: colorRefFirestoreData(instance.ringColor),
    labelText: instance.labelText || undefined,
    offsets: groupOffsetsFirestoreData(instance.offsets),
  });
}

/** Serialized verbatim post-sanitize (drop undefined optionals via JSON round-trip semantics). */
function customWidgetSpecFirestoreData(spec: OverlayCustomWidgetSpec): Record<string, unknown> {
  return JSON.parse(JSON.stringify(spec)) as Record<string, unknown>;
}

export function customWidgetInstanceFirestoreData(instance: OverlayCustomWidgetInstance): Record<string, unknown> {
  return compactData({
    ...instanceBaseFirestoreData(instance),
    // Spec and values are live state — stored unconditionally.
    spec: customWidgetSpecFirestoreData(instance.spec),
    values: { ...instance.values },
    offsets: groupOffsetsFirestoreData(instance.offsets),
  });
}

export function bugInstanceFirestoreData(instance: OverlayBugInstance): Record<string, unknown> {
  return compactData({
    ...instanceBaseFirestoreData(instance),
    mode: instance.mode ?? "manual",
    sourceID: instance.sourceID,
    logoField: instance.logoField,
    accentField: instance.accentField,
    selectedRowIndices: instance.selectedRowIndices ?? [],
    dataRows: (instance.dataRows ?? []).slice(0, BUG_ROW_LIMIT).map((row) => compactData({ id: row.id, url: row.url, accentHex: row.accentHex })),
    rotationIntervalSeconds: clampNumber(instance.rotationIntervalSeconds, BUG_ROTATION_SECONDS_MIN, BUG_ROTATION_SECONDS_MAX),
    rows: instance.rows.slice(0, BUG_ROW_LIMIT).map((row) => compactData({
      id: row.id,
      url: row.url,
      accentHex: row.accentHex,
    })),
    offsets: groupOffsetsFirestoreData(instance.offsets),
  });
}

export function billboardInstanceFirestoreData(instance: OverlayBillboardInstance): Record<string, unknown> {
  return compactData({
    ...instanceBaseFirestoreData(instance),
    // Default mode folds away (compact).
    mode: instance.mode === "rotate" ? undefined : instance.mode,
    rotationIntervalSeconds: clampNumber(instance.rotationIntervalSeconds, BUG_ROTATION_SECONDS_MIN, BUG_ROTATION_SECONDS_MAX),
    activeRowId: instance.activeRowId,
    rows: instance.rows.slice(0, BILLBOARD_ROW_LIMIT).map((row) => compactData({
      id: row.id,
      url: row.url,
      message: row.message || undefined,
      backgroundHex: row.backgroundHex,
      // Fully opaque folds away (playerCard photoBackgroundOpacity precedent).
      backgroundOpacity: row.backgroundOpacity === 1 ? undefined : row.backgroundOpacity,
      accentHex: row.accentHex,
    })),
    scale: instance.scale === 1 ? undefined : instance.scale,
    skin: instance.skin,
    customSkin: instance.customSkin,
    material: instance.material,
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(instance.materialTextureOpacities),
    offsets: groupOffsetsFirestoreData(instance.offsets),
  });
}

export function startListInstanceFirestoreData(instance: OverlayStartListInstance): Record<string, unknown> {
  return compactData({
    ...instanceBaseFirestoreData(instance),
    sourceID: instance.sourceID,
    sortFieldKey: instance.sortFieldKey,
    // Defaults folded away (compact): "asc", 3 columns, the default rows-per-column, page 0, competition logo.
    sortDirection: instance.sortDirection === "desc" ? "desc" : undefined,
    filterFieldKey: instance.filterFieldKey,
    filterValue: instance.filterValue,
    columns: instance.columns === 2 ? 2 : undefined,
    rowsPerColumn: instance.rowsPerColumn === START_LIST_DEFAULT_ROWS_PER_COLUMN ? undefined : instance.rowsPerColumn,
    pageIndex: normalizeSortFilterPylonPageIndex(instance.pageIndex) || undefined,
    rows: pylonAthleteRows(instance.rows).slice(0, START_LIST_ROW_LIMIT).map(pylonRowFirestoreData),
    label: instance.label,
    logoMode: instance.logoMode === "competition" ? undefined : instance.logoMode,
    customLogoURL: instance.customLogoURL,
    customLogoDarkURL: instance.customLogoDarkURL,
    logoHeight: instance.logoHeight,
    // Default-true flags stored only when off (compact).
    useFlag: instance.useFlag === false ? false : undefined,
    useBib: instance.useBib === false ? false : undefined,
    useShortName: instance.useShortName ? true : undefined,
    shrinkTextToFit: instance.shrinkTextToFit ? true : undefined,
    darkModeOverride: instance.darkModeOverride,
    skin: instance.skin,
    customSkin: instance.customSkin,
    material: instance.material,
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(instance.materialTextureOpacities),
    scale: instance.scale === 1 ? undefined : instance.scale,
    maxWidth: instance.maxWidth,
    maxHeight: instance.maxHeight,
    offsets: lowerThirdOffsetsFirestoreData(instance.offsets),
  });
}

export function webSourceInstanceFirestoreData(instance: OverlayWebSourceInstance): Record<string, unknown> {
  const url = normalizeWebSourceURL(instance.url);
  if (instance.url.trim() && !url) {
    throw new Error("Web Source URL must be a valid HTTPS URL without embedded credentials (maximum 8192 characters).");
  }
  const width = parseMaxSizePx(instance.canvasWidth, WEB_SOURCE_SIZE_MIN, WEB_SOURCE_WIDTH_MAX) ?? DEFAULT_WEB_SOURCE_WIDTH;
  const height = parseMaxSizePx(instance.canvasHeight, WEB_SOURCE_SIZE_MIN, WEB_SOURCE_HEIGHT_MAX) ?? DEFAULT_WEB_SOURCE_HEIGHT;
  return compactData({
    ...instanceBaseFirestoreData(instance),
    region: "canvas",
    url: url || undefined,
    canvasWidth: width === DEFAULT_WEB_SOURCE_WIDTH ? undefined : width,
    canvasHeight: height === DEFAULT_WEB_SOURCE_HEIGHT ? undefined : height,
    unloadWhenHidden: instance.unloadWhenHidden === false ? false : undefined,
    reloadToken: instance.reloadToken.slice(0, 80) || undefined,
    scale: instance.scale === 1 ? undefined : instance.scale,
    offsets: groupOffsetsFirestoreData(instance.offsets),
  });
}

export function advancedTimerInstanceFirestoreData(instance: OverlayAdvancedTimerInstance): Record<string, unknown> {
  return compactData({
    ...instanceBaseFirestoreData(instance),
    label: instance.label,
    mode: instance.mode === "countup" ? "countup" : undefined,
    targetAt: instance.targetAt ?? undefined,
    timeZone: isValidTimeZone(instance.timeZone) ? instance.timeZone : "UTC",
    darkModeOverride: instance.darkModeOverride,
    skin: instance.skin,
    customSkin: instance.customSkin,
    material: instance.material,
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(instance.materialTextureOpacities),
    offsets: lowerThirdOffsetsFirestoreData(instance.offsets),
  });
}

export function tickerInstanceFirestoreData(instance: OverlayTickerInstance): Record<string, unknown> {
  const speed = parseMaxSizePx(instance.speed, TICKER_SPEED_MIN, TICKER_SPEED_MAX) ?? DEFAULT_TICKER_SPEED;
  const barWidth = parseMaxSizePx(instance.barWidth, TICKER_WIDTH_MIN, TICKER_WIDTH_MAX) ?? DEFAULT_TICKER_WIDTH;
  const fontSize = parseMaxSizePx(instance.fontSize, TICKER_FONT_SIZE_MIN, TICKER_FONT_SIZE_MAX) ?? DEFAULT_TICKER_FONT_SIZE;
  return compactData({
    ...instanceBaseFirestoreData(instance),
    messages: instance.messages.slice(0, TICKER_MESSAGE_LIMIT).map(({ id, text }) => ({ id, text: text.slice(0, TICKER_MESSAGE_MAX_LENGTH) })),
    divider: instance.divider === "bullet" ? undefined : instance.divider,
    dividerImageURL: parseHttpsImageURL(instance.dividerImageURL),
    direction: instance.direction === "ltr" ? "ltr" : undefined,
    speed: speed === DEFAULT_TICKER_SPEED ? undefined : speed,
    barWidth: barWidth === DEFAULT_TICKER_WIDTH ? undefined : barWidth,
    fontSize: fontSize === DEFAULT_TICKER_FONT_SIZE ? undefined : fontSize,
    darkModeOverride: instance.darkModeOverride,
    material: instance.material,
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(instance.materialTextureOpacities),
    offsets: groupOffsetsFirestoreData(instance.offsets),
  });
}

export function imageCanvasInstanceFirestoreData(instance: OverlayImageCanvasInstance): Record<string, unknown> {
  const url = instance.url.trim();
  const photoPosition = instance.photoPosition.x === DEFAULT_IMAGE_CANVAS_PHOTO_POSITION.x
    && instance.photoPosition.y === DEFAULT_IMAGE_CANVAS_PHOTO_POSITION.y
    ? undefined
    : { x: instance.photoPosition.x, y: instance.photoPosition.y };
  return compactData({
    ...instanceBaseFirestoreData(instance),
    // Re-gated so a non-https draft never persists (parse would blank it anyway).
    url: BUG_ROW_URL_PATTERN.test(url) ? url : undefined,
    // The stage-sized default folds away (compact).
    canvasWidth: instance.canvasWidth === DEFAULT_IMAGE_CANVAS_WIDTH
      ? undefined
      : clampNumber(Math.round(instance.canvasWidth), IMAGE_CANVAS_SIZE_MIN, IMAGE_CANVAS_WIDTH_MAX),
    canvasHeight: instance.canvasHeight === DEFAULT_IMAGE_CANVAS_HEIGHT
      ? undefined
      : clampNumber(Math.round(instance.canvasHeight), IMAGE_CANVAS_SIZE_MIN, IMAGE_CANVAS_HEIGHT_MAX),
    background: instance.background ? colorRefFirestoreData(instance.background) : undefined,
    backgroundOpacity: instance.backgroundOpacity === DEFAULT_IMAGE_CANVAS_BACKGROUND_OPACITY ? undefined : instance.backgroundOpacity,
    cornerRadius: instance.cornerRadius || undefined,
    photoFit: instance.photoFit === DEFAULT_IMAGE_CANVAS_PHOTO_FIT ? undefined : instance.photoFit,
    photoPosition,
    scale: instance.scale === 1 ? undefined : instance.scale,
    offsets: groupOffsetsFirestoreData(instance.offsets),
  });
}

export function podiumInstanceFirestoreData(instance: OverlayPodiumInstance): Record<string, unknown> {
  return compactData({
    ...instanceBaseFirestoreData(instance),
    label: instance.label,
    // Default depth (3) folds away (compact).
    depth: instance.depth === 5 ? 5 : undefined,
    showPlace: instance.showPlace ? true : undefined,
    useFlag: instance.useFlag === false ? false : undefined,
    useBib: instance.useBib === false ? false : undefined,
    useShortName: instance.useShortName ? true : undefined,
    shrinkTextToFit: instance.shrinkTextToFit ? true : undefined,
    darkModeOverride: instance.darkModeOverride,
    skin: instance.skin,
    customSkin: instance.customSkin,
    material: instance.material,
    materialTextureOpacities: parseOverlayMaterialTextureOpacities(instance.materialTextureOpacities),
    barMaxWidth: instance.barMaxWidth,
    maxWidth: instance.maxWidth,
    maxHeight: instance.maxHeight,
    scale: instance.scale === 1 ? undefined : instance.scale,
    offsets: lowerThirdOffsetsFirestoreData(instance.offsets),
    sourceID: instance.sourceID,
    slots: normalizePodiumSlots(instance.slots).map(podiumSlotFirestoreData),
  });
}

export function overlayElementsFirestoreData(elements: OverlayElements): Record<string, unknown> {
  return {
    gpsGroups: elements.gpsGroups.slice(0, OVERLAY_INSTANCE_CAP).map(gpsGroupInstanceFirestoreData),
    timers: elements.timers.slice(0, OVERLAY_INSTANCE_CAP).map(timerInstanceFirestoreData),
    pylons: elements.pylons.slice(0, OVERLAY_INSTANCE_CAP).map((instance) => pylonInstanceFirestoreData(instance)),
    gpsPylons: elements.gpsPylons.slice(0, overlayInstanceCap("gpsPylon")).map(gpsPylonInstanceFirestoreData),
    sortFilterPylons: elements.sortFilterPylons.slice(0, overlayInstanceCap("sortFilterPylon")).map(sortFilterPylonInstanceFirestoreData),
    lowerThirds: elements.lowerThirds.slice(0, OVERLAY_INSTANCE_CAP).map(lowerThirdInstanceFirestoreData),
    customLowerThirds: elements.customLowerThirds.slice(0, OVERLAY_INSTANCE_CAP).map(customLowerThirdInstanceFirestoreData),
    playerCards: elements.playerCards.slice(0, overlayInstanceCap("playerCard")).map(playerCardInstanceFirestoreData),
    scoreboards: elements.scoreboards.slice(0, overlayInstanceCap("scoreboard")).map(scoreboardInstanceFirestoreData),
    miniMaps: elements.miniMaps.slice(0, overlayInstanceCap("miniMap")).map(miniMapInstanceFirestoreData),
    fullMaps: elements.fullMaps.slice(0, overlayInstanceCap("fullMap")).map(fullMapInstanceFirestoreData),
    courseProfiles: elements.courseProfiles.slice(0, overlayInstanceCap("courseProfile")).map(courseProfileInstanceFirestoreData),
    speedometers: elements.speedometers.slice(0, overlayInstanceCap("speedometer")).map(speedometerInstanceFirestoreData),
    gradeScopes: elements.gradeScopes.slice(0, overlayInstanceCap("gradeScope")).map(gradeScopeInstanceFirestoreData),
    customWidgets: elements.customWidgets.slice(0, overlayInstanceCap("customWidget")).map(customWidgetInstanceFirestoreData),
    bugs: elements.bugs.slice(0, overlayInstanceCap("bug")).map(bugInstanceFirestoreData),
    compasses: elements.compasses.slice(0, overlayInstanceCap("compass")).map(compassInstanceFirestoreData),
    billboards: elements.billboards.slice(0, overlayInstanceCap("billboard")).map(billboardInstanceFirestoreData),
    podiums: elements.podiums.slice(0, overlayInstanceCap("podium")).map(podiumInstanceFirestoreData),
    imageCanvases: elements.imageCanvases.slice(0, overlayInstanceCap("imageCanvas")).map(imageCanvasInstanceFirestoreData),
    webSources: elements.webSources.slice(0, overlayInstanceCap("webSource")).map(webSourceInstanceFirestoreData),
    startLists: elements.startLists.slice(0, overlayInstanceCap("startList")).map(startListInstanceFirestoreData),
    tickers: elements.tickers.slice(0, overlayInstanceCap("ticker")).map(tickerInstanceFirestoreData),
    advancedTimers: elements.advancedTimers.slice(0, overlayInstanceCap("advancedTimer")).map(advancedTimerInstanceFirestoreData),
  };
}

// --- Config parse/serialize ---------------------------------------------------------

/** Competition logo pair stored on the overlay doc; either side may be absent. */
export interface OverlayEventLogo {
  url?: string;
  darkURL?: string;
}

export function parseOverlayEventLogo(raw: unknown): OverlayEventLogo | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const url = parseHttpsImageURL(record.url);
  const darkURL = parseHttpsImageURL(record.darkURL);
  return url || darkURL ? { url, darkURL } : null;
}

export function overlayEventLogoFirestoreData(logo: OverlayEventLogo | null | undefined): Record<string, unknown> | null {
  const parsed = parseOverlayEventLogo(logo);
  return parsed ? compactData({ url: parsed.url, darkURL: parsed.darkURL }) : null;
}

/** Structural equality on the stored pair — the designer's sync guard. */
export function overlayEventLogoEqual(a: OverlayEventLogo | null | undefined, b: OverlayEventLogo | null | undefined): boolean {
  const left = parseOverlayEventLogo(a);
  const right = parseOverlayEventLogo(b);
  if (!left || !right) return !left && !right;
  return left.url === right.url && left.darkURL === right.darkURL;
}

/** The logo a themed element should show: dark mode prefers the dark variant, light mode the light one; each falls back to the other. */
export function overlayEventLogoURL(logo: OverlayEventLogo | null | undefined, darkMode: boolean): string | undefined {
  if (!logo) return undefined;
  return darkMode ? logo.darkURL ?? logo.url : logo.url ?? logo.darkURL;
}

export function parseOverlayConfig(id: string, data: DocumentData): OverlayConfig | null {
  const leagueID = stringValue(data.leagueID);
  const competitionInstanceID = stringValue(data.competitionInstanceID);
  const name = stringValue(data.name);
  const design = data.design as Record<string, unknown> | undefined;
  const textColor = parseColorRef(design?.textColor);
  const accentColor = parseColorRef(design?.accentColor);
  if (!leagueID || !competitionInstanceID || !name || !textColor || !accentColor) return null;

  const fontRecord = (design?.font ?? {}) as Record<string, unknown>;

  let courseKML: OverlayCourseKMLRef | null = null;
  if (data.courseKML && typeof data.courseKML === "object") {
    const record = data.courseKML as Record<string, unknown>;
    const role = stringValue(record.role);
    const url = stringValue(record.url);
    if (role && isCourseKMLRole(role) && url) {
      courseKML = { role: role as CourseKMLRole, name: stringValue(record.name) ?? role, url };
    }
  }

  return {
    id,
    leagueID,
    competitionInstanceID,
    name,
    courseKML,
    eventLogo: parseOverlayEventLogo(data.eventLogo),
    skinSnapshots: parseStoredSkinSnapshots(data.skinSnapshots),
    skinContractVersion: data.skinContractVersion === 1 ? 1 : undefined,
    gpsFocus: parseGPSFocusOption(data.gpsFocus),
    gpsRelockNonce: finiteNumber(data.gpsRelockNonce) ?? 0,
    design: {
      textColor,
      accentColor,
      font: {
        googleFamily: stringValue(fontRecord.googleFamily),
        customFontURL: stringValue(fontRecord.customFontURL),
        customFontName: stringValue(fontRecord.customFontName),
      },
      autoCapitalizePersonText: design?.autoCapitalizePersonText === true,
      skin: parseOverlaySkin(design?.skin),
      customSkin: parseCustomSkinReference(design?.customSkin),
      material: parseOverlayMaterial(design?.material),
      materialTextureOpacities: parseOverlayMaterialTextureOpacities(design?.materialTextureOpacities),
      darkMode: design?.darkMode === true,
    },
    elements: parseOverlayElements(data),
    controlGroups: parseControlGroups(data.controlGroups),
    railTypeGroups: parseRailTypeGroups(data.railTypeGroups),
    programRefresh: parseOverlayProgramRefresh(data.programRefresh),
    createdByUserID: stringValue(data.createdByUserID),
    createdAt: dateValue(data.createdAt),
    updatedAt: dateValue(data.updatedAt),
  };
}

export function overlayConfigFirestoreData(config: OverlayConfig): Record<string, unknown> {
  return compactData({
    skinContractWrittenAt: serverTimestamp(),
    skinSnapshots: config.skinContractVersion === 1 ? skinSnapshotsFirestoreData(config.skinSnapshots ?? {}) : undefined,
    skinContractVersion: config.skinContractVersion,
    id: config.id,
    leagueID: config.leagueID,
    competitionInstanceID: config.competitionInstanceID,
    name: config.name,
    courseKML: config.courseKML ? { role: config.courseKML.role, name: config.courseKML.name, url: config.courseKML.url } : null,
    eventLogo: overlayEventLogoFirestoreData(config.eventLogo),
    gpsFocus: config.gpsFocus,
    gpsRelockNonce: config.gpsRelockNonce,
    design: compactData({
      textColor: colorRefFirestoreData(config.design.textColor),
      accentColor: colorRefFirestoreData(config.design.accentColor),
      font: compactData({ ...config.design.font }),
      autoCapitalizePersonText: config.design.autoCapitalizePersonText ? true : undefined,
      skin: config.design.skin === DEFAULT_OVERLAY_SKIN ? undefined : config.design.skin,
      customSkin: config.design.customSkin,
      material: config.design.material === DEFAULT_OVERLAY_MATERIAL ? undefined : config.design.material,
      materialTextureOpacities: parseOverlayMaterialTextureOpacities(config.design.materialTextureOpacities),
      darkMode: config.design.darkMode ? true : undefined,
    }),
    elements: overlayElementsFirestoreData(config.elements),
    controlGroups: controlGroupsFirestoreData(config.controlGroups),
    railTypeGroups: railTypeGroupsFirestoreData(config.railTypeGroups),
    programRefresh: config.programRefresh,
    createdByUserID: config.createdByUserID,
    createdAt: config.createdAt,
    updatedAt: config.updatedAt,
  });
}

// --- Designer factories ---------------------------------------------------------------

export function makeOverlayInstanceID(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `inst-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Deep copy of an instance under a fresh id/name, keeping every configured
 * parameter (offsets, colors, rows, tracks, labels, device ids, widget
 * spec/values). Only on-air state resets: the copy starts hidden and a timer's
 * run state clears. Row/layer/track ids are scoped to the instance, so they
 * carry over unchanged.
 */
export function duplicateOverlayInstance(
  type: OverlayElementType,
  source: OverlayElementInstance,
  id: string,
  name: string,
): OverlayElementInstance {
  const copy = structuredClone(source);
  copy.id = id;
  copy.name = name;
  copy.visible = false;
  if (type === "webSource") (copy as OverlayWebSourceInstance).reloadToken = "";
  if (type === "timer") {
    const timer = copy as OverlayTimerInstance;
    timer.running = false;
    timer.startedAt = null;
    timer.offsetMs = 0;
  }
  return copy;
}

export function newGpsGroupInstance(id: string, name: string): OverlayGpsGroupInstance {
  return {
    id,
    name,
    visible: false,
    avoidCollisions: true,
    region: "left",
    category: "MPRO",
    darkModeOverride: undefined,
    showChase: false,
    showDistanceRemaining: false,
    showSpeed: false,
    showGrade: false,
    showPace: false,
    speedUnit: "mph",
    leadLabel: "LEAD",
    chaseLabel: "CHASE GROUP 1",
    offsets: defaultGroupOffsets(),
  };
}

export function newTimerInstance(id: string, name: string): OverlayTimerInstance {
  return {
    id,
    name,
    visible: false,
    avoidCollisions: true,
    region: "top",
    direction: "up",
    running: false,
    startedAt: null,
    offsetMs: 0,
    durationMs: 0,
    darkModeOverride: undefined,
    offsets: defaultLowerThirdOffsets(),
  };
}

export function newPylonInstance(id: string, name: string): OverlayPylonInstance {
  return {
    id,
    name,
    visible: false,
    avoidCollisions: true,
    region: "left",
    showRank: true,
    useFlag: true,
    useBib: true,
    useShortName: false,
    anchorBottom: false,
    widthMode: "fixed",
    shrinkTextToFit: false,
    darkModeOverride: undefined,
    offsets: defaultLowerThirdOffsets(),
    rows: [],
  };
}

export function newGpsPylonInstance(id: string, name: string): OverlayGpsPylonInstance {
  return {
    id,
    name,
    visible: false,
    avoidCollisions: true,
    region: "left",
    category: "MPRO",
    speedUnit: "mph",
    showRank: true,
    useFlag: true,
    useBib: true,
    useShortName: false,
    anchorBottom: false,
    widthMode: "fixed",
    shrinkTextToFit: false,
    darkModeOverride: undefined,
    offsets: defaultLowerThirdOffsets(),
    rows: [],
  };
}

export function newSortFilterPylonInstance(id: string, name: string): OverlaySortFilterPylonInstance {
  return {
    id,
    name,
    visible: false,
    avoidCollisions: true,
    region: "left",
    showRank: true,
    useFlag: true,
    useBib: true,
    useShortName: false,
    anchorBottom: false,
    widthMode: "fixed",
    shrinkTextToFit: false,
    darkModeOverride: undefined,
    offsets: defaultLowerThirdOffsets(),
    rows: [],
    sortDirection: "asc",
    rowCount: SORT_FILTER_PYLON_DEFAULT_ROW_COUNT,
    pageIndex: 0,
    showSortValue: false,
  };
}

export function newPodiumInstance(id: string, name: string): OverlayPodiumInstance {
  return {
    id,
    name,
    visible: false,
    avoidCollisions: true,
    region: "bottom",
    depth: 3,
    showPlace: false,
    useFlag: true,
    useBib: true,
    useShortName: false,
    shrinkTextToFit: false,
    darkModeOverride: undefined,
    offsets: defaultLowerThirdOffsets(),
    slots: [],
  };
}

export function newLowerThirdInstance(id: string, name: string): OverlayLowerThirdInstance {
  return {
    mode: "data",
    rows: [],
    id,
    name,
    visible: false,
    avoidCollisions: true,
    region: "left",
    useFlag: true,
    useBib: true,
    showExtraLabel: false,
    useShortName: false,
    widthMode: "fixed",
    shrinkTextToFit: false,
    darkModeOverride: undefined,
    offsets: defaultLowerThirdOffsets(),
    person: null,
  };
}

export function newPlayerCardInstance(id: string, name: string): OverlayPlayerCardInstance {
  return {
    id,
    name,
    visible: false,
    avoidCollisions: true,
    region: "left",
    useFlag: true,
    useBib: true,
    showExtraLabel: false,
    useShortName: false,
    darkModeOverride: undefined,
    photoFit: DEFAULT_PLAYER_CARD_PHOTO_FIT,
    photoPosition: { ...DEFAULT_PLAYER_CARD_PHOTO_POSITION },
    photoBackground: { ...DEFAULT_PLAYER_CARD_PHOTO_BACKGROUND },
    photoBackgroundOpacity: DEFAULT_PLAYER_CARD_PHOTO_BACKGROUND_OPACITY,
    offsets: defaultLowerThirdOffsets(),
    person: null,
  };
}

export function newCustomLowerThirdInstance(id: string, name: string): OverlayCustomLowerThirdInstance {
  return {
    id,
    name,
    visible: false,
    avoidCollisions: true,
    region: "left",
    widthMode: "fixed",
    shrinkTextToFit: false,
    darkModeOverride: undefined,
    offsets: defaultLowerThirdOffsets(),
    rows: [],
  };
}

export function newScoreboardInstance(id: string, name: string): OverlayScoreboardInstance {
  return {
    id,
    name,
    visible: false,
    avoidCollisions: true,
    region: "top",
    team1Name: "",
    team2Name: "",
    team1Color: { source: "custom", hex: DEFAULT_SCOREBOARD_TEAM1_HEX },
    team2Color: { source: "custom", hex: DEFAULT_SCOREBOARD_TEAM2_HEX },
    score1: 0,
    score2: 0,
    darkModeOverride: undefined,
    offsets: defaultGroupOffsets(),
  };
}

export function newMiniMapInstance(id: string, name: string): OverlayMiniMapInstance {
  return {
    id,
    name,
    visible: false,
    avoidCollisions: true,
    region: "left",
    deviceID: "",
    zoomSpan: DEFAULT_MINIMAP_ZOOM_SPAN,
    diameter: DEFAULT_MINIMAP_DIAMETER,
    ringColor: { source: "custom", hex: DEFAULT_MINIMAP_RING_HEX },
    bibText: "",
    labelText: "",
    showSpeed: false,
    speedUnit: "mph",
    offsets: defaultGroupOffsets(),
  };
}

export function newFullMapInstance(id: string, name: string): OverlayFullMapInstance {
  return {
    id,
    name,
    visible: false,
    // Full-canvas backdrop — never takes part in region collision take-outs.
    avoidCollisions: false,
    region: "full",
    framing: "follow",
    deviceID: "",
    zoomSpan: DEFAULT_FULLMAP_ZOOM_SPAN,
    mapType: "mutedStandard",
    colorScheme: "dark",
    tintColor: { source: "custom", hex: DEFAULT_FULLMAP_TINT_HEX },
    rotation: 0,
    courseLineColor: { source: "custom", hex: DEFAULT_FULLMAP_COURSE_LINE_HEX },
  };
}

export function newCourseProfileInstance(id: string, name: string): OverlayCourseProfileInstance {
  return {
    id,
    name,
    visible: false,
    avoidCollisions: true,
    region: "bottom",
    width: DEFAULT_COURSE_PROFILE_WIDTH,
    height: DEFAULT_COURSE_PROFILE_HEIGHT,
    baseColor: { source: "custom", hex: DEFAULT_COURSE_PROFILE_BASE_HEX },
    baseOpacity: DEFAULT_COURSE_PROFILE_BASE_OPACITY,
    tracks: [],
    offsets: defaultGroupOffsets(),
  };
}

export function newSpeedometerInstance(id: string, name: string): OverlaySpeedometerInstance {
  return {
    id,
    name,
    visible: false,
    avoidCollisions: true,
    region: "left",
    deviceID: "",
    displayMode: "analog",
    diameter: DEFAULT_SPEEDOMETER_DIAMETER,
    maxSpeed: DEFAULT_SPEEDOMETER_MAX_SPEED,
    ringColor: { source: "custom", hex: DEFAULT_SPEEDOMETER_RING_HEX },
    labelText: "",
    speedUnit: "mph",
    offsets: defaultGroupOffsets(),
  };
}

export function newCompassInstance(id: string, name: string): OverlayCompassInstance {
  return {
    id,
    name,
    visible: false,
    avoidCollisions: true,
    region: "left",
    deviceID: "",
    diameter: DEFAULT_COMPASS_DIAMETER,
    ringColor: { source: "custom", hex: DEFAULT_COMPASS_RING_HEX },
    showReadout: true,
    labelText: "",
    offsets: defaultGroupOffsets(),
  };
}

export function newGradeScopeInstance(id: string, name: string): OverlayGradeScopeInstance {
  return {
    id,
    name,
    visible: false,
    avoidCollisions: true,
    region: "left",
    deviceID: "",
    diameter: DEFAULT_GRADE_SCOPE_DIAMETER,
    tiltGain: DEFAULT_GRADE_SCOPE_TILT_GAIN,
    ringColor: { source: "custom", hex: DEFAULT_GRADE_SCOPE_RING_HEX },
    labelText: "",
    offsets: defaultGroupOffsets(),
  };
}

/** Starts with an empty spec (content-gated OUT) — the AI assistant designs it. */
export function newCustomWidgetInstance(id: string, name: string): OverlayCustomWidgetInstance {
  return {
    id,
    name,
    visible: false,
    avoidCollisions: true,
    region: "left",
    spec: emptyCustomWidgetSpec(),
    values: {},
    offsets: defaultGroupOffsets(),
  };
}

export function newBugInstance(id: string, name: string): OverlayBugInstance {
  return {
    id,
    name,
    visible: false,
    avoidCollisions: true,
    region: "top-left",
    rotationIntervalSeconds: DEFAULT_BUG_ROTATION_SECONDS,
    mode: "manual",
    selectedRowIndices: [],
    dataRows: [],
    rows: [],
    offsets: defaultGroupOffsets(),
  };
}

export function newBillboardInstance(id: string, name: string): OverlayBillboardInstance {
  return {
    id,
    name,
    visible: false,
    avoidCollisions: true,
    region: "center",
    mode: "rotate",
    rotationIntervalSeconds: DEFAULT_BUG_ROTATION_SECONDS,
    rows: [],
    offsets: defaultGroupOffsets(),
  };
}

export function newStartListInstance(id: string, name: string): OverlayStartListInstance {
  return {
    id,
    name,
    visible: false,
    avoidCollisions: true,
    region: "center",
    sortDirection: "asc",
    columns: 3,
    rowsPerColumn: START_LIST_DEFAULT_ROWS_PER_COLUMN,
    pageIndex: 0,
    rows: [],
    logoMode: "competition",
    useFlag: true,
    useBib: true,
    useShortName: false,
    shrinkTextToFit: false,
    darkModeOverride: undefined,
    offsets: defaultLowerThirdOffsets(),
  };
}

export function newWebSourceInstance(id: string, name: string): OverlayWebSourceInstance {
  return {
    id,
    name,
    visible: false,
    avoidCollisions: false,
    region: "canvas",
    url: "",
    canvasWidth: DEFAULT_WEB_SOURCE_WIDTH,
    canvasHeight: DEFAULT_WEB_SOURCE_HEIGHT,
    unloadWhenHidden: true,
    reloadToken: "",
    offsets: defaultGroupOffsets(),
  };
}

export function newAdvancedTimerInstance(id: string, name: string, timeZone: string = browserTimeZone()): OverlayAdvancedTimerInstance {
  return {
    id,
    name,
    visible: false,
    avoidCollisions: true,
    region: "top",
    mode: "countdown",
    targetAt: null,
    timeZone: isValidTimeZone(timeZone) ? timeZone : "UTC",
    darkModeOverride: undefined,
    offsets: defaultLowerThirdOffsets(),
  };
}

export function newTickerInstance(id: string, name: string): OverlayTickerInstance {
  return {
    id,
    name,
    visible: false,
    avoidCollisions: true,
    region: "bottom",
    messages: [],
    divider: "bullet",
    direction: "rtl",
    speed: DEFAULT_TICKER_SPEED,
    barWidth: DEFAULT_TICKER_WIDTH,
    fontSize: DEFAULT_TICKER_FONT_SIZE,
    darkModeOverride: undefined,
    offsets: defaultGroupOffsets(),
  };
}

export function newImageCanvasInstance(id: string, name: string): OverlayImageCanvasInstance {
  return {
    id,
    name,
    visible: false,
    // Backdrops layer by default (fullMap precedent) — operators opt in to take-outs.
    avoidCollisions: false,
    region: "canvas",
    url: "",
    canvasWidth: DEFAULT_IMAGE_CANVAS_WIDTH,
    canvasHeight: DEFAULT_IMAGE_CANVAS_HEIGHT,
    backgroundOpacity: DEFAULT_IMAGE_CANVAS_BACKGROUND_OPACITY,
    photoFit: DEFAULT_IMAGE_CANVAS_PHOTO_FIT,
    photoPosition: { ...DEFAULT_IMAGE_CANVAS_PHOTO_POSITION },
    offsets: defaultGroupOffsets(),
  };
}

// --- Designer-facing config helpers -----------------------------------------------------

/** Resolve a color source against live league/org docs (designer-side only). */
export function resolveColorRef(
  source: OverlayColorSource,
  context: {
    league: LeagueModel | null;
    organization?: OrganizationModel | null;
    customHex?: string;
    secondaryCustomHex?: string;
    gradientAngle?: number;
    gradientDirection?: OverlayGradientDirection;
    fallbackHex: string;
  },
): OverlayColorRef {
  const pick = (): string | undefined => {
    switch (source) {
      case "league-primary":
        return context.league?.primaryColorHex;
      case "league-secondary":
        return context.league?.secondaryColorHex;
      case "league-primary-secondary-gradient":
        return context.league?.primaryColorHex;
      case "org-primary":
        return context.organization?.primaryColorHex;
      case "org-secondary":
        return context.organization?.secondaryColorHex;
      case "custom":
      case "custom-gradient":
        return context.customHex;
    }
  };
  const hex = normalizedHex(pick()) ?? normalizedHex(context.fallbackHex) ?? "FFFFFF";
  if (source === "league-primary-secondary-gradient") {
    return {
      source,
      hex,
      secondaryHex: normalizedHex(context.league?.secondaryColorHex) ?? hex,
      gradientAngle: normalizeOverlayGradientAngle(context.gradientAngle, overlayGradientAngleForDirection(context.gradientDirection)),
    };
  }
  if (source === "custom-gradient") {
    return {
      source,
      hex,
      secondaryHex: normalizedHex(context.secondaryCustomHex) ?? hex,
      gradientAngle: normalizeOverlayGradientAngle(context.gradientAngle, overlayGradientAngleForDirection(context.gradientDirection)),
    };
  }
  return {
    source,
    organizationID: source.startsWith("org-") ? context.organization?.id : undefined,
    hex,
  };
}

/** Default course pick: role courseKML first, else the first available leg KML. */
export function selectOverlayCourseKML(courseKMLs: GPSTrackerCourseKML[]): OverlayCourseKMLRef | null {
  const preferred = courseKMLs.find((kml) => kml.role === "courseKML") ?? courseKMLs[0];
  return preferred ? { role: preferred.role, name: preferred.name, url: preferred.url } : null;
}

export function makeDefaultOverlayConfig(params: {
  id: string;
  leagueID: string;
  competitionInstanceID: string;
  name: string;
  textColor: OverlayColorRef;
  accentColor: OverlayColorRef;
  courseKML: OverlayCourseKMLRef | null;
  eventLogo?: OverlayEventLogo | null;
  createdByUserID: string;
}): OverlayConfig {
  const now = new Date();
  return {
    id: params.id,
    leagueID: params.leagueID,
    competitionInstanceID: params.competitionInstanceID,
    name: params.name,
    courseKML: params.courseKML,
    eventLogo: params.eventLogo ?? null,
    gpsFocus: "full",
    gpsRelockNonce: 0,
    design: {
      textColor: params.textColor,
      accentColor: params.accentColor,
      font: {},
      autoCapitalizePersonText: false,
      skin: DEFAULT_OVERLAY_SKIN,
      material: DEFAULT_OVERLAY_MATERIAL,
      darkMode: false,
    },
    // Starter set = the legacy synthesis of an empty doc: two GPS groups,
    // two timers, two lower thirds, one custom, two pylons — all OUT/empty.
    elements: legacyOverlayElements({}),
    controlGroups: [],
    railTypeGroups: parseRailTypeGroups(undefined),
    createdByUserID: params.createdByUserID,
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Copy the general styling customization from a source overlay onto a freshly
 * created base config: design plus the source's element topology with styling
 * fields only. Identity, course, live on-air state, and person/row selections
 * are reset. Explicit allowlists so future fields default to "not copied".
 */
export function applyOverlayTemplate(base: OverlayConfig, source: OverlayConfig): OverlayConfig {
  if (base.leagueID !== source.leagueID && source.skinContractVersion === 1) throw new Error("Custom skins cannot be copied between leagues.");
  return {
    ...base,
    skinSnapshots: source.skinSnapshots ? structuredClone(source.skinSnapshots) : undefined,
    skinContractVersion: source.skinContractVersion,
    design: {
      textColor: { ...source.design.textColor },
      accentColor: { ...source.design.accentColor },
      font: { ...source.design.font },
      autoCapitalizePersonText: source.design.autoCapitalizePersonText,
      skin: source.design.skin,
      customSkin: source.design.customSkin,
      material: source.design.material,
      materialTextureOpacities: parseOverlayMaterialTextureOpacities(source.design.materialTextureOpacities),
      darkMode: source.design.darkMode,
    },
    elements: {
      gpsGroups: source.elements.gpsGroups.map((instance) => ({
        ...instance,
        rootScale: normalizeOverlayRootScale(instance.rootScale),
        visible: false,
        offsets: cloneGroupOffsets(instance.offsets),
      })),
      timers: source.elements.timers.map((instance) => ({
        ...instance,
        rootScale: normalizeOverlayRootScale(instance.rootScale),
        visible: false,
        running: false,
        startedAt: null,
        offsetMs: 0,
        offsets: cloneLowerThirdOffsets(instance.offsets),
      })),
      pylons: source.elements.pylons.map((instance) => ({
        ...instance,
        rootScale: normalizeOverlayRootScale(instance.rootScale),
        visible: false,
        sourceID: undefined,
        rows: [],
        offsets: cloneLowerThirdOffsets(instance.offsets),
      })),
      gpsPylons: source.elements.gpsPylons.map((instance) => ({
        ...instance,
        rootScale: normalizeOverlayRootScale(instance.rootScale),
        visible: false,
        sourceID: undefined,
        rows: [],
        offsets: cloneLowerThirdOffsets(instance.offsets),
      })),
      // Sort/filter field keys reference another overlay's source mappings — reset them with the source.
      sortFilterPylons: source.elements.sortFilterPylons.map((instance) => ({
        ...instance,
        rootScale: normalizeOverlayRootScale(instance.rootScale),
        visible: false,
        sourceID: undefined,
        sortFieldKey: undefined,
        filterFieldKey: undefined,
        filterValue: undefined,
        pageIndex: 0,
        rows: [],
        offsets: cloneLowerThirdOffsets(instance.offsets),
      })),
      lowerThirds: source.elements.lowerThirds.map((instance) => ({
        ...instance,
        rootScale: normalizeOverlayRootScale(instance.rootScale),
        visible: false,
        rows: [],
        selectedRowID: undefined,
        sourceID: undefined,
        recordKey: undefined,
        person: null,
        offsets: cloneLowerThirdOffsets(instance.offsets),
      })),
      customLowerThirds: source.elements.customLowerThirds.map((instance) => ({
        ...instance,
        rootScale: normalizeOverlayRootScale(instance.rootScale),
        visible: false,
        selectedRowID: undefined,
        rows: [],
        offsets: cloneLowerThirdOffsets(instance.offsets),
      })),
      playerCards: source.elements.playerCards.map((instance) => ({
        ...instance,
        rootScale: normalizeOverlayRootScale(instance.rootScale),
        visible: false,
        sourceID: undefined,
        recordKey: undefined,
        person: null,
        photoPosition: { ...instance.photoPosition },
        photoBackground: { ...instance.photoBackground },
        offsets: cloneLowerThirdOffsets(instance.offsets),
      })),
      scoreboards: source.elements.scoreboards.map((instance) => ({
        ...instance,
        rootScale: normalizeOverlayRootScale(instance.rootScale),
        visible: false,
        score1: 0,
        score2: 0,
        team1Color: { ...instance.team1Color },
        team2Color: { ...instance.team2Color },
        offsets: cloneGroupOffsets(instance.offsets),
      })),
      miniMaps: source.elements.miniMaps.map((instance) => ({
        ...instance,
        rootScale: normalizeOverlayRootScale(instance.rootScale),
        visible: false,
        deviceID: "",
        ringColor: { ...instance.ringColor },
        offsets: cloneGroupOffsets(instance.offsets),
      })),
      fullMaps: source.elements.fullMaps.map((instance) => ({
        ...instance,
        rootScale: undefined,
        visible: false,
        deviceID: "",
        tintColor: { ...instance.tintColor },
        courseLineColor: { ...instance.courseLineColor },
      })),
      courseProfiles: source.elements.courseProfiles.map((instance) => ({
        ...instance,
        rootScale: normalizeOverlayRootScale(instance.rootScale),
        visible: false,
        tracks: [],
        baseColor: { ...instance.baseColor },
        offsets: cloneGroupOffsets(instance.offsets),
      })),
      speedometers: source.elements.speedometers.map((instance) => ({
        ...instance,
        rootScale: normalizeOverlayRootScale(instance.rootScale),
        visible: false,
        deviceID: "",
        ringColor: { ...instance.ringColor },
        offsets: cloneGroupOffsets(instance.offsets),
      })),
      gradeScopes: source.elements.gradeScopes.map((instance) => ({
        ...instance,
        rootScale: normalizeOverlayRootScale(instance.rootScale),
        visible: false,
        deviceID: "",
        ringColor: { ...instance.ringColor },
        offsets: cloneGroupOffsets(instance.offsets),
      })),
      // The spec IS the styling a template exists to carry; operator-entered
      // values are live state and reset.
      customWidgets: source.elements.customWidgets.map((instance) => ({
        ...instance,
        rootScale: normalizeOverlayRootScale(instance.rootScale),
        visible: false,
        spec: parseCustomWidgetSpec(customWidgetSpecFirestoreData(instance.spec)),
        values: {},
        offsets: cloneGroupOffsets(instance.offsets),
      })),
      // Logo rows and rotation cadence are the bug's styling — they travel.
      bugs: source.elements.bugs.map((instance) => ({
        ...instance,
        rootScale: normalizeOverlayRootScale(instance.rootScale),
        visible: false,
        rows: instance.rows.map((row) => ({ ...row })),
        selectedRowIndices: [...(instance.selectedRowIndices ?? [])],
        dataRows: (instance.dataRows ?? []).map((row) => ({ ...row })),
        offsets: cloneGroupOffsets(instance.offsets),
      })),
      compasses: source.elements.compasses.map((instance) => ({
        ...instance,
        rootScale: normalizeOverlayRootScale(instance.rootScale),
        visible: false,
        deviceID: "",
        ringColor: { ...instance.ringColor },
        offsets: cloneGroupOffsets(instance.offsets),
      })),
      // Slates, mode, selection, cadence and scale are the billboard's styling — they travel.
      billboards: source.elements.billboards.map((instance) => ({
        ...instance,
        rootScale: normalizeOverlayRootScale(instance.rootScale),
        visible: false,
        rows: instance.rows.map((row) => ({ ...row })),
        offsets: cloneGroupOffsets(instance.offsets),
      })),
      // Depth is layout intent (like billboard mode) — it travels; athlete slots reset.
      podiums: source.elements.podiums.map((instance) => ({
        ...instance,
        rootScale: normalizeOverlayRootScale(instance.rootScale),
        visible: false,
        sourceID: undefined,
        slots: [],
        offsets: cloneLowerThirdOffsets(instance.offsets),
      })),
      // The image, canvas box, background and framing are all design — they travel.
      imageCanvases: source.elements.imageCanvases.map((instance) => ({
        ...instance,
        rootScale: normalizeOverlayRootScale(instance.rootScale),
        visible: false,
        background: instance.background ? { ...instance.background } : undefined,
        photoPosition: { ...instance.photoPosition },
        offsets: cloneGroupOffsets(instance.offsets),
      })),
      // URL and viewport are design; reload is a live command and never travels.
      webSources: source.elements.webSources.map((instance) => ({
        ...instance,
        rootScale: normalizeOverlayRootScale(instance.rootScale),
        visible: false,
        reloadToken: "",
        offsets: cloneGroupOffsets(instance.offsets),
      })),
      // Grid shape + logo are the start list's styling — they travel; source-bound sort/filter/rows reset.
      startLists: source.elements.startLists.map((instance) => ({
        ...instance,
        rootScale: normalizeOverlayRootScale(instance.rootScale),
        visible: false,
        sourceID: undefined,
        sortFieldKey: undefined,
        filterFieldKey: undefined,
        filterValue: undefined,
        pageIndex: 0,
        rows: [],
        offsets: cloneLowerThirdOffsets(instance.offsets),
      })),
      // Messages, divider and crawl settings are the ticker's copy + styling — they travel (billboard precedent).
      tickers: source.elements.tickers.map((instance) => ({
        ...instance,
        rootScale: normalizeOverlayRootScale(instance.rootScale),
        visible: false,
        messages: instance.messages.map((message) => ({ ...message })),
        offsets: cloneGroupOffsets(instance.offsets),
      })),
      // Target, zone and mode are the timer's content — they travel.
      advancedTimers: source.elements.advancedTimers.map((instance) => ({
        ...instance,
        rootScale: normalizeOverlayRootScale(instance.rootScale),
        visible: false,
        targetAt: instance.targetAt ? new Date(instance.targetAt.getTime()) : null,
        offsets: cloneLowerThirdOffsets(instance.offsets),
      })),
    },
    // Containers travel with the template (instance ids are preserved above)
    // but arrive inactive — live on-air state never copies. `collapsed` is
    // kept: accordion state is authoring layout (like design), not on-air state.
    controlGroups: source.controlGroups.map((group) => ({
      ...group,
      active: false,
      members: [...group.members],
    })),
    railTypeGroups: source.railTypeGroups.map((group) => ({ ...group })),
  };
}

/**
 * Replace an existing overlay's content with a copy of `source`'s design —
 * the in-place twin of "start from" for new overlays. Identity, course, logo,
 * GPS focus and the live program-refresh command stay with `current` (byte-
 * identical, so their diff-gated rule validators skip); styling, element
 * topology, containers and rail layout come from `source` via
 * applyOverlayTemplate, all OUT with person/row/device selections reset.
 */
export function replaceOverlayDesign(current: OverlayConfig, source: OverlayConfig, name: string): OverlayConfig {
  const base = makeDefaultOverlayConfig({
    id: current.id,
    leagueID: current.leagueID,
    competitionInstanceID: current.competitionInstanceID,
    name,
    textColor: { ...source.design.textColor },
    accentColor: { ...source.design.accentColor },
    courseKML: current.courseKML,
    eventLogo: current.eventLogo,
    createdByUserID: current.createdByUserID ?? "",
  });
  return {
    ...applyOverlayTemplate(base, source),
    gpsFocus: current.gpsFocus,
    gpsRelockNonce: current.gpsRelockNonce,
    programRefresh: current.programRefresh,
    createdByUserID: current.createdByUserID,
    createdAt: current.createdAt,
  };
}
